import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Routes,
  Route,
  NavLink,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  Bell,
  Boxes,
  Building2,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock3,
  Factory,
  LoaderCircle,
  LogOut,
  LayoutGrid,
  Maximize2,
  Menu,
  MonitorPlay,
  Play,
  Plus,
  QrCode,
  RefreshCw,
  Save,
  ScanLine,
  ShieldAlert,
  Square,
  Trash2,
  Table2,
  Printer,
  Users,
  Workflow,
  Wrench,
  X,
} from "lucide-react";
import type { Html5Qrcode as Html5QrcodeType } from "html5-qrcode";
import { io } from "socket.io-client";
import { can, homePage } from './permissions';
import { SidebarNavigation } from './sidebar-navigation';
import { StationFirmwareNotice } from './station-firmware';
import {
  addEdge,
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  MiniMap,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeProps,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { api, getAccessToken, post, urlBase64ToUint8Array } from "./api";
import { Guard, useAuth } from "./auth";
import { TvPlaybackLayer, TvScenarioAdmin, type TvScenario } from "./tv";
import {TvAudioPlayer} from './tv-audio';
import {ProductionWorkspace} from './production';
import {PrintingPage} from './printing';
import {ConnectionStationsPage} from './connection-stations';
import {Esp32FirmwarePage} from './esp32-firmware';
import {MachineQrPrintDialog} from './machine-print';
import {filterMachines, machineViewMode, type MachineViewMode} from './machine-view';
import {useActionGate} from './action-feedback';
import { machineProductionJobs, progressRequestPath, taskForMachine } from './production-dashboard';
import { identifyMachine, machineCameraConfig, machineCameraConstraints, machineCameraIssue, type MachineCameraIssue } from './machine-scan';

const fmt = (date?: string) =>
  date
    ? new Intl.DateTimeFormat("vi-VN", {
        timeZone: "Asia/Ho_Chi_Minh",
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
      }).format(new Date(date))
    : "—";
const num = (value: any) =>
  new Intl.NumberFormat("vi-VN").format(Number(value || 0));

function DeadlinePicker({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const [open, setOpen] = useState(false);
  const [pickerDate, setPickerDate] = useState("");
  const [pickerTime, setPickerTime] = useState("08:00");
  const openPicker = () => {
    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}:\d{2})$/);
    if (match) {
      setPickerDate(`${match[3]}-${match[2]}-${match[1]}`);
      setPickerTime(match[4]);
    } else {
      const now = new Date();
      setPickerDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`);
    }
    setOpen(true);
  };
  const apply = () => {
    if (!pickerDate || !pickerTime) return;
    const [year, month, day] = pickerDate.split("-");
    onChange(`${day}/${month}/${year} ${pickerTime}`);
    setOpen(false);
  };
  return <div className="deadline-picker">
    <div className="deadline-input-row">
      <input name="deadline" required value={value} onChange={e => onChange(e.target.value)} placeholder="dd/mm/yyyy hh:mm" inputMode="numeric" autoComplete="off" />
      <button type="button" className="secondary deadline-picker-button" onClick={openPicker} aria-label="Chọn deadline bằng lịch"><CalendarDays /></button>
    </div>
    {open && <div className="deadline-popover" role="dialog" aria-label="Chọn deadline">
      <label>Ngày<input type="date" value={pickerDate} onChange={e => setPickerDate(e.target.value)} /></label>
      <label>Giờ<Clock3 /><input type="time" value={pickerTime} onChange={e => setPickerTime(e.target.value)} /></label>
      <div><button type="button" className="secondary" onClick={() => setOpen(false)}>Hủy</button><button type="button" className="primary" onClick={apply} disabled={!pickerDate || !pickerTime}>Áp dụng</button></div>
    </div>}
  </div>;
}
const managementRoles = [
  "SUPERADMIN",
  "ADMIN",
  "PRODUCTION_MANAGER",
  "TEAM_LEADER",
];
const isManager = (roles: string[]) =>
  roles.some((r) => managementRoles.includes(r));
const roleLabels: Record<string, string> = {
  SUPERADMIN: "Chủ hệ thống / Nhà phát triển",
  WORKER: "Công nhân",
  TEAM_LEADER: "Tổ trưởng",
  PRODUCTION_MANAGER: "Trưởng phòng sản xuất",
  QC: "Kiểm tra chất lượng (QC)",
  PACKING: "Đóng gói",
  WAREHOUSE: "Kho thành phẩm",
  SHIPPING: "Điều phối giao hàng",
  DRIVER: "Tài xế / giao hàng",
  ADMIN: "Quản trị",
};
const assignableRoleCodes = [
  "WORKER",
  "TEAM_LEADER",
  "PRODUCTION_MANAGER",
  "QC",
  "PACKING",
  "WAREHOUSE",
  "SHIPPING",
  "DRIVER",
  "ADMIN",
] as const;

const realtimeEvents = [
  "dashboard.updated",
  "machine.created",
  "machine.restored",
  "machine.updated",
  "machine.deleted",
  "machine.session.started",
  "machine.session.ended",
  "machine.session.forced-out",
  "issue.created",
  "issue.updated",
  "order.created",
  "order.released",
  "order.cancelled",
  "operation.updated",
  "operation.ack",
  "operation.start",
  "operation.complete",
  "operation.completed",
  "workflow.updated",
  "tv.scenario.updated",
  "tv.kiosk.updated",
  "tv.audio.updated",
  "notification.created",
  "notification.reminder",
  "notification.escalated",
  "warehouse.updated",
  "order.updated",
] as const;

function useSocket(onEvent: () => void | Promise<unknown>, fallbackMs = 15000) {
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);
  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let active = true;
    let refreshing = false;
    let refreshAgain = false;
    const refresh = () => {
      if (!active || document.visibilityState === "hidden") return;
      if (refreshing) {
        refreshAgain = true;
        return;
      }
      refreshing = true;
      Promise.resolve(onEventRef.current())
        .catch((error) => console.error("Realtime refresh failed", error))
        .finally(() => {
          refreshing = false;
          if (refreshAgain) {
            refreshAgain = false;
            refresh();
          }
        });
    };
    const socket = io({
      auth: { token },
      transports: ["polling", "websocket"],
    });
    socket.io.on("reconnect_attempt", () => {
      socket.auth = { token: getAccessToken() };
    });
    socket.on("connect", refresh);
    realtimeEvents.forEach((event) => socket.on(event, refresh));
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    const fallbackTimer = window.setInterval(refresh, fallbackMs);
    return () => {
      active = false;
      window.clearInterval(fallbackTimer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
      socket.disconnect();
    };
  }, [fallbackMs]);
}

function Shell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [online, setOnline] = useState(navigator.onLine);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const yes = () => setOnline(true),
      no = () => setOnline(false);
    addEventListener("online", yes);
    addEventListener("offline", no);
    return () => {
      removeEventListener("online", yes);
      removeEventListener("offline", no);
    };
  }, []);
  if (!user) return <>{children}</>;
  return (
    <div className="app-shell">
      {!online && (
        <div className="offline">
          <AlertTriangle size={17} /> MẤT KẾT NỐI · Dữ liệu có thể chưa được
          đồng bộ
        </div>
      )}
      <header className="topbar">
        <button className="icon-btn mobile" aria-label="Mở menu" aria-controls="main-navigation" aria-expanded={open} onClick={() => setOpen(true)}>
          <Menu />
        </button>
        <NavLink
          to={homePage(user)}
          className="brand"
        >
          <img src="/brand/phuoc-hao-horizontal.png" alt="Phước Hào" />
          <small>Vận hành sản xuất</small>
        </NavLink>
        <div className="user-chip">
          <div className="avatar">{user.fullName.charAt(0)}</div>
          <span>{user.fullName}</span>
        </div>
      </header>
      <aside className={`sidebar ${open ? "open" : ""}`}>
        <button
          className="icon-btn close mobile"
          aria-label="Đóng menu"
          onClick={() => setOpen(false)}
        >
          <X />
        </button>
        <SidebarNavigation user={user} onNavigate={() => setOpen(false)} />
        <button className="logout" onClick={logout}>
          <LogOut />
          Đăng xuất
        </button>
      </aside>
      {open && <div className="scrim" onClick={() => setOpen(false)} />}
      <main><InAppNotifications /><StationFirmwareNotice />{children}</main>
    </div>
  );
}
function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const from = (location.state as any)?.from;
  const destination = from
    ? `${from.pathname}${from.search || ""}`
    : user
      ? homePage(user)
      : "/worker";
  if (loading) return <div className="splash">Đang kiểm tra phiên đăng nhập…</div>;
  if (user)
    return (
      <Navigate to={destination} replace />
    );
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(username, pin);
      navigate(destination, { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="login-page">
      <section className="login-hero">
        <div className="hero-mark">
          <img src="/brand/phuoc-hao-mark.png" alt="PH" />
        </div>
        <h1>
          Đúng người.
          <br />
          Đúng máy.
          <br />
          <em>Đúng thời điểm.</em>
        </h1>
        <p>Hệ thống điều phối sản xuất Phước Hào</p>
      </section>
      <form className="login-card" onSubmit={submit}>
        <div className="mini-brand">
          <img src="/brand/phuoc-hao-horizontal.png" alt="Phước Hào" />
        </div>
        <h2>Đăng nhập ca làm việc</h2>
        <p>Dùng tài khoản và mã PIN được cấp.</p>
        <label>
          Tên đăng nhập
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoCapitalize="none"
            autoComplete="username"
            required
          />
        </label>
        <label>
          Mã PIN
          <input
            type="password"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        {error && <div className="error">{error}</div>}
        <button className="primary wide" disabled={busy}>
          {busy ? "Đang đăng nhập…" : "ĐĂNG NHẬP"}
          <ChevronRight />
        </button>
      </form>
    </div>
  );
}

type TvPairing = {
  id: string;
  code: string;
  secret: string;
  expiresAt: string;
  pairingUrl: string;
  qrDataUrl: string;
};

function TvDevice() {
  const { user, loading } = useAuth();
  const [pairing, setPairing] = useState<TvPairing>();
  const [error, setError] = useState("");
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const pairingInFlight = useRef(false);
  const hasDashboardAccess = can(user, 'dashboard.view');

  const createPairing = useCallback(() => {
    if (pairingInFlight.current) return;
    pairingInFlight.current = true;
    setError("");
    setRetryAt(null);
    setPairing(undefined);
    void post<TvPairing>("/tv/pairings")
      .then((nextPairing) => {
        setPairing(nextPairing);
        setError("");
      })
      .catch(() => {
        setError("Không thể kết nối máy chủ. TV sẽ tự thử lại.");
        setRetryAt(Date.now() + 10_000);
      })
      .finally(() => { pairingInFlight.current = false; });
  }, []);

  useEffect(() => {
    if (loading || hasDashboardAccess) return;
    createPairing();
  }, [createPairing, hasDashboardAccess, loading]);

  useEffect(() => {
    if (loading || hasDashboardAccess) return;
    const refreshPairing = () => {
      if (pairing && Date.parse(pairing.expiresAt) - Date.now() <= 30_000) {
        createPairing();
      } else if (!pairing && retryAt !== null && Date.now() >= retryAt) {
        createPairing();
      }
    };
    const onResume = () => {
      if (document.visibilityState === 'visible') refreshPairing();
    };
    const refresh = window.setInterval(refreshPairing, 2000);
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('online', onResume);
    return () => {
      window.clearInterval(refresh);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('online', onResume);
    };
  }, [createPairing, hasDashboardAccess, loading, pairing, retryAt]);

  useEffect(() => {
    if (!pairing) return;
    let requestInFlight = false;
    let cancelled = false;
    const poll = window.setInterval(() => {
      if (requestInFlight) return;
      requestInFlight = true;
      void api<{ status: "WAITING" | "EXPIRED" | "APPROVED" }>(
        `/tv/pairings/${pairing.id}/status?secret=${encodeURIComponent(pairing.secret)}`,
      )
        .then((result) => {
          if (cancelled) return;
          setError("");
          if (result.status === "APPROVED") {
            window.clearInterval(poll);
            window.location.replace("/admin/dashboard?tv=1");
          } else if (result.status === "EXPIRED") {
            window.clearInterval(poll);
            createPairing();
          }
        })
        .catch(() => {
          if (!cancelled) setError("Mất kết nối tạm thời. TV đang tự kết nối lại.");
        })
        .finally(() => { requestInFlight = false; });
    }, 2000);
    return () => { cancelled = true; window.clearInterval(poll); };
  }, [createPairing, pairing]);

  if (loading) return <div className="splash">Đang kiểm tra màn hình TV…</div>;
  if (hasDashboardAccess)
    return <Navigate to="/admin/dashboard?tv=1" replace />;

  return (
    <div className="tv-pair-screen">
      <div className="tv-pair-brand">
        <div className="tv-pair-logo-card">
          <img src="/brand/phuoc-hao-blue-horizontal.png" alt="Phước Hào" />
        </div>
        <span>ANDROID TV · KẾT NỐI AN TOÀN</span>
      </div>
      <section>
        <div className="tv-pair-copy">
          <span className="eyebrow">KẾT NỐI MÀN HÌNH</span>
          <h1>Quét mã để bắt đầu trình chiếu</h1>
          <p>Dùng điện thoại của admin quét QR, đăng nhập và xác nhận kết nối TV này.</p>
          {pairing && <div className="tv-pair-code"><small>MÃ KẾT NỐI</small><b>{pairing.code}</b></div>}
          {error && <div className="error">{error}</div>}
          {error && <button className="primary" onClick={createPairing}>Tạo mã mới</button>}
        </div>
        <div className="tv-pair-qr">
          {pairing ? <img src={pairing.qrDataUrl} alt="QR đăng nhập TV" /> : <LoaderCircle className="spin" />}
          <small>Mã tự làm mới sau 10 phút</small>
        </div>
      </section>
    </div>
  );
}

function TvPair() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState<"READY" | "BUSY" | "DONE">("READY");
  const [error, setError] = useState("");
  const code = (params.get("code") || "").toUpperCase();
  const isAdmin = can(user, 'tv.publish');
  const claim = useCallback(async () => {
    if (!isAdmin || !code || state !== "READY") return;
    setState("BUSY");
    setError("");
    try {
      await post(`/tv/pairings/${encodeURIComponent(code)}/claim`);
      setState("DONE");
    } catch (caught) {
      setError((caught as Error).message);
      setState("READY");
    }
  }, [code, isAdmin, state]);
  useEffect(() => {
    if (!loading && user && isAdmin && code && state === "READY" && !error)
      void claim();
  }, [claim, code, error, isAdmin, loading, state, user]);
  if (loading) return <div className="splash">Đang kiểm tra phiên đăng nhập…</div>;
  if (!user)
    return <Navigate to="/login" state={{ from: { pathname: "/tv/pair", search: `?code=${encodeURIComponent(code)}` } }} replace />;
  return (
    <div className="tv-pair-mobile">
      <img src="/brand/phuoc-hao-blue-horizontal.png" alt="Phước Hào" />
      {state === "DONE" ? (
        <><CheckCircle2 /><h1>Đã kết nối TV</h1><p>TV sẽ tự chuyển sang màn hình trình chiếu. Bạn có thể đóng trang này.</p></>
      ) : (
        <><MonitorPlay /><h1>Kết nối màn hình TV</h1><p>Mã thiết bị: <b>{code || "Không hợp lệ"}</b></p>
          {!isAdmin && <div className="error">Chỉ tài khoản ADMIN được phép kết nối TV.</div>}
          {error && <div className="error">{error}</div>}
          <button className="primary wide" disabled={!isAdmin || !code || state === "BUSY"} onClick={() => { setError(""); void claim(); }}>
            {state === "BUSY" ? "Đang kết nối…" : error ? "THỬ KẾT NỐI LẠI" : "KẾT NỐI TV"}
          </button></>
      )}
    </div>
  );
}

function EnablePush() {
  const [state, setState] = useState<
    "idle" | "working" | "done" | "unsupported"
  >("idle");
  const pushSetup = useRef<{registration: ServiceWorkerRegistration; publicKey: string; sub: PushSubscription | null} | null>(null);
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const standalone =
    matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone;
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window))
      setState("unsupported");
    else {
      let mounted = true;
      void Promise.all([navigator.serviceWorker.ready, api<any>("/push/config")]).then(async ([registration, config]) => {
        if (!config.enabled) return;
        const sub = await registration.pushManager.getSubscription();
        if (!mounted) return;
        pushSetup.current = {registration, publicKey: config.publicKey, sub};
        if (!sub) return;
        await post("/push-subscriptions", {
          ...sub.toJSON(), deviceName: navigator.platform,
          platform: ios ? "ios" : "android/web",
        });
        if (mounted) setState("done");
      }).catch(() => { if (mounted) setState("idle"); });
      return () => { mounted = false; };
    }
  }, []);
  const enable = async () => {
    setState("working");
    try {
      const setup = pushSetup.current;
      if (!setup) throw new Error("Chưa sẵn sàng đăng ký thông báo. Hãy kiểm tra kết nối và mở lại app.");
      let sub = setup.sub;
      if (!sub)
        sub = await setup.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(setup.publicKey),
        });
      setup.sub = sub;
      await post("/push-subscriptions", {
        ...sub.toJSON(),
        deviceName: navigator.platform,
        platform: ios ? "ios" : "android/web",
      });
      setState("done");
    } catch (e) {
      alert((e as Error).message);
      setState("idle");
    }
  };
  if (state === "done")
    return (
      <div className="push-ok">
        <CheckCircle2 /> Thiết bị đã bật thông báo
      </div>
    );
  if (state === "unsupported")
    return (
      <div className="callout warning">
        Trình duyệt này không hỗ trợ Web Push. Hãy dùng Safari trên iPhone hoặc
        Chrome trên Android.
      </div>
    );
  if (ios && !standalone)
    return (
      <div className="callout">
        <b>Để nhận thông báo trên iPhone</b>
        <span>
          Mở nút Chia sẻ của Safari → “Thêm vào Màn hình chính”, sau đó mở app
          từ biểu tượng mới.
        </span>
      </div>
    );
  return (
    <button
      className="secondary wide"
      onClick={enable}
      disabled={state === "working"}
    >
      <Bell />
      {state === "working" ? "Đang bật…" : "BẬT THÔNG BÁO TRÊN MÁY NÀY"}
    </button>
  );
}

function WorkerHome() {
  const { user } = useAuth();
  const [session, setSession] = useState<any>(null);
  const [operation, setOperation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showShiftReport, setShowShiftReport] = useState(false);
  const [shiftGood, setShiftGood] = useState("0");
  const [shiftDefect, setShiftDefect] = useState("0");
  const [handoverNote, setHandoverNote] = useState("");
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const navigate = useNavigate();
  const load = useCallback(async () => {
    try {
      const [s, o] = await Promise.all([
        can(user, 'sessions.work') ? api("/machine-sessions/current") : Promise.resolve(null),
        can(user, 'operations.work') ? api("/operations/my-current") : Promise.resolve(null),
      ]);
      setSession(s);
      setOperation(o);
    } finally {
      setLoading(false);
    }
  }, [user]);
  useEffect(() => {
    load();
  }, [load]);
  useSocket(load);
  const reportableOperation =
    session && operation?.machine_id === session.machine_id ? operation : null;
  const openShiftReport = () => {
    if(operation?.order_line_id){navigate(`/production?tab=jobs&job=${operation.id}`);return;}
    setShiftGood("0");
    setShiftDefect("0");
    setHandoverNote("");
    setCheckoutError("");
    setShowShiftReport(true);
  };
  const checkout = async (event: FormEvent) => {
    event.preventDefault();
    if (checkingOut) return;
    setCheckingOut(true);
    setCheckoutError("");
    try {
      await post("/machine-sessions/check-out", {
        operationId: reportableOperation?.id ?? null,
        goodQuantity: reportableOperation ? Number(shiftGood) : 0,
        defectQuantity: reportableOperation ? Number(shiftDefect) : 0,
        handoverNote: handoverNote.trim() || undefined,
        reason: "SHIFT_ENDED",
      });
      setShowShiftReport(false);
      await load();
    } catch (error) {
      setCheckoutError((error as Error).message);
    } finally {
      setCheckingOut(false);
    }
  };
  if (loading)
    return (
      <Page>
        <div className="skeleton tall" />
      </Page>
    );
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">CA LÀM VIỆC</span>
          <h1>Xin chào, {user!.fullName}</h1>
        </div>
        <span className="date">
          {new Intl.DateTimeFormat("vi-VN", {
            weekday: "long",
            day: "2-digit",
            month: "2-digit",
          }).format(new Date())}
        </span>
      </div>
      {!session ? (
        <section className="empty-machine">
          <div className="scan-orbit">
            <QrCode />
          </div>
          <h2>Bạn chưa vận hành máy nào</h2>
          <p>Quét mã QR được dán trên máy để bắt đầu ca làm việc.</p>
          <button
            className="primary big"
            onClick={() => navigate("/scan-machine")}
          >
            <ScanLine />
            QUÉT MÃ MÁY
          </button>
        </section>
      ) : (
        <>
          <section
            className={`machine-panel ${session.machine_status === "BREAKDOWN" ? "breakdown" : ""}`}
          >
            <div className="status-line">
              {session.machine_status === "BREAKDOWN" ? (
                <>
                  <AlertTriangle /> MÁY ĐANG BÁO SỰ CỐ
                </>
              ) : (
                <>
                  <span className="live-dot" /> ĐANG LÀM VIỆC
                </>
              )}
            </div>
            <div className="machine-name">
              <div className="machine-icon">
                <Factory />
              </div>
              <div>
                <span>{session.code}</span>
                <h2>{session.name}</h2>
                <p>
                  {session.workshop_area || "Khu vực sản xuất"} · Check-in{" "}
                  {fmt(session.check_in_at)}
                </p>
              </div>
            </div>
          </section>
          {operation ? (
            <section className="current-order">
              <div className="section-head">
                <span>LỆNH HIỆN TẠI</span>
                <Status value={operation.status} />
              </div>
              <h3>{operation.order_code}</h3>
              <h2>{operation.product_name}</h2>
              <div className="order-facts">
                <div>
                  <span>Công đoạn</span>
                  <b>{operation.operation_name}</b>
                </div>
                <div>
                  <span>Kế hoạch</span>
                  <b>
                    {num(operation.planned_quantity)} {operation.unit}
                  </b>
                </div>
                <div>
                  <span>Lũy kế đạt</span>
                  <b>
                    {num(operation.completed_quantity)} {operation.unit}
                  </b>
                </div>
                <div>
                  <span>Lũy kế lỗi</span>
                  <b>
                    {num(operation.defect_quantity)} {operation.unit}
                  </b>
                </div>
                <div>
                  <span>Ưu tiên</span>
                  <b>{operation.priority <= 2 ? "Khẩn" : "Bình thường"}</b>
                </div>
                <div>
                  <span>Hạn</span>
                  <b>{fmt(operation.planned_end_at)}</b>
                </div>
              </div>
              {operation.last_shift_reported_at && (
                <div className="callout shift-handover-summary">
                  <b>
                    Bàn giao từ ca của {operation.last_shift_worker_name}
                  </b>
                  <span>
                    Ca trước: đạt {num(operation.last_shift_good_quantity)}, lỗi{" "}
                    {num(operation.last_shift_defect_quantity)} ·{" "}
                    {fmt(operation.last_shift_reported_at)}
                  </span>
                  {operation.last_shift_handover_note && (
                    <span>Ghi chú: {operation.last_shift_handover_note}</span>
                  )}
                </div>
              )}
              <button
                className="primary wide"
                onClick={() => navigate(`/operation/${operation.id}`)}
              >
                XEM VÀ THỰC HIỆN LỆNH
                <ChevronRight />
              </button>
            </section>
          ) : (
            <section className="waiting">
              <RefreshCw />
              <div>
                <b>Đang chờ lệnh sản xuất</b>
                <span>Ứng dụng sẽ báo ngay khi có lệnh mới cho máy.</span>
              </div>
            </section>
          )}
          <div className="quick-actions">
            <button
              onClick={() =>
                navigate(
                  `/report-issue?machine=${session.machine_id}${operation?.id ? `&operation=${operation.id}` : ""}`,
                )
              }
            >
              <Wrench />
              Báo sự cố
            </button>
            <button className="danger-soft" onClick={openShiftReport}>
              <Square />
              Rời máy
            </button>
          </div>
        </>
      )}
      <EnablePush />
      {showShiftReport && session && (
        <div
          className="dialog-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !checkingOut)
              setShowShiftReport(false);
          }}
        >
          <form
            className="modal-card issue-resolution-dialog shift-report-dialog"
            onSubmit={checkout}
          >
            <div className="section-head">
              <span>KẾT THÚC CA LÀM VIỆC</span>
              <button
                type="button"
                className="icon-button"
                aria-label="Đóng"
                disabled={checkingOut}
                onClick={() => setShowShiftReport(false)}
              >
                <X />
              </button>
            </div>
            <h2>Báo cáo sản lượng & bàn giao</h2>
            {reportableOperation ? (
              <>
                <p>
                  {reportableOperation.order_code} ·{" "}
                  {reportableOperation.product_name} ·{" "}
                  {reportableOperation.operation_name}
                </p>
                <div className="shift-progress-summary">
                  <div>
                    <span>Kế hoạch</span>
                    <b>{num(reportableOperation.planned_quantity)}</b>
                  </div>
                  <div>
                    <span>Đã đạt lũy kế</span>
                    <b>{num(reportableOperation.completed_quantity)}</b>
                  </div>
                  <div>
                    <span>Lỗi lũy kế</span>
                    <b>{num(reportableOperation.defect_quantity)}</b>
                  </div>
                </div>
                <div className="shift-quantity-grid">
                  <label>
                    Sản lượng đạt bổ sung chưa báo cáo
                    <input
                      type="number"
                      min="0"
                      step="any"
                      required
                      inputMode="decimal"
                      value={shiftGood}
                      onChange={(event) => setShiftGood(event.target.value)}
                    />
                  </label>
                  <label>
                    Sản lượng lỗi bổ sung chưa báo cáo
                    <input
                      type="number"
                      min="0"
                      step="any"
                      required
                      inputMode="decimal"
                      value={shiftDefect}
                      onChange={(event) => setShiftDefect(event.target.value)}
                    />
                  </label>
                </div>
              </>
            ) : (
              <p>
                Máy hiện không có lệnh sản xuất đang giao cho bạn. Hệ thống sẽ
                chỉ kết thúc phiên đứng máy.
              </p>
            )}
            <label>
              Ghi chú bàn giao cho ca sau
              <textarea
                maxLength={1000}
                placeholder="Ví dụ: nguyên liệu còn ở khay số 2, cần kiểm tra khuôn trước khi chạy tiếp…"
                value={handoverNote}
                onChange={(event) => setHandoverNote(event.target.value)}
              />
            </label>
            {checkoutError && <div className="error">{checkoutError}</div>}
            <div className="issue-resolution-actions">
              <button
                type="button"
                className="secondary"
                disabled={checkingOut}
                onClick={() => setShowShiftReport(false)}
              >
                Quay lại
              </button>
              <button className="primary" disabled={checkingOut}>
                {checkingOut ? "ĐANG LƯU…" : "BÁO CÁO & RỜI MÁY"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Page>
  );
}

function ScanMachine() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [machine, setMachine] = useState<any>(null);
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [identifying, setIdentifying] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [cameraError, setCameraError] = useState<MachineCameraIssue | null>(null);
  const [openingCamera, setOpeningCamera] = useState(false);
  const [readingImage, setReadingImage] = useState(false);
  const imageInput = useRef<HTMLInputElement>(null);
  const cameraLibrary = useRef<Promise<typeof import('html5-qrcode')> | null>(null);
  const scanner = useRef<Html5QrcodeType | null>(null);
  const scannerStart = useRef<Promise<unknown> | null>(null);
  const scannerStop = useRef<Promise<void> | null>(null);
  const mounted = useRef(true);
  const starting = useRef(false);
  const resolving = useRef(false);
  const stopScanner = useCallback(() => {
    if (scannerStop.current) return scannerStop.current;
    const current = scanner.current;
    scanner.current = null;
    if (!current) return Promise.resolve();
    if (mounted.current) setStopping(true);
    const task = (async () => {
      try { await scannerStart.current; } catch { /* camera failed to start */ }
      try { if (current.isScanning) await current.stop(); } catch { /* already stopped */ }
      try { current.clear(); } catch { /* navigated away */ }
    })();
    scannerStop.current = task;
    void task.finally(() => {
      scannerStop.current = null;
      if (mounted.current) setStopping(false);
    });
    return task;
  }, []);
  const resolve = useCallback(
    async (raw: string) => {
      if (resolving.current) return;
      resolving.current = true;
      setError("");
      setIdentifying(true);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      try {
        const result = await identifyMachine(raw, token => post<any>("/machine-sessions/resolve-qr", { token },
          { signal: controller.signal, feedback: false }), stopScanner);
        if (mounted.current) { setScanning(false); setMachine(result); }
      } catch (e) {
        if (mounted.current) setError(controller.signal.aborted
          ? 'Máy chủ phản hồi chậm. Kiểm tra mạng rồi quét lại; bạn chưa nhận máy.' : (e as Error).message);
        resolving.current = false;
      } finally {
        window.clearTimeout(timeout);
        if (mounted.current) { setIdentifying(false); setScanning(!!scanner.current); }
      }
    },
    [stopScanner],
  );
  useEffect(() => {
    const token = params.get("t");
    if (token) resolve(token);
  }, [params, resolve]);
  useEffect(() => {
    mounted.current = true;
    // Warm the scanner chunk so retries after a permission error don't wait for
    // a network download between the user's click and the camera request.
    cameraLibrary.current ??= import('html5-qrcode');
    void cameraLibrary.current.catch(() => { cameraLibrary.current = null; });
    return () => { mounted.current = false; void stopScanner(); };
  }, [stopScanner]);
  const start = async () => {
    if (starting.current || scanner.current || scannerStop.current || resolving.current) return;
    starting.current = true;
    setError("");
    setCameraError(null);
    setOpeningCamera(true);
    setScanning(true);
    resolving.current = false;
    try {
      if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('Camera streaming not supported in this secure context');
      }
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await (cameraLibrary.current ??= import("html5-qrcode"));
      if (!mounted.current) return;
      const qr = new Html5Qrcode("qr-reader", { formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE], useBarCodeDetectorIfSupported: true, verbose: false });
      scanner.current = qr;
      scannerStart.current = qr.start(
        machineCameraConstraints,
        machineCameraConfig,
        resolve,
        () => {},
      );
      await scannerStart.current;
      if (!mounted.current) void stopScanner();
    } catch (e) {
      cameraLibrary.current = null;
      void stopScanner();
      if (mounted.current) {
        setScanning(false);
        setCameraError(machineCameraIssue(e));
      }
    } finally {
      scannerStart.current = null;
      starting.current = false;
      if (mounted.current) setOpeningCamera(false);
    }
  };
  const readImage = async (file?: File) => {
    if (!file || starting.current || resolving.current || scannerStop.current) return;
    starting.current = true;
    setReadingImage(true);
    setError("");
    setCameraError(null);
    try {
      await stopScanner();
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await (cameraLibrary.current ??= import('html5-qrcode'));
      if (!mounted.current) return;
      const qr = new Html5Qrcode('qr-reader', { formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE], verbose: false });
      scanner.current = qr;
      let raw: string;
      try {
        scannerStart.current = qr.scanFile(file, false);
        raw = await scannerStart.current as string;
      }
      catch { throw new Error('Chưa đọc được QR trong ảnh. Chọn ảnh rõ, có toàn bộ mã QR và viền trắng quanh mã.'); }
      if (mounted.current) await resolve(raw);
    } catch (e) {
      cameraLibrary.current = null;
      if (mounted.current) setError((e as Error).message || 'Chưa đọc được ảnh. Hãy thử ảnh khác.');
    } finally {
      void stopScanner();
      scannerStart.current = null;
      starting.current = false;
      if (mounted.current) { setReadingImage(false); setScanning(false); }
    }
  };
  const checkin = async () => {
    if (!machine || checkingIn) return;
    setCheckingIn(true);
    setError("");
    try {
      await post("/machine-sessions/check-in", {
        machineId: machine.id,
        qrToken: machine.qrToken,
        switchMachine: true,
        deviceInfo: {
          platform: navigator.platform || "unknown",
          userAgent: navigator.userAgent,
        },
      });
      void stopScanner();
      navigate("/worker", { replace: true });
    } catch (e) {
      setError((e as Error).message);
      setCheckingIn(false);
    }
  };
  return (
    <Page narrow>
      <div className="page-title">
        <div>
          <span className="eyebrow">QR MACHINE CHECK-IN</span>
          <h1>Quét mã máy</h1>
        </div>
      </div>
      <div hidden={!!machine} className={`machine-scan-area${cameraError ? ' camera-failed' : ''}`}>
          <div className={scanning ? "scanner active" : "scanner"}>
            <div id="qr-reader" />
            {!scanning && !stopping && !readingImage && (
              <div className="machine-scan-placeholder">
              <>
                <ScanLine />
                <p>Đưa toàn bộ mã QR vào khung. Giữ camera cách nhãn khoảng 15–25 cm.</p>
              </>
              </div>
            )}
          </div>
          {identifying && <p className="machine-scan-status" role="status" aria-live="polite"><LoaderCircle />{scanning ? 'Đã đọc QR — đang xác định máy…' : 'Đang xác định máy từ mã QR…'}</p>}
          {openingCamera && <p className="machine-scan-status" role="status"><LoaderCircle />Đang mở camera… Nếu được hỏi, chọn “Cho phép”.</p>}
          <button
            className="primary wide big"
            onClick={start}
            disabled={scanning || identifying || stopping || readingImage || openingCamera}
          >
            <QrCode />
            {readingImage ? "ĐANG ĐỌC ẢNH…" : identifying ? "ĐANG XÁC ĐỊNH MÁY…" : openingCamera ? "ĐANG MỞ CAMERA…" : scanning ? "ĐANG QUÉT…" : stopping ? "ĐANG ĐÓNG CAMERA…" : cameraError?.kind === 'permission' ? "CHO PHÉP CAMERA" : cameraError ? "THỬ MỞ CAMERA LẠI" : "MỞ CAMERA QUÉT MÃ"}
          </button>
          {cameraError && <div className="machine-camera-help" role="alert">
            <p>{cameraError.message}</p>
            {cameraError.kind === 'permission' && <details>
              <summary>Không thấy cửa sổ xin quyền?</summary>
              <p>Nếu trình duyệt đã chặn camera, app không thể tự bỏ chặn hay ép cửa sổ hỏi quyền xuất hiện.</p>
              <p>Cách nhanh: dùng Camera điện thoại hoặc Zalo quét mã trên máy → mở đường link → xác nhận nhận máy trong app.</p>
              <p>Nếu đang dùng app từ màn hình chính iPhone hoặc trình duyệt trong Zalo, thử mở trang này bằng Safari. Trong Safari, mở menu trang web ở thanh địa chỉ → Cài đặt trang web → Camera → Cho phép (tên mục có thể khác theo phiên bản).</p>
            </details>}
          </div>}
          {scanning && !openingCamera && <button className="secondary wide" disabled={identifying || stopping} onClick={async () => { await stopScanner(); if (mounted.current) setScanning(false); }}>Dừng camera</button>}
          <div className="machine-scan-fallback">
            <button className="secondary wide" disabled={scanning || identifying || stopping || readingImage || openingCamera} onClick={() => imageInput.current?.click()}>
              {readingImage ? 'ĐANG ĐỌC ẢNH…' : 'CHỤP / CHỌN ẢNH MÃ QR'}
            </button>
            <input ref={imageInput} type="file" accept="image/*" hidden aria-label="Chọn ảnh mã QR máy" onChange={e => { const file = e.target.files?.[0]; e.target.value = ''; void readImage(file); }} />
            <p>Ảnh được đọc ngay trên điện thoại, không gửi lên máy chủ. Sau khi nhận diện, bạn vẫn cần xác nhận nhận máy.</p>
          </div>
      </div>
      {machine && (
        <section className="confirm-machine">
          <CheckCircle2 />
          <span>ĐÃ NHẬN DIỆN</span>
          <h2>{machine.name}</h2>
          <p>
            {machine.code} · {machine.workshop_area || "Khu vực sản xuất"}
          </p>
          <div className="capacity">
            Người đang vận hành:{" "}
            <b>
              {machine.operators.length}/{machine.max_operators}
            </b>
          </div>
          <button
            className="primary wide big"
            onClick={checkin}
            disabled={checkingIn}
          >
            {checkingIn ? "ĐANG CHECK-IN…" : "XÁC NHẬN CHECK-IN"}
            <ChevronRight />
          </button>
          <button
            className="text-btn"
            onClick={() => {
              resolving.current = false;
              setMachine(null);
            }}
            disabled={checkingIn || stopping}
          >
            Quét lại mã khác
          </button>
        </section>
      )}
      {error && <div className="error space">{error}</div>}
    </Page>
  );
}

function LegacyOperation() {
  const [op, setOp] = useState<any>(null);
  const [error, setError] = useState("");
  const [showComplete, setShowComplete] = useState(false);
  const [good, setGood] = useState("");
  const [defect, setDefect] = useState("0");
  const navigate = useNavigate();
  const load = useCallback(
    () =>
      api("/operations/my-current")
        .then(setOp)
        .catch((e) => setError(e.message)),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  const action = async (name: string) => {
    try {
      await post(`/operations/${op.id}/${name}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const complete = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await post(`/operations/${op.id}/complete`, {
        completedQuantity: Number(good),
        defectQuantity: Number(defect),
      });
      navigate("/worker");
    } catch (e) {
      setError((e as Error).message);
    }
  };
  if (!op)
    return (
      <Page>
        {error ? (
          <div className="error">{error}</div>
        ) : (
          <div className="skeleton tall" />
        )}
      </Page>
    );
  return (
    <Page narrow>
      <button className="back" onClick={() => navigate("/worker")}>
        ← Máy của tôi
      </button>
      <section className="operation-card">
        <div className="section-head">
          <span>LỆNH SẢN XUẤT</span>
          <Status value={op.status} />
        </div>
        <h1>{op.order_code}</h1>
        <h2>{op.product_name}</h2>
        <dl>
          <dt>Công đoạn</dt>
          <dd>{op.operation_name}</dd>
          <dt>Máy</dt>
          <dd>{op.machine_name}</dd>
          <dt>Số lượng</dt>
          <dd>
            {num(op.planned_quantity)} {op.unit}
          </dd>
          <dt>Đã đạt lũy kế</dt>
          <dd>
            {num(op.completed_quantity)} {op.unit}
          </dd>
          <dt>Lỗi lũy kế</dt>
          <dd>
            {num(op.defect_quantity)} {op.unit}
          </dd>
          <dt>Còn lại dự kiến</dt>
          <dd>
            {num(
              Math.max(
                0,
                Number(op.planned_quantity) - Number(op.completed_quantity),
              ),
            )} {op.unit}
          </dd>
          <dt>Deadline</dt>
          <dd>{fmt(op.planned_end_at)}</dd>
          <dt>Ghi chú</dt>
          <dd>{op.order_notes || "Không có"}</dd>
        </dl>
        {op.status === "RELEASED" && (
          <button className="primary wide big" onClick={() => action("ack")}>
            <CheckCircle2 />
            ĐÃ NHẬN LỆNH
          </button>
        )}
        {["ACKNOWLEDGED", "PAUSED"].includes(op.status) && (
          <button className="primary wide big" onClick={() => action("start")}>
            <Play />
            BẮT ĐẦU SẢN XUẤT
          </button>
        )}
        {op.status === "IN_PROGRESS" && (
          <div className="work-actions">
            <button className="secondary" onClick={() => action("pause")}>
              <Square />
              Tạm dừng
            </button>
            <button className="primary" onClick={() => setShowComplete(true)}>
              <CheckCircle2 />
              Hoàn thành
            </button>
          </div>
        )}
        {showComplete && (
          <form className="complete-form" onSubmit={complete}>
            <h3>Báo cáo hoàn thành</h3>
            <label>
              Số lượng đạt
              <input
                type="number"
                min="0"
                required
                value={good}
                onChange={(e) => setGood(e.target.value)}
              />
            </label>
            <label>
              Số lượng lỗi
              <input
                type="number"
                min="0"
                required
                value={defect}
                onChange={(e) => setDefect(e.target.value)}
              />
            </label>
            <button className="primary wide">XÁC NHẬN HOÀN THÀNH</button>
          </form>
        )}
        {error && <div className="error space">{error}</div>}
      </section>
    </Page>
  );
}

function InAppNotifications() {
  const { user } = useAuth();
  const [notices,setNotices]=useState<any[]>([]);
  const [requests,setRequests]=useState<any[]>([]);
  const [integratedRequests,setIntegratedRequests]=useState<any[]>([]);
  const [handoffs,setHandoffs]=useState<any[]>([]);
  const [good,setGood]=useState("0");
  const [defect,setDefect]=useState("0");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const [collapsed,setCollapsed]=useState(false);
  const load=useCallback(async()=>{
    const rows=await api<any[]>('/notifications');
    setNotices(rows.filter(row=>!row.opened_at && row.entity_type!=='progress_request'));
    if(can(user,'operations.work')) {
      setRequests(await api<any[]>('/progress-requests/my'));
      setIntegratedRequests(await api<any[]>('/integrated/requests'));
      setHandoffs(await api<any[]>('/urgent-handoffs/my'));
    }
  },[user]);
  useEffect(()=>{void load().catch(()=>{});},[load]);
  useSocket(load);
  const current=requests[0];
  const handoff=!current ? handoffs[0] : null;
  const handoverKey=useRef(crypto.randomUUID());
  useEffect(()=>{setGood("0");setDefect("0");setError("");},[current?.id,handoff?.id]);
  const report=async(event:FormEvent)=>{
    event.preventDefault();if(busy || (!current && !handoff))return;
    setBusy(true);setError("");
    try {
      if(handoff)await post(`/operations/${handoff.id}/urgent-handover`,{sourceOperationId:handoff.source_id,reportId:handoverKey.current,goodQuantity:Number(good),defectQuantity:defect===''?null:Number(defect)});
      else await post(`/progress-requests/${current.id}/report`,{goodQuantity:Number(good),defectQuantity:Number(defect)});
      handoverKey.current=crypto.randomUUID();await load();
    }
    catch(caught){setError((caught as Error).message);}finally{setBusy(false);}
  };
  const notice=notices[0];
  if(collapsed && (notice || current || handoff || integratedRequests.length))return <button className="primary in-app-notifications-toggle" onClick={()=>setCollapsed(false)}><Bell/>Thông báo / yêu cầu đang chờ</button>;
  if(integratedRequests.length&&!current&&!handoff)return <aside className="in-app-notifications" aria-live="polite"><button className="secondary" onClick={()=>setCollapsed(true)}>Thu gọn thông báo</button><h3><Bell/>Yêu cầu báo cáo hiện tại</h3><p>{integratedRequests[0].order_code} · {integratedRequests[0].operation_name}</p><NavLink className="primary" to={`/production?tab=jobs&job=${integratedRequests[0].operation_id}`}>Mở để báo cáo ({integratedRequests.length})</NavLink></aside>;
  if(!current && !handoff && !notice)return null;
  return <aside className="in-app-notifications" aria-live="polite">
    <button className="secondary" onClick={()=>setCollapsed(true)}>Thu gọn thông báo</button>
    {current || handoff ? <form onSubmit={report}>
      <h3><Bell />{handoff ? 'Bàn giao để chuyển lệnh gấp' : 'Yêu cầu báo cáo tiến độ'}</h3>
      {handoff ? <p>Dừng an toàn <b>{handoff.source_order_code}</b> tại {handoff.machine_name}, báo phần sản lượng chưa ghi nhận để chuyển sang <b>{handoff.order_code}</b>. Sau xác nhận, lệnh cũ tạm ngưng; lệnh mới vẫn cần nhận và bắt đầu chạy.</p> : <p>{current.requested_by_name} yêu cầu báo cáo <b>{current.order_code}</b> · {current.machine_name}</p>}
      <p>Đã ghi nhận: <b>{num((current || handoff).completed_quantity)}</b> · Lỗi: {num((current || handoff).defect_quantity)}</p>
      <small>Chỉ nhập số lượng bổ sung CHƯA báo cáo. Không nhập lại tổng đã ghi nhận. Sau khi gửi, kết ca/hoàn thành cũng chỉ nhập phần phát sinh tiếp theo.</small>
      <div className="in-app-report-fields"><label>Đạt bổ sung<input required type="number" min="0" step="any" max="999999999999" value={good} onChange={e=>setGood(e.target.value)} /></label><label>Lỗi bổ sung<input required type="number" min="0" step="any" max="999999999999" value={defect} onChange={e=>setDefect(e.target.value)} /></label></div>
      {error && <p className="error">{error}</p>}
      <button className="primary" disabled={busy}>{busy ? "Đang gửi…" : handoff ? "Xác nhận đã dừng an toàn và bàn giao" : "Gửi báo cáo tiến độ"}</button>
    </form> : <div><h3><Bell />{notice.title}</h3><p>{notice.body}</p><div className="page-actions"><NavLink to={`/notifications/${notice.delivery_id}`}>Mở yêu cầu ({notices.length})</NavLink><button type="button" onClick={()=>{void post(`/notifications/${notice.delivery_id}/open`,{}, {feedback:{pending:'Đang đánh dấu đã xem…',success:'Đã đánh dấu thông báo đã xem.'}}).then(load).catch(()=>{});}}>Đã xem</button></div></div>}
  </aside>;
}

function Notifications() {
  const [items, setItems] = useState<any[]>([]);
  const load = useCallback(
    () => api<any[]>("/notifications").then(setItems),
    [],
  );
  useEffect(() => {
    load();
  }, [load]);
  useSocket(load);
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">INBOX</span>
          <h1>Thông báo</h1>
        </div>
      </div>
      <div className="notification-list">
        {items.map((n) => (
          <NavLink
            key={n.delivery_id}
            to={`/notifications/${n.delivery_id}`}
            className={`notification-item ${n.acknowledged_at ? "read" : ""}`}
          >
            <span className={`notice-dot ${n.level?.toLowerCase()}`} />
            <div>
              <div className="section-head">
                <b>{n.title}</b>
                <small>{fmt(n.created_at)}</small>
              </div>
              <p>{n.body}</p>
              {n.requires_ack && !n.acknowledged_at && (
                <span className="ack-label">CẦN XÁC NHẬN</span>
              )}
              <span className="notification-open">Mở thông báo →</span>
            </div>
          </NavLink>
        ))}
        {!items.length && <div className="empty-list">Chưa có thông báo.</div>}
      </div>
    </Page>
  );
}

function NotificationDetail() {
  const {id}=useParams();
  const {user}=useAuth();
  const [notice,setNotice]=useState<any>(null);
  const [error,setError]=useState("");
  const [busy,setBusy]=useState(false);
  const load=useCallback(async()=>{
    if(!id)return;
    try {setNotice(await api<any>(`/notifications/${id}`));setError("");}
    catch(caught){setError((caught as Error).message);}
  },[id]);
  useEffect(()=>{void load();},[load]);
  useSocket(load);
  useEffect(()=>{if(id && notice && !notice.opened_at)void post(`/notifications/${id}/open`).then(load).catch(()=>{});},[id,notice,load]);
  const assigned=notice?.entity_type==='operation' && notice?.operation_assignee_id===user?.id &&
    notice?.operation_status==='ASSIGNED' && notice?.dispatch_state!=='QUEUED' && notice?.dispatch_state!=='INTERRUPTED';
  const acknowledge=async()=>{
    if(busy || !id)return;
    setBusy(true);
    try {await post(`/notifications/${id}/ack`);await load();}
    catch(caught){setError((caught as Error).message);}
    finally{setBusy(false);}
  };
  return <Page narrow>
    <NavLink className="back" to="/notifications">← Thông báo</NavLink>
    {notice ? <section className="operation-card notification-detail">
      <span className="eyebrow">YÊU CẦU / THÔNG BÁO</span>
      <h1>{notice.title}</h1>
      <p>{notice.body}</p>
      {notice.order_code && <p>{notice.order_code}{notice.machine_name ? ` · ${notice.machine_name}` : ''}</p>}
      {assigned ? <NavLink className="primary" to={`/operation/${notice.entity_id}`}>Mở công việc để tiếp nhận</NavLink>
        : ['integrated_request','warehouse_task'].includes(notice.entity_type) ? <NavLink className="primary" to={`/production?tab=${notice.entity_type==='integrated_request'?'jobs':'tasks'}`}>Mở yêu cầu để thực hiện</NavLink>
        : notice.entity_type==='progress_request' ? <NavLink className="primary" to="/worker">Mở yêu cầu báo cáo</NavLink>
        : notice.entity_type==='machine_assignment' ? <NavLink className="primary" to="/scan-machine">Quét QR tiếp quản máy</NavLink>
        : ['production_order','material_request'].includes(notice.entity_type) && notice.production_order_id && can(user,'operations.assign') ? <NavLink className="primary" to={notice.model_version===2?`/production?tab=orders&order=${notice.production_order_id}`:`/admin/orders/${notice.production_order_id}/dispatch`}>Mở màn hình điều phối</NavLink>
        : notice.event_type==='URGENT_SWITCH_REQUEST' ? <NavLink className="primary" to="/worker">Mở yêu cầu bàn giao lệnh gấp</NavLink> : null}
      {notice.requires_ack && !notice.acknowledged_at && !assigned && !['progress_request','machine_assignment','integrated_request'].includes(notice.entity_type) && notice.event_type!=='URGENT_SWITCH_REQUEST' && <button type="button" className="secondary notification-ack" disabled={busy} onClick={acknowledge}>{busy?'Đang xác nhận…':'Xác nhận đã xem'}</button>}
      {notice.acknowledged_at && <span className="notification-confirmed"><CheckCircle2 /> Đã xác nhận</span>}
    </section> : !error ? <div className="skeleton tall" /> : null}
    {error && <div className="error space">{error}</div>}
  </Page>;
}

function ProgressRequestButton({operationId,orderId}:{operationId:string;orderId?:string}) {
  const [busy,setBusy]=useState(false);
  const [message,setMessage]=useState("");
  const send=async()=>{
    if(busy)return;setBusy(true);setMessage("");
    try {await post(progressRequestPath(operationId,orderId));setMessage(orderId?"Đã gửi yêu cầu tới các công nhân đang làm lệnh này.":"Đã gửi yêu cầu tới công nhân đang đứng máy.");}
    catch(error){setMessage((error as Error).message);}finally{setBusy(false);}
  };
  return <div><button type="button" className="secondary" disabled={busy} onClick={()=>void send()}>{busy ? "Đang gửi…" : "Yêu cầu báo cáo tiến độ"}</button>{message && <small role="status">{message}</small>}</div>;
}

function TvTaskRow({ task, selected, onSelect }: { task: any; selected?: boolean; onSelect?: () => void }) {
  const afterProduction = ["PACKING", "SHIPPING"].includes(task.node_type);
  const dynamic=task.routing_mode==='DISPATCHER_PLANNED';
  const total = Number(dynamic ? task.order_quantity : task.planned_quantity || task.order_quantity || 0);
  const done = Number(dynamic ? task.final_completed_quantity : task.completed_quantity || 0);
  const percent = afterProduction ? 100 : total > 0 ? Math.min(100, Math.max(0, Math.round(done / total * 100))) : 0;
  const stage = task.node_type === "PACKING" ? "Đóng gói"
    : task.node_type === "SHIPPING" ? "Giao hàng"
    : task.node_label || task.operation_name || "Sản xuất";
  const actor = !afterProduction && task.active_operator_name
    ? task.active_operator_name
    : task.assignee_name || "Chưa phân công";
  const alert = ["BLOCKED", "WAITING_SUPPORT"].includes(task.status);
  const activityState = task.dispatch_state==='QUEUED' ? 'Hàng chờ máy' : task.dispatch_state==='INTERRUPTED' ? 'Tạm ngưng vì lệnh gấp' : alert ? "Chờ hỗ trợ" : task.status === "IN_PROGRESS"
    ? "Đang thực hiện" : task.status === "PAUSED" ? "Tạm dừng / chờ ca sau" : task.route_placeholder ? "Đang chờ người điều phối lập tuyến" : "Chờ nhận việc";
  return <div className={`tv-task-row ${alert ? "alert" : ""} ${selected ? "tv-selected" : ""}`} role="button" tabIndex={0} onClick={onSelect} onFocus={onSelect} aria-label={`Chọn đơn ${task.order_code}`}>
    <div className="tv-task-order"><b>{task.order_code}</b><small>{task.customer_name || task.product_name || "—"}</small>{task.integrated&&<small>{task.product_name}</small>}</div>
    <div className="tv-task-actor">{task.tv_people?<div className="tv-parallel-workers">{task.tv_people.map((person:any)=><div key={person.id} className={person.status==='IN_PROGRESS'?'tv-stage-running':''}><b>{person.worker_name||'Chờ nhận việc'}</b><small>{person.operation_name} · {person.machine_code||'—'} · {person.status==='IN_PROGRESS'?'Đang làm':person.status==='WAITING_SUPPORT'?'Chờ hỗ trợ':person.dispatch_state==='QUEUED'?'Chờ máy':'Chờ nhận/chạy'}</small></div>)}</div>:<><b>{actor}</b><small>{stage}{task.machine_code ? ` · ${task.machine_code}` : ""}</small><small className="tv-task-state">{activityState}</small></>}{task.progress_report_pending && <small className="tv-report-pending">{task.report_caption||'Đang chờ báo cáo sản lượng hiện tại'}</small>}</div>
    <div className="tv-task-progress">
      <strong>{task.is_delivery?'Giao hàng':afterProduction ? "100%" : `${percent}%`}</strong>
      {!task.is_delivery && <span className="machine-progress-track" role="progressbar" aria-label={`Tiến độ sản xuất ${task.order_code}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}><span style={{ width: `${percent}%` }} /></span>}
      <small>{task.is_delivery?'Xuất/giao từng phần':afterProduction ? "SX hoàn tất" : total > 0 ? `${num(done)} / ${num(total)} ${task.unit || ""}` : "Chưa có số lượng"}</small>{task.integrated&&task.last_report_at&&<small>Báo gần nhất {fmt(task.last_report_at)}</small>}
    </div>
  </div>;
}

function Dashboard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<any>();
  const [machines, setMachines] = useState<any[]>([]);
  const [live, setLive] = useState<any>();
  const [integrated,setIntegrated]=useState<any>({jobs:[],requests:[],deliveries:[]});
  const [tvScenario, setTvScenario] = useState<TvScenario | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>();
  const [clock, setClock] = useState(() => new Date());
  const [tvMode, setTvMode] = useState(() => searchParams.get("tv") === "1");
  const [tvAudioDuck,setTvAudioDuck]=useState(false);
  const [tvDepartment, setTvDepartment] = useState("");
  const [tvMachineIndex, setTvMachineIndex] = useState(0);
  const [tvTaskPage, setTvTaskPage] = useState(0);
  const [tvSelection, setTvSelection] = useState<"machines" | "tasks">("machines");
  const [tvSelectedTaskId, setTvSelectedTaskId] = useState("");
  const [tvReportMessage, setTvReportMessage] = useState("");
  const tvRequestBusy = useRef(false);
  const tvMachineListRef = useRef<HTMLDivElement>(null);
  const load = useCallback(
    () =>
      Promise.all([
        api("/dashboard"),
        api<any[]>("/machines"),
        api("/live-operations"),
        api<TvScenario | null>("/tv/active"),
        api<{ normalMode: boolean }>("/tv/kiosk-mode"),
        api('/integrated/live'),
      ]).then(([d, m, current, activeScenario, kioskMode,production]) => {
        setData(d);
        setMachines(m);
        setLive(current);
        setIntegrated(production);
        setTvScenario(activeScenario);
        setLastUpdated(new Date());
        if (tvMode && window.PhuocHaoTv)
          window.PhuocHaoTv.setKioskEnabled(!kioskMode.normalMode);
      }),
    [tvMode],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const clockTimer = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(clockTimer);
  }, []);
  useEffect(() => {
    document.body.classList.toggle("dashboard-tv-mode", tvMode);
    return () => document.body.classList.remove("dashboard-tv-mode");
  }, [tvMode]);
  useEffect(
    () => () => {
      if (document.fullscreenElement) void document.exitFullscreen();
    },
    [],
  );
  useEffect(() => {
    const syncFullscreen = () => {
      if (!document.fullscreenElement) setTvMode(false);
    };
    document.addEventListener("fullscreenchange", syncFullscreen);
    return () =>
      document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);
  useSocket(load);
  const toggleTvMode = async () => {
    if (!tvMode) {
      setTvMode(true);
      try {
        await document.documentElement.requestFullscreen?.();
      } catch {
        // TV layout still works when the browser blocks the Fullscreen API.
      }
      return;
    }
    if (document.fullscreenElement) await document.exitFullscreen();
    setTvMode(false);
  };
  const machineJobs = useMemo(() => machineProductionJobs(live?.tasks || [], integrated.jobs || []), [live, integrated]);
  const activeTasks = useMemo(
    () => {
      const candidates=(live?.tasks || [])
        .filter(
          (task: any) => !task.order_line_id && !["PLANNED","COMPLETED", "CANCELLED"].includes(task.status),
        )
        .sort((a: any, b: any) => {
          const alertStatus = (task: any) =>
            ["BLOCKED", "WAITING_SUPPORT"].includes(task.status) ? 0 : 1;
          return alertStatus(a) - alertStatus(b) || a.sequence - b.sequence;
        });
      const grouped=new Map<string,any>();
      for(const task of candidates){
        if(task.routing_mode!=='DISPATCHER_PLANNED'){grouped.set(task.id,task);continue;}
        const current=grouped.get(task.production_order_id);
        const score=(item:any)=>item.route_source==='SUPPLEMENT'&&item.status==='IN_PROGRESS'?0:item.status==='IN_PROGRESS'?1:item.route_source==='SUPPLEMENT'?2:['BLOCKED','WAITING_SUPPORT'].includes(item.status)?3:4;
        if(!current||score(task)<score(current))grouped.set(task.production_order_id,task);
      }
      const lines=new Map<string,any>();
      for(const op of integrated.jobs||[]){if(['PLANNED','COMPLETED','CANCELLED'].includes(op.status))continue;const line=lines.get(op.order_line_id)||{...op,id:op.order_line_id,integrated:true,routing_mode:'DISPATCHER_PLANNED',order_quantity:op.line_target,final_completed_quantity:op.line_produced,unit:op.line_unit,tv_people:[]};line.tv_people.push(op);if(!line.last_report_at||op.last_report_at>line.last_report_at)line.last_report_at=op.last_report_at;lines.set(op.order_line_id,line);}
      for(const line of lines.values()){const ids=line.tv_people.map((p:any)=>p.id);const requests=(integrated.requests||[]).filter((r:any)=>ids.includes(r.operation_id)&&r.status!=='STALE');const waiting=requests.filter((r:any)=>r.status==='PENDING'),reported=requests.filter((r:any)=>r.status==='REPORTED');line.progress_report_pending=waiting.length>0;line.report_caption=`${reported.length?reported.map((r:any)=>r.worker_name).join(', ')+' đã báo cáo · ':''}Chờ ${waiting.map((r:any)=>r.worker_name).join(', ')}`;}
      const shipping=(integrated.deliveries||[]).map((d:any)=>({...d,id:'shipping-'+d.id,is_delivery:true,node_type:'SHIPPING',order_code:d.order_codes||d.number,operation_name:d.status==='DELIVERING'?'Đang giao':'Chờ đi giao',assignee_name:d.worker_name,customer_name:d.customer_name,status:d.status==='DELIVERING'?'IN_PROGRESS':'ASSIGNED'}));
      const parallelRows=[...lines.values()].flatMap(line=>{
        const rows=[];for(let start=0;start<line.tv_people.length;start+=2)rows.push({...line,id:start===0?line.id:`${line.id}-people-${start}`,tv_people:line.tv_people.slice(start,start+2)});return rows;
      });
      const planning=(integrated.planning||[]).map((l:any)=>({...l,id:`planning-${l.order_line_id}`,integrated:true,routing_mode:'DISPATCHER_PLANNED',route_placeholder:true,order_quantity:l.line_target,final_completed_quantity:l.line_produced,unit:l.line_unit,operation_name:'Chờ lập công đoạn',status:'ASSIGNED',assignee_name:l.dispatcher_name}));
      return [...grouped.values(),...parallelRows,...planning,...shipping];
    },
    [live,integrated],
  );
  const tvDepartments = useMemo(
    () => [...new Set(machines.map((machine) => machine.department_name || "Chưa phân xưởng"))]
      .sort((a, b) => a.localeCompare(b, "vi")),
    [machines],
  );
  const currentTvDepartment = tvDepartments.includes(tvDepartment)
    ? tvDepartment
    : tvDepartments[0] || "";
  const tvMachines = useMemo(
    () => machines.filter((machine) => (machine.department_name || "Chưa phân xưởng") === currentTvDepartment),
    [currentTvDepartment, machines],
  );
  const tvTasksPerPage = window.innerHeight < 800 ? 4 : 5;
  const tvTaskPages = Math.max(1, Math.ceil(activeTasks.length / tvTasksPerPage));
  const currentTvTaskPage = Math.min(tvTaskPage, tvTaskPages - 1);
  const tvVisibleTasks = activeTasks.slice(
    currentTvTaskPage * tvTasksPerPage,
    (currentTvTaskPage + 1) * tvTasksPerPage,
  );
  const changeTvDepartment = useCallback((direction: number) => {
    if (!tvDepartments.length) return;
    const index = Math.max(0, tvDepartments.indexOf(currentTvDepartment));
    setTvDepartment(tvDepartments[(index + direction + tvDepartments.length) % tvDepartments.length]);
    setTvMachineIndex(0);
    tvMachineListRef.current?.scrollTo({ top: 0 });
  }, [currentTvDepartment, tvDepartments]);
  const selectTvTask = useCallback((task: any) => {
    setTvSelection("tasks");
    setTvSelectedTaskId(task.id);
    setTvTaskPage(Math.floor(activeTasks.findIndex((item: any) => item.id === task.id) / tvTasksPerPage));
    setTvReportMessage("");
  }, [activeTasks, tvTasksPerPage]);
  const requestTvProgress = useCallback(async (task: any) => {
    if (tvRequestBusy.current) return;
    if(task.route_placeholder){setTvReportMessage(`Đơn đang chờ ${task.dispatcher_name || 'người điều phối'} lập công đoạn tiếp theo.`);return;}
    const progressTotal=Number(task.routing_mode==='DISPATCHER_PLANNED' ? task.order_quantity : task.planned_quantity);
    const progressDone=Number(task.routing_mode==='DISPATCHER_PLANNED' ? task.final_completed_quantity : task.completed_quantity);
    if (progressTotal > 0 && progressDone >= progressTotal) {
      setTvReportMessage("Đã đủ sản lượng; không cần yêu cầu báo cáo thêm."); return;
    }
    if (!can(user, 'operations.assign')) { setTvReportMessage("Tài khoản kết nối TV chưa có quyền yêu cầu báo cáo."); return; }
    if (task.progress_report_pending) { setTvReportMessage("Đang chờ báo cáo sản lượng hiện tại."); return; }
    if ((!task.machine_id&&!task.integrated) || ["PACKING", "SHIPPING"].includes(task.node_type)) {
      setTvReportMessage("Chỉ yêu cầu sản lượng đối với công việc sản xuất tại máy."); return;
    }
    tvRequestBusy.current = true;
    setTvReportMessage("Đang gửi yêu cầu…");
    try {
      await post(progressRequestPath(task.id,task.integrated?task.production_order_id:undefined));
      setLive((current: any) => ({...current, tasks: current.tasks.map((item: any) => item.id === task.id ? {...item, progress_report_pending: true} : item)}));
      setTvReportMessage("Đã gửi yêu cầu báo cáo đến công nhân.");
      await load();
    } catch (error) { setTvReportMessage((error as Error).message); }
    finally { tvRequestBusy.current = false; }
  }, [load, user]);
  useEffect(() => {
    if (!tvMode) return;
    const onRemoteKey = (event: KeyboardEvent) => {
      if (document.querySelector(".tv-scene-layer")) return;
      if (!["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter", "Select"].includes(event.key)) return;
      event.preventDefault();
      if (tvSelection === "tasks") {
        const index = Math.max(0, activeTasks.findIndex((task: any) => task.id === tvSelectedTaskId));
        if (event.key === "ArrowLeft") { setTvSelection("machines"); setTvReportMessage(""); }
        else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
          const task = activeTasks[Math.max(0, Math.min(activeTasks.length - 1, index + (event.key === "ArrowUp" ? -1 : 1)))];
          if (task) selectTvTask(task);
        } else if ((event.key === "Enter" || event.key === "Select") && !event.repeat && activeTasks[index]) {
          void requestTvProgress(activeTasks[index]);
        }
        return;
      }
      if (event.key === "Enter" || event.key === "Select") {
        const task = taskForMachine(activeTasks,tvMachines[tvMachineIndex]?.id);
        if (task) selectTvTask(task);
        else setTvReportMessage("Máy chưa có lệnh sản xuất đang mở.");
        return;
      }
      if (event.key === "ArrowLeft") changeTvDepartment(-1);
      else if (event.key === "ArrowRight") changeTvDepartment(1);
      else if (event.key === "ArrowUp") setTvMachineIndex((index) => Math.max(0, index - 1));
      else setTvMachineIndex((index) => Math.min(Math.max(0, tvMachines.length - 1), index + 1));
    };
    window.addEventListener("keydown", onRemoteKey);
    return () => window.removeEventListener("keydown", onRemoteKey);
  }, [activeTasks, changeTvDepartment, requestTvProgress, selectTvTask, tvMachineIndex, tvMachines, tvMode, tvSelectedTaskId, tvSelection]);
  useEffect(() => {
    setTvMachineIndex((index) => Math.min(index, Math.max(0, tvMachines.length - 1)));
  }, [tvMachines.length]);
  useEffect(() => {
    if (!tvMode) return;
    tvMachineListRef.current?.querySelector<HTMLElement>(`[data-tv-machine-index="${tvMachineIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [currentTvDepartment, tvMachineIndex, tvMode]);
  useEffect(() => {
    if (!tvMode || tvTaskPages <= 1 || tvSelection === "tasks") return;
    const timer = window.setInterval(() => setTvTaskPage((page) => (page + 1) % tvTaskPages), 15000);
    return () => window.clearInterval(timer);
  }, [tvMode, tvTaskPages, tvSelection]);
  useEffect(() => {
    if (tvSelection !== "tasks") return;
    const index = activeTasks.findIndex((task: any) => task.id === tvSelectedTaskId);
    if (index < 0) { setTvSelection("machines"); setTvReportMessage(""); }
    else setTvTaskPage(Math.floor(index / tvTasksPerPage));
  }, [activeTasks, tvSelectedTaskId, tvSelection, tvTasksPerPage]);
  const playbackScenario = useMemo<TvScenario>(
    () =>
      tvScenario || {
        id: "default-realtime-dashboard",
        name: "Tổng quan sản xuất",
        is_active: true,
        updated_at: "",
        media: [],
        items: [
          {
            id: "default-dashboard-scene",
            type: "DASHBOARD",
            title: "Tổng quan sản xuất realtime",
            durationSeconds: 3600,
            config: {},
          },
        ],
      },
    [tvScenario],
  );
  if (!data)
    return (
      <Page>
        <div className="skeleton tall" />
      </Page>
    );
  const timeText = new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(clock);
  const dateText = new Intl.DateTimeFormat("vi-VN", {
    timeZone: "Asia/Ho_Chi_Minh",
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(clock);
  return (
    <Page>
      <div className={`dashboard-screen ${tvMode ? "is-tv" : ""}`}>
        <div className="page-title dashboard-title">
          <div className="dashboard-heading">
            <img
              className="dashboard-tv-logo"
              src="/brand/phuoc-hao-horizontal.png"
              alt="Phước Hào"
            />
            <div>
              <span className="eyebrow">TRUNG TÂM ĐIỀU HÀNH REALTIME</span>
              <h1>Tổng quan sản xuất</h1>
            </div>
          </div>
          <div className="dashboard-actions">
            <div className="dashboard-clock">
              <span className="live-dot" />
              <div>
                <b>{timeText}</b>
                <small>
                  {dateText} · Cập nhật{" "}
                  {lastUpdated ? fmt(lastUpdated.toISOString()) : "—"}
                </small>
              </div>
            </div>
            {!tvMode && (
              <NavLink className="secondary dashboard-flow-link" to="/admin/live">
                <Activity /> Sơ đồ công đoạn
              </NavLink>
            )}
            {!tvMode && can(user,'tv.view') && (
              <NavLink className="secondary dashboard-tv-setup" to="/admin/tv">
                <MonitorPlay /> Kịch bản TV
              </NavLink>
            )}
            {!tvMode && (
              <button className="secondary" onClick={() => void toggleTvMode()}>
                <Maximize2 /> Trình chiếu TV
              </button>
            )}
          </div>
        </div>
        <div className="kpi-grid">
          <Kpi
            label="Tổng máy"
            value={data.machines.total}
            icon={<Factory />}
          />
          <Kpi label="Đang chạy" value={data.machines.running} tone="green" />
          <Kpi
            label="Sẵn sàng"
            value={data.machines.ready ?? data.machines.idle}
          />
          <Kpi label="Lệnh trễ" value={data.orders.overdue} tone="red" />
          <Kpi label="Chưa xác nhận" value={data.unacknowledged} tone="amber" />
          <Kpi label="Sự cố" value={data.issues.open} tone="red" />
        </div>
        <div className="dashboard-grid">
          <section className="table-card dashboard-machines-card">
            <div className="card-title">
              <h2>Tình trạng máy</h2>
              {tvMode && <div className="tv-workshop-nav" aria-label="Chọn xưởng">
                <button type="button" onClick={() => changeTvDepartment(-1)} aria-label="Xưởng trước" disabled={tvDepartments.length < 2}><ChevronLeft /></button>
                <span>{currentTvDepartment || "Chưa có máy"} <small>{tvDepartments.length ? `${tvDepartments.indexOf(currentTvDepartment) + 1}/${tvDepartments.length}` : ""}</small></span>
                <button type="button" onClick={() => changeTvDepartment(1)} aria-label="Xưởng tiếp theo" disabled={tvDepartments.length < 2}><ChevronRight /></button>
              </div>}
              {!tvMode && <NavLink to="/admin/machines">Xem tất cả</NavLink>}
            </div>
            <div className="machine-list" ref={tvMachineListRef}>
              {(tvMode ? tvMachines : machines.slice(0, 8)).map((m, index) => (
                <div
                  key={m.id}
                  data-tv-machine-index={tvMode ? index : undefined}
                  className={`machine-row ${m.status.toLowerCase()} ${machineJobs.some((task:any) => task.machine_id === m.id) ? "has-job" : ""} ${tvMode && tvSelection === "machines" && index === tvMachineIndex ? "tv-selected" : ""}`}
                >
                  <span className={`machine-state ${m.status.toLowerCase()}`} />
                  <div>
                    <b>{m.name}</b>
                    <small>
                      {m.code} ·{" "}
                      {m.operators.length
                        ? <strong className="machine-operator-name">{m.operators.map((o: any) => o.fullName).join(", ")}</strong>
                        : "Chưa có người vận hành"}
                    </small>
                    {m.handling_by_name && <small className="machine-handler">Đang xử lý: <strong>{m.handling_by_name}</strong></small>}
                  </div>
                  <Status value={m.status} />
                  {machineJobs.filter((task:any) => task.machine_id === m.id).slice(0, tvMode ? 1 : undefined).map((task:any) => {
                    const done=Number(task.completed_quantity),total=Number(task.planned_quantity);
                    const percent=total>0 ? Math.round(done/total*100) : 0;
                    return <div className="machine-job-progress" key={task.id}>
                      <div className="machine-job-heading"><b>{task.order_code}</b><span>{task.customer_name || task.product_name}</span></div>
                      {task.integrated && !task.is_final_output ? <small>{task.operation_name} · {task.status==='IN_PROGRESS'?'Đang thực hiện':task.status==='WAITING_SUPPORT'?'Chờ hỗ trợ':'Chờ tiếp quản/chạy'}</small> : <>
                      <div className="machine-progress-label"><strong>{num(done)} / {num(total)} {task.unit}</strong><strong>{percent}%</strong></div>
                      <span className="machine-progress-track" role="progressbar" aria-label={`Tiến độ ${task.order_code}`} aria-valuemin={0} aria-valuemax={total} aria-valuenow={Math.min(done,total)} aria-valuetext={`${num(done)} trên ${num(total)} ${task.unit}`}><span style={{width:`${Math.min(100,Math.max(0,percent))}%`}} /></span>
                      {!tvMode && <small>Còn {num(Math.max(0,total-done))} {task.unit} · Lỗi: {task.integrated?'Theo báo cáo (nếu có)':num(task.defect_quantity)}{task.status==='PAUSED' ? ' · Tạm dừng / chờ tiếp quản ca' : ''}</small>}
                      </>}
                      {task.integrated && task.last_report_at && <small>Báo gần nhất: {fmt(task.last_report_at)}</small>}
                      {!tvMode && task.last_shift_reported_at && <small>Báo cáo ca gần nhất: +{num(task.last_shift_good_quantity)} đạt · {fmt(task.last_shift_reported_at)}</small>}
                      {!tvMode && can(user,'operations.assign') && m.operators.length > 0 && (task.integrated?Number(task.line_produced)<Number(task.line_target):done<total) && <ProgressRequestButton operationId={task.id} orderId={task.integrated?task.production_order_id:undefined} />}
                    </div>;
                  })}
                </div>
              ))}
            </div>
          </section>
          <section className="table-card dashboard-activity-card">
            <div className="card-title">
              <h2>{tvMode ? "Lệnh sản xuất · Ai? Làm gì? Bao nhiêu?" : "Hoạt động hiện tại"}</h2>
              {tvMode && activeTasks.length > tvTasksPerPage && <span className="tv-task-page">Trang {currentTvTaskPage + 1}/{tvTaskPages} · {activeTasks.length} việc</span>}
              {!tvMode && <NavLink to="/admin/live">Xem flow realtime</NavLink>}
            </div>
            {tvMode ? <div className="tv-task-columns"><span>Tên lệnh sản xuất</span><span>Người · Công đoạn · Máy</span><span>Tiến độ</span></div> : null}
            <div className={`dashboard-task-list ${tvMode ? "tv-task-list" : ""}`}>
              {(tvMode ? tvVisibleTasks : activeTasks.slice(0, 6)).map((task: any) => tvMode ? (
                <TvTaskRow key={task.id} task={task} selected={tvSelection === "tasks" && tvSelectedTaskId === task.id} onSelect={() => selectTvTask(task)} />
              ) : (
                <div
                  key={task.id}
                  className={
                    ["BLOCKED", "WAITING_SUPPORT"].includes(task.status)
                      ? "dashboard-task-row alert"
                      : "dashboard-task-row"
                  }
                >
                  <div className="dashboard-task-order">
                    <b>{task.order_code}</b>
                    <small>
                      {tvMode ? (task.customer_name || task.product_name) : <>{task.product_name}{task.customer_name ? ` · ${task.customer_name}` : ""}</>}
                    </small>
                  </div>
                  <div className="dashboard-task-stage">
                    <b>{task.tv_people?task.tv_people.map((person:any)=>person.operation_name).join(' · '):task.node_label || task.operation_name}</b>
                    <small>
                      {task.tv_people?task.tv_people.map((person:any)=>`${person.worker_name||'Chờ nhận'} · ${person.machine_code||'—'}`).join('; '):task.machine_code
                        ? `${task.machine_code} · ${task.assignee_name || task.active_operator_name || task.machine_name}`
                        : task.assignee_name || "Chưa phân công"}
                    </small>
                  </div>
                  <Status value={task.status} />
                </div>
              ))}
              {!activeTasks.length && (
                <div className="dashboard-empty">
                  <CheckCircle2 />
                  <b>Không có công việc đang mở</b>
                  <span>
                    Dashboard sẽ tự cập nhật khi lệnh mới được phát hành.
                  </span>
                </div>
              )}
            </div>
          </section>
        </div>
        <div className="dashboard-tv-footer">
          <span>
            <span className="live-dot" /> Dữ liệu realtime · dự phòng làm mới
            mỗi 15 giây
          </span>
          <b role="status">{tvReportMessage || "← → Chuyển xưởng · ↑ ↓ Chọn · OK máy: chọn đơn · OK đơn: yêu cầu báo cáo · ← Quay lại"}</b>
        </div>
      </div>
      <TvPlaybackLayer
        active={tvMode}
        scenario={playbackScenario}
        onExit={() => {}}
        duckAudio={tvAudioDuck}
      />
      <TvAudioPlayer active={tvMode} onDuck={setTvAudioDuck}/>
    </Page>
  );
}
function Kpi({
  label,
  value,
  tone = "",
  icon,
}: {
  label: string;
  value: any;
  tone?: string;
  icon?: ReactNode;
}) {
  return (
    <div className={`kpi ${tone}`}>
      {icon || <span className="kpi-mark" />}
      <div>
        <b>{value}</b>
        <span>{label}</span>
      </div>
    </div>
  );
}

function Machines() {
  const actionGate = useActionGate();
  const { user } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState(''), [departmentFilter, setDepartmentFilter] = useState('');
  const [view, setView] = useState<MachineViewMode>(() => {
    try { return machineViewMode(localStorage.getItem(`ph:machines:view:${user?.id}`)); } catch { return 'cards'; }
  });
  const [printTarget, setPrintTarget] = useState<{machine: any | null} | null>(null);
  const closePrint = useCallback(() => setPrintTarget(null), []);
  const filtered = filterMachines(items, search, departmentFilter);
  const changeView = (value: MachineViewMode) => {
    setView(value);
    try { localStorage.setItem(`ph:machines:view:${user?.id}`, value); } catch { /* Storage is optional. */ }
  };
  const canEdit = can(user, 'machines.update');
  const load = useCallback(
    () =>
      Promise.all([api<any[]>("/machines"), api<any[]>("/departments")]).then(
        ([m, d]) => {
          setItems(m);
          setDepartments(d);
          setListError('');
        },
      ).catch(e => setListError(e.message)),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useSocket(load);
  const openForm = () => {
    setError("");
    setShow(true);
  };
  const create = actionGate.guard(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (saving) return;
    const f = new FormData(e.currentTarget);
    setSaving(true);
    setError("");
    try {
      const machine = await post<any>("/machines", {
        code: f.get("code"),
        name: f.get("name"),
        departmentId: f.get("department") || null,
        workshopArea: f.get("area"),
        maxOperators: Number(f.get("max")),
      });
      setShow(false);
      await load();
      if (machine.restored)
        alert(`Đã khôi phục máy ${machine.code} và tạo mã QR mới.`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Không thể lưu máy. Vui lòng thử lại.",
      );
    } finally {
      setSaving(false);
    }
  });
  const move = actionGate.guard(async (id: string, departmentId: string) => {
    try {
      await api(`/machines/${id}`, { method: 'PATCH', body: JSON.stringify({ departmentId: departmentId || null }) });
      await load();
    } catch (e: any) { setListError(e.message); }
  });
  const remove = actionGate.guard(async (m: any) => {
    if (
      !confirm(
        `Xóa máy ${m.code} · ${m.name}?\n\nLịch sử sản xuất của máy vẫn được giữ lại.`,
      )
    )
      return;
    try {
      await api(`/machines/${m.id}`, { method: "DELETE" });
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Không thể xóa máy.");
    }
  });
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">THIẾT BỊ</span>
          <h1>Máy sản xuất</h1>
        </div>
        <div className="machine-page-actions">
          <button className="secondary" disabled={!items.length} onClick={() => setPrintTarget({machine:null})}><Printer /> In tất cả mã QR ({items.length})</button>
        {can(user, 'machines.create') && (
          <button className="primary" onClick={openForm}>
            + Thêm máy
          </button>
        )}
        </div>
      </div>
      {show && (
        <form className="modal-card" onSubmit={create}>
          <div className="card-title">
            <h2>Thêm máy sản xuất</h2>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setShow(false)}
              disabled={saving}
            >
              <X />
            </button>
          </div>
          <div className="form-grid">
            <label>
              Mã máy
              <input
                name="code"
                required
                placeholder="MD-04"
                autoCapitalize="characters"
              />
            </label>
            <label>
              Tên máy
              <input name="name" required placeholder="Máy Dập 04" />
            </label>
            <label>
              Phòng/xưởng
              <select name="department">
                <option value="">Chưa phân phòng</option>
                {departments.map((d) => (
                  <option value={d.id} key={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Khu vực
              <input name="area" />
            </label>
            <label>
              Số người tối đa
              <input
                name="max"
                type="number"
                min="1"
                max="20"
                defaultValue="1"
                required
              />
            </label>
          </div>
          {error && <div className="error">{error}</div>}
          <button className="primary" disabled={saving}>
            {saving ? "ĐANG LƯU..." : "TẠO MÁY & MÃ QR"}
          </button>
        </form>
      )}
      {listError && <p className="error" role="alert">{listError}</p>}
      <div className="machine-list-toolbar">
        <label className="machine-search">Tìm máy / người vận hành<input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Mã máy, tên máy, tên người..." /></label>
        <label>Lọc phòng/xưởng<select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)}><option value="">Tất cả phòng/xưởng</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select></label>
        <div className="machine-view-switch" role="group" aria-label="Chế độ hiển thị máy">
          <button className={view === 'cards' ? 'primary' : 'secondary'} aria-pressed={view === 'cards'} onClick={() => changeView('cards')}><LayoutGrid /> Thẻ</button>
          <button className={view === 'table' ? 'primary' : 'secondary'} aria-pressed={view === 'table'} onClick={() => changeView('table')}><Table2 /> Bảng gọn</button>
        </div>
      </div>
      <p className="machine-list-summary">Hiển thị {filtered.length}/{items.length} máy. “In tất cả” in toàn bộ máy bạn được quyền xem, không chỉ kết quả đang lọc.</p>
      {view === 'cards' ? <div className="machine-grid">
        {filtered.map((m) => (
          <article key={m.id}>
            <div className="machine-code">{m.code}</div>
            <Factory />
            <h2>{m.name}</h2>
            <p>{m.workshop_area || m.department_name}</p>
            <Status value={m.status} />
            {canEdit && (
              <label className="compact-field">
                Phòng/xưởng
                <select
                  value={m.department_id || ""}
                  onChange={(e) => move(m.id, e.target.value)}
                >
                  <option value="">Chưa phân phòng</option>
                  {departments.map((d) => (
                    <option value={d.id} key={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="operator-box">
              <span>Người vận hành</span>
              {m.operators.length ? (
                m.operators.map((o: any) => <b key={o.id}>{o.fullName}</b>)
              ) : (
                <em>Chưa có người</em>
              )}
            </div>
            <div className="machine-actions">
              <button className="secondary" aria-label={`In mã QR ${m.code}`} onClick={() => setPrintTarget({machine:m})}>
                In mã QR
              </button>
              {can(user, 'machines.delete') && (
                <button
                  className="secondary danger-text"
                  onClick={() => remove(m)}
                >
                  <Trash2 />
                  Xóa máy
                </button>
              )}
            </div>
          </article>
        ))}
      </div> : <div className="machine-table-wrap"><table className="machine-table">
        <thead><tr><th>Mã máy</th><th>Tên máy</th><th>Phòng/xưởng</th><th>Trạng thái</th><th>Người vận hành</th><th>Thao tác</th></tr></thead>
        <tbody>{filtered.map(m => <tr key={m.id}>
          <td><b className="machine-row-code">{m.code}</b></td>
          <td><b>{m.name}</b>{m.workshop_area && <small>{m.workshop_area}</small>}</td>
          <td>{canEdit ? <select aria-label={`Phòng/xưởng ${m.code}`} value={m.department_id || ''} onChange={e => void move(m.id,e.target.value)}><option value="">Chưa phân phòng</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select> : m.department_name || 'Chưa phân phòng'}</td>
          <td><Status value={m.status} /></td>
          <td>{m.operators.length ? m.operators.map((o: any) => <div key={o.id}><b>{o.fullName}</b></div>) : <span>Chưa có người</span>}</td>
          <td><div className="machine-actions"><button className="secondary" aria-label={`In mã QR ${m.code}`} onClick={() => setPrintTarget({machine:m})}>In mã QR</button>
            {can(user,'machines.delete') && <button className="secondary danger-text" aria-label={`Xóa máy ${m.code}`} onClick={() => void remove(m)}><Trash2 /> Xóa máy</button>}</div></td>
        </tr>)}</tbody>
      </table></div>}
      {!filtered.length && <p className="empty-list" role="status">{items.length ? 'Không tìm thấy máy phù hợp. Hãy đổi từ khóa hoặc xưởng.' : 'Chưa có máy sản xuất.'}</p>}
      {printTarget && <MachineQrPrintDialog machine={printTarget.machine} count={printTarget.machine ? 1 : items.length} onClose={closePrint} />}
    </Page>
  );
}

function Orders() {
  const actionGate = useActionGate();
  const {user} = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [dispatchers, setDispatchers] = useState<any[]>([]);
  const [customerName,setCustomerName]=useState("");
  const [productName,setProductName]=useState("");
  const [deadline,setDeadline]=useState("");
  const productNameRef=useRef<HTMLInputElement>(null);
  const [creating,setCreating]=useState(false);
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [cancelTarget, setCancelTarget] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const load = useCallback(
    () =>
      Promise.all([
        api<any[]>("/production-orders"),
        api<any[]>("/order-customers"),
        api<any[]>('/dispatchers'),
      ]).then(([orders, customerList, dispatcherList]) => {
        setItems(orders.filter(order=>order.model_version!==2));
        setCustomers(customerList);
        setDispatchers(dispatcherList);
      }),
    [user],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useSocket(load);
  const closeCreate = () => {
    setShow(false);
    setError("");
    setDeadline("");
  };
  const create = actionGate.guard(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if(creating)return;
    setCreating(true);
    setError("");
    const f = new FormData(e.currentTarget);
    try {
      const deadlineMatch = deadline.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/);
      if (!deadlineMatch) throw new Error("Deadline phải theo định dạng dd/mm/yyyy hh:mm.");
      const [, day, month, year, hour, minute] = deadlineMatch;
      const deadlineDate = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));
      if (Number.isNaN(deadlineDate.getTime()) || deadlineDate.getFullYear() !== Number(year) || deadlineDate.getMonth() !== Number(month) - 1 || deadlineDate.getDate() !== Number(day) || deadlineDate.getHours() !== Number(hour) || deadlineDate.getMinutes() !== Number(minute))
        throw new Error("Deadline không phải thời điểm hợp lệ.");
      const order = await post<any>("/production-orders", {
        productName,
        customerId:customers.find(customer=>customer.name===customerName)?.id || null,
        dispatcherId:f.get('dispatcher'),
        quantity: Number(f.get("quantity")),
        unit: f.get('unit'),
        priority: Number(f.get("priority")),
        plannedEndAt: deadlineDate.toISOString(),
        customerName,
        operations: [],
      });
      if (f.get("releaseNow")) {
        try {
          const sent = await post<any>(`/production-orders/${order.id}/release`,{});
          alert(`Đã tạo ${order.order_code} và gửi tới người điều phối. Người điều phối sẽ kiểm tra vật tư rồi chọn công đoạn, người và máy.`);
        } catch(error) {
          closeCreate();await load();
          alert(`Đã lưu lệnh ${order.order_code}, nhưng chưa phát hành: ${(error as Error).message}. Hãy phát hành lại từ danh sách, không tạo lại lệnh.`);
          return;
        }
      }
      closeCreate();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {setCreating(false);}
  });
  const release = actionGate.guard(async (id: string) => {
    if (
      !confirm(
        "Phát hành lệnh và gửi yêu cầu lập tuyến tới người điều phối?",
      )
    )
      return;
    try {
      const r = await post<any>(`/production-orders/${id}/release`);
      alert(`Đã phát hành. Gửi tới ${r.deliveryCount} người nhận.`);
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  });
  const openCancel = (order: any) => {
    setCancelTarget(order);
    setCancelReason("");
  };
  const cancel = actionGate.guard(async (e: FormEvent) => {
    e.preventDefault();
    if (!cancelTarget || cancelling) return;
    setCancelling(true);
    try {
      const result = await post<any>(
        `/production-orders/${cancelTarget.id}/cancel`,
        { reason: cancelReason },
      );
      setCancelTarget(null);
      setCancelReason("");
      await load();
      alert(
        `Đã hủy lệnh. Thông báo tới ${result.deliveryCount} người liên quan.`,
      );
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setCancelling(false);
    }
  });
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">ĐIỀU PHỐI</span>
          <h1>Lệnh sản xuất</h1>
        </div>
        {can(user, 'orders.create') && <NavLink className="primary" to="/production?tab=orders">+ Tạo lệnh mới</NavLink>}
      </div>
      {show && (
        <form className="modal-card" onSubmit={create}>
          <div className="card-title">
            <h2>Tạo lệnh sản xuất</h2>
            <button type="button" className="icon-btn" onClick={closeCreate}>
              <X />
            </button>
          </div>
          <div className="form-grid order-create-grid">
            <label>
              Mã lệnh
              <input readOnly value={`LSX-${customerName.normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[đĐ]/g,'D').toUpperCase().replace(/[^A-Z0-9]/g,'') || 'KHACHHANG'}-${new Intl.DateTimeFormat('en',{timeZone:'Asia/Ho_Chi_Minh',year:'numeric'}).format(new Date())}-…`} />
              <small>Số thứ tự riêng theo khách hàng/năm, tự cấp khi lưu.</small>
            </label>
            <label>
              Tên hàng
              <div className="order-product-input"><input required maxLength={300} ref={productNameRef} value={productName} onChange={e=>setProductName(e.target.value)} placeholder="Ví dụ: Seal nhôm Ø80" /><button type="button" className="secondary" onClick={()=>{
                const input=productNameRef.current,start=input?.selectionStart ?? productName.length,end=input?.selectionEnd ?? start;
                setProductName(productName.slice(0,start)+'Ø'+productName.slice(end));
                window.setTimeout(()=>{input?.focus();input?.setSelectionRange(start+1,start+1);},0);
              }}>Ø</button></div>
            </label>
            <label>
              Số lượng
              <input name="quantity" type="number" min="1" required />
            </label>
            <label>
              Khách hàng / đơn vị
              <input
                required list="order-customers" value={customerName} onChange={e=>setCustomerName(e.target.value)} maxLength={200}
                placeholder="Tên khách hàng nhận đơn"
              />
              <datalist id="order-customers">{customers.map(customer=><option key={customer.id} value={customer.name} />)}</datalist>
            </label>
            <label>
              Đơn vị tính
              <input name="unit" defaultValue="cái" required maxLength={30} list="order-units" /><datalist id="order-units">{['cái','bộ','kg','mét','thùng'].map(unit=><option key={unit} value={unit} />)}</datalist>
            </label>
            <label>
              Ưu tiên
              <select name="priority">
                <option value="3">Bình thường</option>
                <option value="1">Gấp</option>
              </select>
            </label>
            <label>
              Người điều phối trực tiếp
              <select name="dispatcher" required defaultValue={dispatchers.find(item=>item.id===user?.id)?.id || ""}>
                <option value="">Chọn người chịu trách nhiệm…</option>
                {dispatchers.map((dispatcher) => (
                  <option value={dispatcher.id} key={dispatcher.id}>
                    {dispatcher.full_name} · {dispatcher.department_name || "Toàn công ty"}
                  </option>
                ))}
              </select>
              <small>
                Người này kiểm tra vật tư tại xưởng, chọn Cắt/In/Ghép/Dập, rồi chỉ định người và máy. Có thể bàn giao người điều phối sau.
              </small>
            </label>
            <label>
              Deadline
              <DeadlinePicker value={deadline} onChange={setDeadline} />
            </label>
            <label className="check-row">
              <input name="releaseNow" type="checkbox" defaultChecked /> Phát
              hành ngay và gửi thông báo
            </label>
          </div>
          {error && <div className="error">{error}</div>}
          <button className="primary" disabled={creating}>{creating ? 'ĐANG LƯU…' : 'LƯU LỆNH'}</button>
        </form>
      )}
      {cancelTarget && (
        <form className="modal-card cancel-card" onSubmit={cancel}>
          <div className="card-title">
            <div>
              <span className="eyebrow">XÁC NHẬN HỦY</span>
              <h2>Hủy lệnh {cancelTarget.order_code}</h2>
            </div>
            <button
              type="button"
              className="icon-btn"
              onClick={() => setCancelTarget(null)}
              disabled={cancelling}
            >
              <X />
            </button>
          </div>
          <p>
            Lệnh và các công đoạn chưa hoàn thành sẽ chuyển sang trạng thái đã
            hủy. Lịch sử vẫn được giữ lại.
          </p>
          <label>
            Lý do hủy
            <textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              minLength={2}
              maxLength={500}
              required
              autoFocus
              placeholder="Ví dụ: Thay đổi kế hoạch sản xuất"
            />
          </label>
          <div className="cancel-actions">
            <button
              type="button"
              className="secondary"
              onClick={() => setCancelTarget(null)}
              disabled={cancelling}
            >
              Quay lại
            </button>
            <button className="cancel-button" disabled={cancelling}>
              <Trash2 />
              {cancelling ? "ĐANG HỦY..." : "XÁC NHẬN HỦY LỆNH"}
            </button>
          </div>
        </form>
      )}
      <div className="data-table">
        <div className="tr head orders">
          <span>Mã lệnh</span>
          <span>Sản phẩm</span>
          <span>Số lượng</span>
          <span>Trạng thái</span>
          <span>Hành động</span>
        </div>
        {items.map((o) => (
          <div className="tr orders" key={o.id}>
            <span>
              <b>{o.order_code}</b>
              <small>{fmt(o.created_at)}</small>
              {o.workflow_name && <small>Flow: {o.workflow_name}</small>}
              {o.dispatcher_name && <small>Điều phối: {o.dispatcher_name}</small>}
            </span>
            <span>
              {o.product_name}
              {o.customer_name && <small>Khách: {o.customer_name}</small>}
            </span>
            <span>
              {num(o.quantity)} {o.unit}
              {o.routing_mode==='DISPATCHER_PLANNED' && <small>Thành phẩm: {num(o.final_completed_quantity)} / {num(o.quantity)} {o.unit}</small>}
            </span>
            <span>
              <Status value={o.status} />
              {!['CANCELLED','COMPLETED','DRAFT'].includes(o.status) && (o.operations || []).filter((op:any)=>['QUEUED','INTERRUPTED'].includes(op.dispatchState)).map((op:any)=><small key={op.id}>{op.machineName}: {op.dispatchState==='QUEUED' ? 'Hàng chờ máy' : 'Tạm ngưng vì lệnh gấp'}</small>)}
            </span>
            <span className="order-actions">
              {can(user,'operations.assign') && o.routing_mode==='DISPATCHER_PLANNED' && !['CANCELLED','COMPLETED'].includes(o.status) && (
                <NavLink className="small-btn" to={`/admin/orders/${o.id}/dispatch`}>Điều phối</NavLink>
              )}
              {can(user, 'orders.release') && ["DRAFT", "PLANNED"].includes(o.status) && (
                <button className="small-btn" onClick={() => release(o.id)}>
                  Phát hành
                </button>
              )}
              {can(user, 'orders.cancel') && !["COMPLETED", "CANCELLED"].includes(o.status) && (
                <button
                  className="small-btn cancel-soft"
                  onClick={() => openCancel(o)}
                >
                  Hủy
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    </Page>
  );
}

function OrderDispatchPage() {
  const actionGate = useActionGate();
  const {id}=useParams();
  const [data,setData]=useState<any>(null);
  const [definitionId,setDefinitionId]=useState("");
  const [materialRequestId,setMaterialRequestId]=useState("");
  const [busy,setBusy]=useState(false);
  const [error,setError]=useState("");
  const load=useCallback(()=>id ? Promise.all([api<any>(`/production-orders/${id}/dispatch`),api<any[]>('/dispatchers')]).then(([result,dispatchers])=>{
    setData({...result,dispatchers});
    setDefinitionId(current=>current || result.definitions[0]?.id || "");
  }) : Promise.resolve(),[id]);
  useEffect(()=>{void load();},[load]);
  useSocket(load);
  if(!data)return <Page><div className="skeleton tall" /></Page>;
  if(data.order.model_version===2)return <Navigate to={`/production?tab=orders&order=${id}`} replace/>;
  const definition=data.definitions.find((item:any)=>item.id===definitionId);
  const machines=data.machines.filter((machine:any)=>!definition?.machine_ids?.length || definition.machine_ids.includes(machine.id));
  const openRequests=data.requests.filter((item:any)=>['OPEN','SUPPLYING'].includes(item.status));
  const routeStages=data.stages.filter((item:any)=>item.status!=='CANCELLED');
  const addStage=actionGate.guard(async(event:FormEvent<HTMLFormElement>)=>{
    event.preventDefault();if(!id||busy)return;
    setBusy(true);setError("");
    const form=new FormData(event.currentTarget);
    try{
      await post(`/production-orders/${id}/stages`,{
        definitionId,
        machineId:form.get('machineId') || null,
        assignedToUserId:form.get('assignedToUserId'),
        plannedQuantity:Number(form.get('plannedQuantity')),
        unit:form.get('unit'),
        afterOperationId:form.get('afterOperationId') || null,
        materialLotId:form.get('materialLotId') || null,
        materialQuantity:form.get('materialQuantity') ? Number(form.get('materialQuantity')) : null,
        outputMaterialId:form.get('outputMaterialId') || null,
        outputLotCode:form.get('outputLotCode') || null,
        outputQuantity:form.get('outputQuantity') ? Number(form.get('outputQuantity')) : null,
        materialRequestId:materialRequestId || null,
        notes:form.get('notes') || null,
      });
      setMaterialRequestId("");
      await load();
    }catch(caught){setError((caught as Error).message);}finally{setBusy(false);}
  });
  const fulfill=actionGate.guard(async(requestId:string,event:FormEvent<HTMLFormElement>)=>{
    event.preventDefault();setBusy(true);setError("");
    const form=new FormData(event.currentTarget);
    try{await post(`/material-requests/${requestId}/fulfill`,{sourceLotId:form.get('sourceLotId'),quantity:Number(form.get('quantity'))});await load();}
    catch(caught){setError((caught as Error).message);}finally{setBusy(false);}
  });
  const removeStage=actionGate.guard(async(stage:any)=>{
    if(!id||busy||!confirm(`Bỏ công đoạn “${stage.operation_name}” khỏi tuyến?`))return;
    setBusy(true);setError("");
    try{await api(`/production-orders/${id}/stages/${stage.id}`,{method:'DELETE'});await load();}
    catch(caught){setError((caught as Error).message);}finally{setBusy(false);}
  });
  const reassignDispatcher=actionGate.guard(async(event:FormEvent<HTMLFormElement>)=>{
    event.preventDefault();if(!id||busy)return;const form=new FormData(event.currentTarget);setBusy(true);setError("");
    try{await api(`/production-orders/${id}/dispatch`,{method:'PATCH',body:JSON.stringify({dispatcherId:form.get('dispatcherId'),note:'Bàn giao từ màn hình điều phối'})});await load();}
    catch(caught){setError((caught as Error).message);}finally{setBusy(false);}
  });
  const finalDone=routeStages.filter((item:any)=>item.is_final_output).reduce((sum:number,item:any)=>sum+Number(item.completed_quantity),0);
  return <Page>
    <NavLink className="back" to="/admin/orders">← Lệnh sản xuất</NavLink>
    <div className="page-title"><div><span className="eyebrow">LẬP TUYẾN THEO THỰC TẾ XƯỞNG</span><h1>{data.order.order_code}</h1><p>{data.order.product_name} · {data.order.customer_name}</p></div><div><Status value={data.order.status}/></div></div>
    <section className="dispatch-summary">
      <div><small>MỤC TIÊU THÀNH PHẨM</small><strong>{num(finalDone)} / {num(data.order.quantity)} {data.order.unit}</strong></div>
      <form onSubmit={reassignDispatcher}><small>NGƯỜI ĐIỀU PHỐI</small><select name="dispatcherId" defaultValue={data.order.dispatcher_id || ''} required>{data.dispatchers.map((dispatcher:any)=><option key={dispatcher.id} value={dispatcher.id}>{dispatcher.full_name} · {dispatcher.department_name || 'Toàn công ty'}</option>)}</select><button className="small-btn" disabled={busy}>Bàn giao</button></form>
      <div><small>NGUYÊN TẮC</small><strong>Chỉ bước “Đầu ra thành phẩm” tính % lệnh</strong></div>
    </section>
    {openRequests.length>0 && <section className="material-request-panel"><div className="card-title"><div><span className="eyebrow">CẦN XỬ LÝ</span><h2>Yêu cầu bổ sung vật tư</h2></div></div>{openRequests.map((request:any)=><article key={request.id}>
      <div><b>{request.requested_by_name} báo thiếu</b><p>{request.note}</p><small>{request.material_name || "Chưa xác định vật tư"} {request.requested_quantity ? `· đã cấp ${num(request.fulfilled_quantity)} / ${num(request.requested_quantity)} ${request.unit || ''}` : ''}</small><Status value={request.status}/></div>
      {request.status==='OPEN' && <div className="material-request-actions"><form onSubmit={event=>fulfill(request.id,event)}><select name="sourceLotId" required><option value="">Cấp từ lô có sẵn…</option>{data.lots.filter((lot:any)=>Number(lot.on_hand)-Number(lot.reserved)>0).map((lot:any)=><option key={lot.id} value={lot.id}>{lot.lot_code} · {lot.material_name} · còn {num(Number(lot.on_hand)-Number(lot.reserved))} {lot.unit}</option>)}</select><input name="quantity" type="number" min="0.001" step="0.001" placeholder="Số lượng cấp" defaultValue={request.requested_quantity ? Math.max(0,Number(request.requested_quantity)-Number(request.fulfilled_quantity)) : undefined} required/><button className="secondary" disabled={busy}>Cấp lô & cho chạy tiếp</button></form><button type="button" className="primary" onClick={()=>{setMaterialRequestId(request.id);window.scrollTo({top:document.body.scrollHeight,behavior:'smooth'});}}>Tạo công đoạn bổ sung</button></div>}
      {request.supply_operation_name && <small>Đang bổ sung qua: {request.supply_operation_name}</small>}
    </article>)}</section>}
    <section className="table-card route-stage-list"><div className="card-title"><h2>Tuyến thực tế của lệnh</h2><span>{data.stages.length} công đoạn</span></div>
      {data.stages.map((stage:any,index:number)=><article key={stage.id} className={stage.route_source==='SUPPLEMENT'?'supplement':''}><span className="route-index">{index+1}</span><div><b>{stage.operation_name}{stage.is_final_output?' · ĐẦU RA THÀNH PHẨM':''}</b><small>{stage.machine_code ? `${stage.machine_code} · ${stage.machine_name}` : 'Không cần máy'} · {stage.assignee_name || 'Chưa phân công'}</small>{stage.materials?.length>0 && <small>Vật tư: {stage.materials.map((item:any)=>`${item.lotCode} (${num(item.quantity)} ${item.unit})`).join(', ')}</small>}</div><div><strong>{num(stage.completed_quantity)} / {num(stage.planned_quantity)} {stage.output_unit}</strong><Status value={stage.status}/>{stage.dispatch_state==='QUEUED'&&<small>Hàng chờ máy</small>}{['PLANNED','ASSIGNED','ACCEPTED','CHECKING','READY'].includes(stage.status)&&<button type="button" className="small-btn cancel-soft" disabled={busy} onClick={()=>void removeStage(stage)}>Bỏ công đoạn</button>}</div></article>)}
      {!data.stages.length&&<div className="empty-list">Chưa có công đoạn. Người điều phối kiểm tra vật tư thực tế rồi thêm đúng các bước cần làm.</div>}
    </section>
    <form className="modal-card route-builder" onSubmit={addStage} key={`${data.stages.length}-${materialRequestId}`}>
      <div className="card-title"><div><span className="eyebrow">{materialRequestId?'BỔ SUNG VẬT TƯ CHO CÔNG ĐOẠN ĐANG CHỜ':'THÊM VÀO TUYẾN'}</span><h2>Giao một công đoạn</h2></div></div>
      <div className="form-grid order-create-grid">
        <label>Công đoạn<select value={definitionId} onChange={event=>setDefinitionId(event.target.value)} required>{data.definitions.map((item:any)=><option key={item.id} value={item.id}>{item.name} · {item.stage_kind==='FINAL_OUTPUT'?'Tính thành phẩm':item.stage_kind==='POST_PRODUCTION'?'Sau sản xuất':'Chuẩn bị'}</option>)}</select></label>
        <label>Người thực hiện<select name="assignedToUserId" required><option value="">Chọn công nhân…</option>{data.workers.map((worker:any)=><option key={worker.id} value={worker.id}>{worker.full_name} · {worker.current_machine ? `đang ở ${worker.current_machine}` : 'đang rảnh'}</option>)}</select></label>
        <label>Máy<select name="machineId" required={!!definition?.requires_machine}><option value="">{definition?.requires_machine?'Chọn máy…':'Không cần máy'}</option>{machines.map((machine:any)=><option key={machine.id} value={machine.id}>{machine.code} · {machine.name} · {machine.current_orders ? `bận ${machine.current_orders}` : machine.status}</option>)}</select><small>{!machines.length&&definition?.requires_machine?'Admin chưa cấu hình máy phù hợp cho công đoạn này.':''}</small></label>
        <label>Sản lượng của công đoạn<input name="plannedQuantity" type="number" min="0.001" step="0.001" required defaultValue={definition?.stage_kind==='FINAL_OUTPUT'?data.order.quantity:undefined}/></label>
        <label>Đơn vị<input name="unit" required defaultValue={definition?.stage_kind==='FINAL_OUTPUT'?data.order.unit:definition?.default_unit || ''}/></label>
        <label>Thực hiện sau<select name="afterOperationId" defaultValue={materialRequestId?'':routeStages.at(-1)?.id || ''}><option value="">Có thể bắt đầu ngay</option>{routeStages.map((stage:any)=><option key={stage.id} value={stage.id}>{stage.operation_name} · {stage.status}</option>)}</select></label>
        <label>Lấy vật tư từ lô<select name="materialLotId"><option value="">Chưa cấp lô / không cần</option>{data.lots.filter((lot:any)=>Number(lot.on_hand)-Number(lot.reserved)>0).map((lot:any)=><option key={lot.id} value={lot.id}>{lot.lot_code} · {lot.material_name} · khả dụng {num(Number(lot.on_hand)-Number(lot.reserved))} {lot.unit}</option>)}</select></label>
        <label>Số lượng vật tư dự kiến<input name="materialQuantity" type="number" min="0.001" step="0.001" placeholder="Để trống = sản lượng công đoạn"/></label>
        <label>Bán thành phẩm đầu ra<select name="outputMaterialId" required={!!materialRequestId}><option value="">Không tạo lô đầu ra</option>{data.materials.filter((material:any)=>material.material_kind==='SEMI_FINISHED').map((material:any)=><option key={material.id} value={material.id}>{material.code} · {material.name}</option>)}</select></label>
        <label>Mã lô đầu ra<input name="outputLotCode" placeholder="Để trống sẽ tự sinh"/></label>
        <label>Số lượng đầu ra dự kiến<input name="outputQuantity" type="number" min="0.001" step="0.001"/></label>
        <label>Ghi chú<textarea name="notes" rows={2} placeholder="Quy cách cắt, nội dung in, lưu ý ghép…"/></label>
      </div>
      {error&&<div className="error">{error}</div>}
      <button className="primary" disabled={busy||!definitionId}>{busy?'ĐANG GIAO…':materialRequestId?'GIAO VIỆC BỔ SUNG':'THÊM VÀ GIAO VIỆC'}</button>
    </form>
  </Page>;
}

function MaterialsPage(){
  const actionGate = useActionGate();
  const [materials,setMaterials]=useState<any[]>([]),[lots,setLots]=useState<any[]>([]),[definitions,setDefinitions]=useState<any[]>([]),[machines,setMachines]=useState<any[]>([]),[departments,setDepartments]=useState<any[]>([]);
  const [editing,setEditing]=useState<any>(null),[error,setError]=useState("");
  const {user}=useAuth();
  const canViewStages=can(user,'stages.view');
  const load=useCallback(()=>Promise.all([
    api<any[]>('/materials'),
    api<any[]>('/material-lots'),
    canViewStages?api<any[]>('/operation-definitions'):Promise.resolve([]),
    canViewStages?api<any[]>('/machines'):Promise.resolve([]),
    api<any[]>('/departments'),
  ]).then(([m,l,d,mc,dep])=>{setMaterials(m);setLots(l);setDefinitions(d);setMachines(mc);setDepartments(dep);}),[canViewStages]);
  useEffect(()=>{void load();},[load]);useSocket(load);
  const saveMaterial=actionGate.guard(async(event:FormEvent<HTMLFormElement>)=>{event.preventDefault();setError('');const form=event.currentTarget;const f=new FormData(form);try{await post('/materials',{code:f.get('code'),name:f.get('name'),materialKind:f.get('materialKind'),defaultUnit:f.get('defaultUnit'),specification:{description:f.get('specification')}});form.reset();await load();}catch(caught){setError((caught as Error).message);}});
  const receive=actionGate.guard(async(event:FormEvent<HTMLFormElement>)=>{event.preventDefault();setError('');const form=event.currentTarget;const f=new FormData(form);try{await post('/material-lots',{materialId:f.get('materialId'),lotCode:f.get('lotCode'),quantity:Number(f.get('quantity')),unit:f.get('unit'),departmentId:f.get('departmentId')||null,location:f.get('location')||null,note:f.get('note')||null});form.reset();await load();}catch(caught){setError((caught as Error).message);}});
  const adjustLot=actionGate.guard(async(lot:any)=>{const raw=prompt(`Điều chỉnh tồn lô ${lot.lot_code}\nNhập số dương để tăng, số âm để giảm:`);if(raw===null)return;const quantityDelta=Number(raw.replace(',','.'));if(!Number.isFinite(quantityDelta)||quantityDelta===0){alert('Số điều chỉnh không hợp lệ.');return;}const note=prompt('Lý do điều chỉnh:')?.trim();if(!note)return;setError('');try{await post(`/material-lots/${lot.id}/adjust`,{quantityDelta,note});await load();}catch(caught){setError((caught as Error).message);}});
  const saveDefinition=actionGate.guard(async(event:FormEvent<HTMLFormElement>)=>{event.preventDefault();setError('');const form=event.currentTarget;const f=new FormData(form);const payload={code:f.get('code'),name:f.get('name'),description:f.get('description')||null,defaultUnit:f.get('defaultUnit'),requiresMachine:f.get('requiresMachine')==='on',stageKind:f.get('stageKind'),machineIds:f.getAll('machineIds'),isActive:f.get('isActive')==='on'};try{if(editing)await api(`/operation-definitions/${editing.id}`,{method:'PATCH',body:JSON.stringify(payload)});else await post('/operation-definitions',payload);setEditing(null);form.reset();await load();}catch(caught){setError((caught as Error).message);}});
  return <Page><div className="page-title"><div><span className="eyebrow">NGUỒN CHO ĐIỀU PHỐI SẢN XUẤT</span><h1>Nguyên liệu & công đoạn</h1></div></div>
    <div className="materials-admin-grid">
      {can(user,'materials.manage')&&<form className="table-card" onSubmit={saveMaterial}><h2>Danh mục vật tư</h2><label>Mã<input name="code" required/></label><label>Tên vật tư / bán thành phẩm<input name="name" required/></label><label>Phân loại<select name="materialKind"><option value="RAW">Nguyên liệu thô</option><option value="SEMI_FINISHED">Bán thành phẩm</option></select></label><label>Đơn vị mặc định<input name="defaultUnit" required placeholder="cuộn, kg, mét…"/></label><label>Quy cách<textarea name="specification" rows={2} placeholder="Khổ, độ dày, loại giấy…"/></label><button className="primary">Lưu danh mục</button></form>}
      {can(user,'materials.adjust')&&<form className="table-card" onSubmit={receive}><h2>Nhập một lô</h2><label>Vật tư<select name="materialId" required><option value="">Chọn vật tư…</option>{materials.map(item=><option key={item.id} value={item.id}>{item.code} · {item.name}</option>)}</select></label><label>Mã lô<input name="lotCode" required/></label><label>Số lượng<input name="quantity" type="number" min="0.001" step="0.001" required/></label><label>Đơn vị<input name="unit" required/></label><label>Xưởng / kho<select name="departmentId"><option value="">Kho chung</option>{departments.map(item=><option key={item.id} value={item.id}>{item.name}</option>)}</select></label><label>Vị trí<input name="location" placeholder="Kệ A1…"/></label><label>Ghi chú<input name="note"/></label><button className="primary">Nhập lô</button></form>}
    </div>
    {can(user,'stages.manage')&&<form className="modal-card stage-definition-form" onSubmit={saveDefinition} key={editing?.id||'new'}><div className="card-title"><div><span className="eyebrow">ADMIN CẤU HÌNH, KHÔNG GHÉP CỨNG</span><h2>{editing?'Sửa công đoạn':'Tạo công đoạn'}</h2></div>{editing&&<button type="button" className="secondary" onClick={()=>setEditing(null)}>Tạo mới</button>}</div><div className="form-grid order-create-grid"><label>Mã<input name="code" required defaultValue={editing?.code}/></label><label>Tên công đoạn<input name="name" required defaultValue={editing?.name}/></label><label>Đơn vị mặc định<input name="defaultUnit" required defaultValue={editing?.default_unit}/></label><label>Loại bước<select name="stageKind" defaultValue={editing?.stage_kind||'PREPARATION'}><option value="PREPARATION">Chuẩn bị / bán thành phẩm</option><option value="FINAL_OUTPUT">Hình thành thành phẩm</option><option value="POST_PRODUCTION">Sau sản xuất</option></select></label><label className="check-row"><input name="requiresMachine" type="checkbox" defaultChecked={editing?.requires_machine ?? true}/> Bắt buộc chọn máy</label><label className="check-row"><input name="isActive" type="checkbox" defaultChecked={editing?.is_active ?? true}/> Cho phép người điều phối sử dụng</label><label>Mô tả<textarea name="description" rows={2} defaultValue={editing?.description}/></label></div><fieldset className="machine-capability"><legend>Máy có thể làm công đoạn này</legend>{machines.map(machine=><label key={machine.id}><input type="checkbox" name="machineIds" value={machine.id} defaultChecked={editing?.machine_ids?.includes(machine.id)}/>{machine.code} · {machine.name}</label>)}</fieldset><button className="primary">Lưu công đoạn</button></form>}
    {error&&<div className="error space">{error}</div>}
    <div className={`materials-list-grid ${canViewStages?'':'single'}`}><section className="table-card"><div className="card-title"><h2>Lô đang có</h2><span>{lots.length} lô</span></div>{lots.map(lot=><article key={lot.id}><div><b>{lot.lot_code}</b><small>{lot.material_name} · {lot.material_kind==='RAW'?'Nguyên liệu thô':'Bán thành phẩm'}</small><small>{lot.department_name||'Kho chung'}{lot.location?` · ${lot.location}`:''}</small></div><div className="material-lot-balance"><strong>{num(lot.on_hand)} {lot.unit}<small>Khả dụng {num(Number(lot.on_hand)-Number(lot.reserved))}</small></strong>{can(user,'materials.adjust')&&<button type="button" className="small-btn" onClick={()=>void adjustLot(lot)}>Điều chỉnh</button>}</div></article>)}{!lots.length&&<div className="empty-list">Chưa nhập lô vật tư.</div>}</section>{canViewStages&&<section className="table-card"><div className="card-title"><h2>Công đoạn</h2><span>{definitions.length} mục</span></div>{definitions.map(item=><article key={item.id}><div><b>{item.name}</b><small>{item.stage_kind==='FINAL_OUTPUT'?'Tính vào tiến độ thành phẩm':item.stage_kind==='POST_PRODUCTION'?'Sau sản xuất':'Công đoạn chuẩn bị'} · {item.default_unit}</small><small>{item.machine_ids?.length?`${item.machine_ids.length} máy phù hợp`:'Chưa giới hạn máy'}</small>{!item.is_active&&<small className="danger-text">Đã ngừng sử dụng</small>}</div>{can(user,'stages.manage')&&<button className="secondary" onClick={()=>{setEditing(item);window.scrollTo({top:0,behavior:'smooth'});}}>Sửa</button>}</article>)}</section>}</div>
  </Page>;
}

function UsersPage() {
  const actionGate = useActionGate();
  const { user } = useAuth();
  const [roleCatalog, setRoleCatalog] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const admin = can(user, 'users.update') || can(user, 'users.delete');
  const roleChoices = roleCatalog.length
    ? roleCatalog.filter(r => r.can_assign).map(r => r.code as string)
    : [];
  const roleNames = Object.fromEntries(roleCatalog.map(r => [r.code, r.name]));
  const load = () =>
    Promise.all([api<any[]>("/users"), api<any[]>("/departments"), api<any[]>("/roles")]).then(
      ([u, d, r]) => {
        setItems(u);
        setDepartments(d);
        setRoleCatalog(r);
      },
    );
  useEffect(() => {
    load();
  }, []);
  const close = () => {
    setShow(false);
    setEditing(null);
    setError("");
  };
  const create = actionGate.guard(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const f = new FormData(e.currentTarget);
    const roles = f.getAll("roles").map(String);
    if (!roles.length) {
      setError("Hãy chọn ít nhất một vai trò.");
      setSaving(false);
      return;
    }
    try {
      await post("/users", {
        username: f.get("username"),
        fullName: f.get("fullName"),
        pin: f.get("pin"),
        departmentId: f.get("department") || null,
        roles,
      });
      close();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  });
  const update = actionGate.guard(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    setError("");
    const f = new FormData(e.currentTarget);
    const pin = String(f.get("pin") || "");
    const ownAccount = editing.id === user!.id;
    const roles = ownAccount
      ? editing.roles
      : f.getAll("roles").map(String);
    if (!roles.length) {
      setError("Hãy chọn ít nhất một vai trò.");
      setSaving(false);
      return;
    }
    try {
      await api(`/users/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          username: f.get("username"),
          fullName: f.get("fullName"),
          departmentId: f.get("department") || null,
          roles,
          status: ownAccount ? editing.status : f.get("status"),
          ...(pin ? { pin } : {}),
        }),
      });
      close();
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  });
  const toggle = actionGate.guard(async (u: any) => {
    try {
      await api(`/users/${u.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          status: u.status === "ACTIVE" ? "DISABLED" : "ACTIVE",
        }),
      });
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  });
  const remove = actionGate.guard(async (u: any) => {
    if (
      !confirm(
        `Xóa tài khoản “${u.full_name}”?\n\nTài khoản sẽ không thể đăng nhập và bị ẩn khỏi danh sách. Lịch sử lệnh, ca máy và nhật ký vẫn được giữ lại.`,
      )
    )
      return;
    try {
      await api(`/users/${u.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  });
  const accountForm = editing ? (
    <form className="modal-card" onSubmit={update}>
      <div className="card-title">
        <h2>Sửa tài khoản</h2>
        <button
          type="button"
          className="icon-btn"
          onClick={close}
          disabled={saving}
        >
          <X />
        </button>
      </div>
      <div className="form-grid">
        <label>
          Họ tên
          <input name="fullName" defaultValue={editing.full_name} required />
        </label>
        <label>
          Tài khoản
          <input
            name="username"
            defaultValue={editing.username}
            required
            autoCapitalize="none"
          />
        </label>
        <label>
          PIN mới <small>Để trống nếu không đổi</small>
          <input name="pin" type="password" inputMode="numeric" minLength={4} />
        </label>
        <label>
          Phòng/xưởng
          <select name="department" defaultValue={editing.department_id || ""}>
            <option value="">Chưa phân phòng</option>
            {departments.map((d) => (
              <option value={d.id} key={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="role-picker">
          <legend>Vai trò <small>Có thể chọn nhiều vị trí</small></legend>
          <div>
            {roleChoices.map((role) => (
              <label key={role}>
                <input
                  type="checkbox"
                  name="roles"
                  value={role}
                  defaultChecked={editing.roles.includes(role)}
                  disabled={editing.id === user!.id}
                />
                <span>{roleNames[role] || roleLabels[role] || role}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <label>
          Trạng thái
          <select
            name="status"
            defaultValue={editing.status}
            disabled={editing.id === user!.id}
          >
            <option value="ACTIVE">Hoạt động</option>
            <option value="DISABLED">Đã khóa</option>
          </select>
        </label>
      </div>
      {editing.id === user!.id && (
        <p className="form-note">
          Để bảo toàn quyền truy cập, bạn không thể tự đổi vai trò hoặc tự khóa
          tài khoản.
        </p>
      )}
      {error && <div className="error">{error}</div>}
      <button className="primary" disabled={saving}>
        {saving ? "ĐANG LƯU..." : "LƯU THAY ĐỔI"}
      </button>
    </form>
  ) : show ? (
    <form className="modal-card" onSubmit={create}>
      <div className="card-title">
        <h2>Tạo tài khoản</h2>
        <button
          type="button"
          className="icon-btn"
          onClick={close}
          disabled={saving}
        >
          <X />
        </button>
      </div>
      <div className="form-grid">
        <label>
          Họ tên
          <input name="fullName" required />
        </label>
        <label>
          Tài khoản <small>Phải khác mọi tài khoản đăng nhập đang dùng</small>
          <input
            name="username"
            required
            autoCapitalize="none"
            placeholder="vi-du: huy.nguyen"
          />
        </label>
        <label>
          Mã PIN
          <input
            name="pin"
            type="password"
            inputMode="numeric"
            minLength={4}
            required
          />
        </label>
        <label>
          Phòng/xưởng
          <select name="department">
            <option value="">Chưa phân phòng</option>
            {departments.map((d) => (
              <option value={d.id} key={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <fieldset className="role-picker">
          <legend>Vai trò <small>Có thể chọn nhiều vị trí</small></legend>
          <div>
            {roleChoices.map((role) => (
              <label key={role}>
                <input
                  type="checkbox"
                  name="roles"
                  value={role}
                  defaultChecked={role === "WORKER"}
                />
                <span>{roleNames[role] || roleLabels[role] || role}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      {error && <div className="error">{error}</div>}
      <button className="primary" disabled={saving}>
        {saving ? "ĐANG TẠO..." : "TẠO TÀI KHOẢN"}
      </button>
    </form>
  ) : null;
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">NHÂN SỰ & PHÂN QUYỀN</span>
          <h1>Người dùng</h1>
        </div>
        {can(user, 'users.create') && (
          <button
            className="primary"
            onClick={() => {
              setError("");
              setShow(true);
            }}
          >
            + Thêm người dùng
          </button>
        )}
      </div>
      {accountForm}
      <div className="data-table users">
        <div className="tr head users">
          <span>Họ tên</span>
          <span>Tài khoản</span>
          <span>Phòng ban</span>
          <span>Vai trò</span>
          <span>Trạng thái</span>
          <span>Thao tác</span>
        </div>
        {items.map((u) => (
          <div className="tr users" key={u.id}>
            <span>
              <b>{u.full_name}</b>
            </span>
            <span>{u.username}</span>
            <span>{u.department_name || "—"}</span>
            <span>
              {u.roles
                .map((role: string) => roleLabels[role] || role)
                .join(", ")}
            </span>
            <span>
              <Status value={u.status} />
            </span>
            <span className="user-actions">
              {admin && (
                <>
                  <button
                    className="text-btn"
                    disabled={!can(user, 'users.update')}
                    onClick={() => {
                      setError("");
                      setEditing(u);
                    }}
                  >
                    Sửa
                  </button>
                  {u.id !== user!.id && can(user, 'users.update') && (
                    <button className="text-btn" onClick={() => toggle(u)}>
                      {u.status === "ACTIVE" ? "Khóa" : "Mở khóa"}
                    </button>
                  )}
                  {u.id !== user!.id && can(user, 'users.delete') && (
                    <button
                      className="text-btn danger-text"
                      onClick={() => remove(u)}
                    >
                      Xóa
                    </button>
                  )}
                </>
              )}
            </span>
          </div>
        ))}
      </div>
    </Page>
  );
}

function DepartmentsPage() {
  const actionGate = useActionGate();
  const {user} = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const load = () => api<any[]>("/departments").then(setItems);
  useEffect(() => {
    load();
  }, []);
  const create = actionGate.guard(async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await post("/departments", { name });
      setName("");
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  });
  const rename = actionGate.guard(async (item: any) => {
    const value = prompt("Tên phòng/xưởng mới", item.name)?.trim();
    if (!value || value === item.name) return;
    try {
      await api(`/departments/${item.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: value }),
      });
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  });
  const remove = actionGate.guard(async (item: any) => {
    if (!confirm(`Xóa “${item.name}”?`)) return;
    try {
      await api(`/departments/${item.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      alert((e as Error).message);
    }
  });
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">CẤU TRÚC NHÀ MÁY</span>
          <h1>Phòng/xưởng</h1>
        </div>
      </div>
      {can(user, 'departments.manage') && <form className="inline-create" onSubmit={create}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Tên phòng hoặc xưởng mới"
          required
          minLength={2}
        />
        <button className="primary" disabled={actionGate.busy} aria-busy={actionGate.busy}>
          <Plus />
          {actionGate.busy ? 'Đang xử lý…' : 'THÊM'}
        </button>
      </form>}
      {error && <div className="error space">{error}</div>}
      <div className="department-grid">
        {items.map((item) => (
          <article key={item.id}>
            <Building2 />
            <b>{item.name}</b>
            {can(user, 'departments.manage') && <div>
              <button className="text-btn" disabled={actionGate.busy} onClick={() => rename(item)}>
                Đổi tên
              </button>
              <button
                className="text-btn danger-text"
                disabled={actionGate.busy}
                onClick={() => remove(item)}
              >
                Xóa
              </button>
            </div>}
          </article>
        ))}
      </div>
    </Page>
  );
}

type FlowData = {
  label: string;
  subtitle: string;
  entityId: string;
  entityType: "USER" | "MACHINE";
  [key: string]: unknown;
};
type FlowCanvasNode = Node<FlowData>;
function LegacyWorkflowsPage() {
  const [flows, setFlows] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [machines, setMachines] = useState<any[]>([]);
  const [current, setCurrent] = useState<any>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [relation, setRelation] = useState<"ASSIGN" | "REPORT">("ASSIGN");
  const [userId, setUserId] = useState("");
  const [machineId, setMachineId] = useState("");
  const [nodes, setNodes, onNodesChange] = useNodesState<FlowCanvasNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const reload = () =>
    Promise.all([
      api<any[]>("/workflows"),
      api<any[]>("/users"),
      api<any[]>("/machines"),
    ]).then(([f, u, m]) => {
      setFlows(f);
      setUsers(u.filter((x) => x.status === "ACTIVE"));
      setMachines(m.filter((x) => x.is_active));
      return f;
    });
  useEffect(() => {
    reload().then((f) => {
      if (f[0]) selectFlow(f[0]);
    });
  }, []);
  const edgeView = (
    id: string,
    source: string,
    target: string,
    type: "ASSIGN" | "REPORT",
    label?: string,
  ): Edge => ({
    id,
    source,
    target,
    type: "smoothstep",
    label: label || (type === "ASSIGN" ? "Giao việc" : "Báo cáo"),
    animated: false,
    style: {
      stroke: type === "ASSIGN" ? "#0077b2" : "#d18a16",
      strokeWidth: 2,
    },
    labelStyle: {
      fontSize: 10,
      fontWeight: 800,
      fill: type === "ASSIGN" ? "#006a9e" : "#8b5c04",
    },
    labelBgStyle: { fill: "#fff", fillOpacity: 0.92 },
    labelBgPadding: [5, 3],
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: type === "ASSIGN" ? "#0077b2" : "#d18a16",
    },
    data: { relationType: type },
  });
  const selectFlow = (flow: any) => {
    setCurrent(flow);
    setName(flow.name);
    setDescription(flow.description || "");
    setActive(flow.is_active);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setNodes(
      flow.nodes.map((n: any) => ({
        id: n.id,
        position: { x: n.position_x, y: n.position_y },
        data: {
          label: n.label,
          subtitle: n.node_type === "USER" ? "Nhân sự" : "Máy",
          entityId: n.entity_id,
          entityType: n.node_type,
        },
        className: "flow-node " + n.node_type.toLowerCase(),
      })),
    );
    setEdges(
      flow.edges.map((e: any) =>
        edgeView(
          e.id,
          e.source_node_id,
          e.target_node_id,
          e.relation_type,
          e.label,
        ),
      ),
    );
  };
  const create = async () => {
    const value = prompt("Tên flow công việc")?.trim();
    if (!value) return;
    const flow = await post<any>("/workflows", { name: value });
    setFlows((x) => [flow, ...x]);
    selectFlow(flow);
  };
  const addEntity = (type: "USER" | "MACHINE", entityId: string) => {
    if (
      !entityId ||
      nodes.some(
        (n) => n.data.entityType === type && n.data.entityId === entityId,
      )
    )
      return;
    const entity = (type === "USER" ? users : machines).find(
      (x) => x.id === entityId,
    );
    if (!entity) return;
    const node = {
      id: crypto.randomUUID(),
      position: { x: type === "USER" ? 70 : 350, y: 80 + nodes.length * 115 },
      data: {
        label:
          type === "USER"
            ? entity.full_name
            : entity.code + " · " + entity.name,
        subtitle:
          type === "USER"
            ? entity.department_name || "Chưa phân phòng"
            : entity.department_name || "Máy sản xuất",
        entityId,
        entityType: type,
      },
      className: "flow-node " + type.toLowerCase(),
    };
    setNodes((ns) => [...ns, node]);
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
  };
  const connect = useCallback(
    (connection: Connection) => {
      if (
        !connection.source ||
        !connection.target ||
        connection.source === connection.target
      )
        return;
      setEdges((es) =>
        es.some(
          (edge) =>
            edge.source === connection.source &&
            edge.target === connection.target &&
            edge.data?.relationType === relation,
        )
          ? es
          : addEdge(
              edgeView(
                crypto.randomUUID(),
                connection.source,
                connection.target,
                relation,
              ),
              es,
            ),
      );
    },
    [relation, setEdges],
  );
  const tidy = () => {
    const incoming = new Set(edges.map((edge) => edge.target));
    const outgoing = new Set(edges.map((edge) => edge.source));
    const left = nodes.filter(
      (node) => outgoing.has(node.id) && !incoming.has(node.id),
    );
    const right = nodes.filter(
      (node) => incoming.has(node.id) && !outgoing.has(node.id),
    );
    const middle = nodes.filter(
      (node) =>
        !left.some((item) => item.id === node.id) &&
        !right.some((item) => item.id === node.id),
    );
    const placed = new Map<string, { x: number; y: number }>();
    (
      [
        [left, 70],
        [middle, 360],
        [right, 650],
      ] as [FlowCanvasNode[], number][]
    ).forEach(([group, x]) =>
      group.forEach((node, index) =>
        placed.set(node.id, { x, y: 70 + index * 135 }),
      ),
    );
    setNodes((currentNodes) =>
      currentNodes.map((node) => ({
        ...node,
        position: placed.get(node.id) || node.position,
      })),
    );
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  };
  const removeSelected = () => {
    if (selectedNodeId) {
      const node = nodes.find((item) => item.id === selectedNodeId);
      if (!node) return;
      const related = edges.filter(
        (edge) => edge.source === node.id || edge.target === node.id,
      ).length;
      if (
        !confirm(
          "Xóa “" +
            node.data.label +
            "” khỏi flow?" +
            (related
              ? " " + related + " liên kết liên quan cũng sẽ bị xóa."
              : ""),
        )
      )
        return;
      setNodes((items) => items.filter((item) => item.id !== node.id));
      setEdges((items) =>
        items.filter(
          (edge) => edge.source !== node.id && edge.target !== node.id,
        ),
      );
      setSelectedNodeId(null);
      return;
    }
    if (selectedEdgeId) {
      const edge = edges.find((item) => item.id === selectedEdgeId);
      if (!edge) return;
      if (!confirm("Xóa liên kết đã chọn?")) return;
      setEdges((items) => items.filter((item) => item.id !== edge.id));
      setSelectedEdgeId(null);
    }
  };
  const save = async () => {
    if (!current) return;
    setBusy(true);
    try {
      await api("/workflows/" + current.id, {
        method: "PUT",
        body: JSON.stringify({
          name,
          description,
          isActive: active,
          nodes: nodes.map((n) => ({
            id: n.id,
            nodeType: n.data.entityType,
            entityId: n.data.entityId,
            label: n.data.label,
            positionX: n.position.x,
            positionY: n.position.y,
          })),
          edges: edges.map((e) => ({
            id: e.id,
            sourceNodeId: e.source,
            targetNodeId: e.target,
            relationType: e.data?.relationType || "ASSIGN",
            label: String(e.label || ""),
          })),
        }),
      });
      const fresh = await reload();
      const selected = fresh.find((f: any) => f.id === current.id);
      if (selected) selectFlow(selected);
      alert("Đã lưu flow công việc.");
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!current || !confirm("Xóa flow “" + current.name + "”?")) return;
    await api("/workflows/" + current.id, { method: "DELETE" });
    const fresh = await reload();
    fresh[0]
      ? selectFlow(fresh[0])
      : (setCurrent(null), setNodes([]), setEdges([]));
  };
  const selectedNode = nodes.find((node) => node.id === selectedNodeId);
  const selectedEdge = edges.find((edge) => edge.id === selectedEdgeId);
  const edgeDescription = selectedEdge
    ? (nodes.find((node) => node.id === selectedEdge.source)?.data.label ||
        "Nguồn") +
      " → " +
      (nodes.find((node) => node.id === selectedEdge.target)?.data.label ||
        "Đích")
    : null;
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">ĐIỀU PHỐI TRỰC QUAN</span>
          <h1>Flow công việc</h1>
        </div>
        <button className="primary" onClick={create}>
          <Plus />
          Tạo flow
        </button>
      </div>
      <div className="flow-layout">
        <aside className="flow-panel">
          <label>
            Flow
            <select
              value={current?.id || ""}
              onChange={(e) => {
                const f = flows.find((x) => x.id === e.target.value);
                if (f) selectFlow(f);
              }}
            >
              <option value="">Chọn flow</option>
              {flows.map((f) => (
                <option value={f.id} key={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          {current && (
            <>
              <label>
                Tên flow
                <input value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label>
                Mô tả
                <textarea
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />{" "}
                Đang áp dụng
              </label>
              <hr />
              <span className="panel-label">THÊM NODE</span>
              <label>
                Nhân sự
                <select
                  value={userId}
                  onChange={(e) => {
                    addEntity("USER", e.target.value);
                    setUserId("");
                  }}
                >
                  <option value="">Chọn nhân sự…</option>
                  {users.map((u) => (
                    <option value={u.id} key={u.id}>
                      {u.full_name} · {u.department_name || "Chưa phân phòng"}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Máy
                <select
                  value={machineId}
                  onChange={(e) => {
                    addEntity("MACHINE", e.target.value);
                    setMachineId("");
                  }}
                >
                  <option value="">Chọn máy…</option>
                  {machines.map((m) => (
                    <option value={m.id} key={m.id}>
                      {m.code} · {m.name}
                    </option>
                  ))}
                </select>
              </label>
              <hr />
              <span className="panel-label">LIÊN KẾT</span>
              <div className="relation-switch">
                <button
                  type="button"
                  className={relation === "ASSIGN" ? "active" : ""}
                  onClick={() => setRelation("ASSIGN")}
                >
                  Giao việc
                </button>
                <button
                  type="button"
                  className={relation === "REPORT" ? "active report" : ""}
                  onClick={() => setRelation("REPORT")}
                >
                  Báo cáo
                </button>
              </div>
              <button
                type="button"
                className="secondary wide tidy-flow"
                onClick={tidy}
              >
                SẮP XẾP GỌN
              </button>
              {(selectedNode || selectedEdge) && (
                <section className="flow-selection">
                  <span className="panel-label">ĐANG CHỌN</span>
                  <b>
                    {selectedNode ? selectedNode.data.label : edgeDescription}
                  </b>
                  <small>
                    {selectedNode
                      ? "Đối tượng và toàn bộ liên kết liên quan"
                      : "Liên kết " + (selectedEdge?.label || "")}
                  </small>
                  <button
                    type="button"
                    className="secondary danger-text wide"
                    onClick={removeSelected}
                  >
                    <Trash2 />
                    {selectedNode ? "XÓA ĐỐI TƯỢNG" : "XÓA LIÊN KẾT"}
                  </button>
                </section>
              )}
              <p className="flow-help">
                Chạm vào đối tượng hoặc đường nối để chọn. Bấm “Sắp xếp gọn” để
                dàn nguồn bên trái, máy ở giữa và người nhận bên phải.
              </p>
              <button className="primary wide" onClick={save} disabled={busy}>
                <Save />
                {busy ? "Đang lưu…" : "LƯU FLOW"}
              </button>
              <button className="secondary wide danger-text" onClick={remove}>
                <Trash2 />
                Xóa flow
              </button>
            </>
          )}
        </aside>
        <section className="flow-canvas">
          {current ? (
            <ReactFlow
              nodes={nodes.map((node) => ({
                ...node,
                selected: node.id === selectedNodeId,
              }))}
              edges={edges.map((edge) => ({
                ...edge,
                selected: edge.id === selectedEdgeId,
              }))}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={connect}
              onNodeClick={(_event, node) => {
                setSelectedNodeId(node.id);
                setSelectedEdgeId(null);
              }}
              onEdgeClick={(_event, edge) => {
                setSelectedEdgeId(edge.id);
                setSelectedNodeId(null);
              }}
              onPaneClick={() => {
                setSelectedNodeId(null);
                setSelectedEdgeId(null);
              }}
              fitView
              deleteKeyCode={null}
            >
              <Background gap={22} />
              <MiniMap pannable zoomable />
              <Controls />
            </ReactFlow>
          ) : (
            <div className="empty-list">
              Tạo flow đầu tiên để bắt đầu nối giao việc và báo cáo.
            </div>
          )}
        </section>
      </div>
    </Page>
  );
}

function ProcessWorkflowsPage() {
  const [flows, setFlows] = useState<any[]>([]),
    [current, setCurrent] = useState<any>(null),
    [name, setName] = useState(""),
    [multiRolePolicy, setMultiRolePolicy] = useState<
      "ALLOW" | "WARN" | "REQUIRE_DIFFERENT"
    >("WARN"),
    [nodes, setNodes, onNodesChange] = useNodesState<any>([]),
    [edges, setEdges, onEdgesChange] = useEdgesState<any>([]),
    [kind, setKind] = useState<"TRANSITION" | "PASS" | "FAIL" | "REWORK">(
      "TRANSITION",
    ),
    [selected, setSelected] = useState<string | null>(null);
  const labels: Record<string, string> = {
    START: "Bắt đầu",
    DISPATCH: "Điều độ / Phân công",
    PRODUCTION: "Sản xuất",
    QC: "QC",
    PACKING: "Đóng gói",
    WAREHOUSE: "Nhập kho",
    SHIPPING: "Xuất / giao hàng",
    END: "Hoàn tất",
    CUSTOM: "Bước tùy chỉnh",
  };
  const load = async () => {
    const rows = await api<any[]>("/workflows");
    setFlows(rows);
    return rows;
  };
  const choose = (flow: any) => {
    setCurrent(flow);
    setName(flow.name);
    setMultiRolePolicy(flow.multi_role_policy || "WARN");
    setSelected(null);
    setNodes(
      (flow.nodes || [])
        .filter((n: any) => labels[n.node_type])
        .map((n: any) => ({
          id: n.id,
          position: { x: n.position_x, y: n.position_y },
          data: { ...n, label: n.label || labels[n.node_type] },
          className: "flow-node process-node",
        })),
    );
    setEdges(
      (flow.edges || []).map((e: any) => ({
        id: e.id,
        source: e.source_node_id,
        target: e.target_node_id,
        label: e.label || e.relation_type,
        data: { relationType: e.relation_type },
        type: "smoothstep",
        markerEnd: { type: MarkerType.ArrowClosed },
      })),
    );
  };
  useEffect(() => {
    load().then((rows) => rows[0] && choose(rows[0]));
  }, []);
  const create = async () => {
    const value = prompt("Tên flow công việc")?.trim();
    if (!value) return;
    const flow = await post<any>("/workflows", { name: value, template: true });
    const rows = await load();
    choose(rows.find((row) => row.id === flow.id) || flow);
  };
  const add = (type: string) => {
    const id = crypto.randomUUID();
    setNodes((rows) => [
      ...rows,
      {
        id,
        position: {
          x: 120 + (rows.length % 3) * 240,
          y: 100 + Math.floor(rows.length / 3) * 150,
        },
        data: {
          node_type: type,
          label: labels[type],
          assignment_mode: type === "PRODUCTION" ? "DIRECT_MACHINE" : "ROLE",
          checklist_json:
            type === "PRODUCTION"
              ? [
                  "Nguyên liệu đầy đủ",
                  "Máy sẵn sàng",
                  "Đã hiểu yêu cầu sản xuất",
                ]
              : [],
        },
        className: "flow-node process-node",
      },
    ]);
    setSelected(id);
  };
  const connect = useCallback(
    (c: Connection) => {
      if (!c.source || !c.target || c.source === c.target) return;
      setEdges((rows) =>
        addEdge(
          {
            id: crypto.randomUUID(),
            source: c.source,
            target: c.target,
            label: kind === "TRANSITION" ? "Tiếp theo" : kind,
            data: { relationType: kind },
            type: "smoothstep",
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          rows,
        ),
      );
    },
    [kind, setEdges],
  );
  const node = nodes.find((n: any) => n.id === selected);
  const update = (value: any) =>
    setNodes((rows) =>
      rows.map((n: any) =>
        n.id === selected ? { ...n, data: { ...n.data, ...value } } : n,
      ),
    );
  const remove = () => {
    if (!selected) return;
    if (node) {
      setNodes((rows) => rows.filter((n: any) => n.id !== selected));
      setEdges((rows) =>
        rows.filter((e: any) => e.source !== selected && e.target !== selected),
      );
    } else setEdges((rows) => rows.filter((e: any) => e.id !== selected));
    setSelected(null);
  };
  const save = async () => {
    if (!current) return;
    try {
      await api(`/workflows/${current.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name,
          description: current.description || "",
          isActive: true,
          multiRolePolicy,
          nodes: nodes.map((n: any) => ({
            id: n.id,
            nodeType: n.data.node_type,
            label: n.data.label,
            positionX: n.position.x,
            positionY: n.position.y,
            assignmentMode: n.data.assignment_mode || "ROLE",
            fixedMachineId: null,
            assigneeRole: null,
            checklist: n.data.checklist_json || [],
          })),
          edges: edges.map((e: any) => ({
            id: e.id,
            sourceNodeId: e.source,
            targetNodeId: e.target,
            relationType: e.data?.relationType || "TRANSITION",
            label: String(e.label || ""),
          })),
          subscriptions: current.subscriptions || [],
        }),
      });
      const rows = await load();
      choose(rows.find((row) => row.id === current.id));
      alert("Đã lưu flow quy trình.");
    } catch (e) {
      alert((e as Error).message);
    }
  };
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">QUY TRÌNH NGHIỆP VỤ</span>
          <h1>Flow công việc</h1>
        </div>
        <button className="primary" onClick={create}>
          <Plus />
          Tạo flow mẫu
        </button>
      </div>
      <div className="flow-layout">
        <aside className="flow-panel">
          <label>
            Flow
            <select
              value={current?.id || ""}
              onChange={(e) => {
                const f = flows.find((x) => x.id === e.target.value);
                if (f) choose(f);
              }}
            >
              <option value="">Chọn flow</option>
              {flows.map((f) => (
                <option value={f.id} key={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </label>
          {current && (
            <>
              <label>
                Tên flow
                <input value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <span className="panel-label">THÊM BƯỚC NGHIỆP VỤ</span>
              <select
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) add(e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="">Chọn bước…</option>
                {Object.entries(labels).map(([value, label]) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="panel-label">CHUYỂN BƯỚC</span>
              <div className="relation-switch">
                {(["TRANSITION", "PASS", "FAIL", "REWORK"] as const).map(
                  (x) => (
                    <button
                      type="button"
                      className={kind === x ? "active" : ""}
                      onClick={() => setKind(x)}
                      key={x}
                    >
                      {x === "TRANSITION" ? "Tiếp theo" : x}
                    </button>
                  ),
                )}
              </div>
              {node && (
                <section className="flow-selection">
                  <span className="panel-label">CẤU HÌNH BƯỚC</span>
                  <label>
                    Tên bước
                    <input
                      value={node.data.label}
                      onChange={(e) => update({ label: e.target.value })}
                    />
                  </label>
                  <label>
                    Phân công
                    <select
                      value={node.data.assignment_mode || "ROLE"}
                      onChange={(e) =>
                        update({ assignment_mode: e.target.value })
                      }
                    >
                      <option value="DIRECT_MACHINE">Điều độ chọn máy</option>
                      <option value="TEAM_LEADER">Tổ trưởng chọn máy</option>
                      <option value="FIXED_MACHINE">Máy cố định</option>
                      <option value="ROLE">Theo vai trò</option>
                    </select>
                  </label>
                  <label>
                    Checklist <small>Mỗi dòng một mục</small>
                    <textarea
                      rows={4}
                      value={(node.data.checklist_json || []).join("\n")}
                      onChange={(e) =>
                        update({
                          checklist_json: e.target.value
                            .split("\n")
                            .map((x) => x.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </label>
                </section>
              )}
              {selected && (
                <button className="secondary danger-text wide" onClick={remove}>
                  <Trash2 />
                  Xóa bước / liên kết
                </button>
              )}
              <p className="flow-help">
                Canvas chỉ có bước nghiệp vụ. Máy/người được chọn khi giao Task;
                thông báo là Event Subscription.
              </p>
              <button className="primary wide" onClick={save}>
                <Save />
                LƯU FLOW
              </button>
            </>
          )}
        </aside>
        <section className="flow-canvas">
          {current ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={connect}
              onNodeClick={(_e, n) => setSelected(n.id)}
              onEdgeClick={(_e, e) => setSelected(e.id)}
              onPaneClick={() => setSelected(null)}
              fitView
            >
              <Background gap={22} />
              <MiniMap />
              <Controls />
            </ReactFlow>
          ) : (
            <div className="empty-list">
              Tạo flow mẫu để có Sản xuất → QC → Đóng gói.
            </div>
          )}
        </section>
      </div>
    </Page>
  );
}

function Operation() {
  const actionGate = useActionGate();
  const {id}=useParams();
  const [op, setOp] = useState<any>(null),
    [error, setError] = useState(""),
    [check, setCheck] = useState(false),
    [support, setSupport] = useState(false),
    [supportReason,setSupportReason]=useState("MATERIAL_SHORTAGE"),
    [supportMaterialId,setSupportMaterialId]=useState(""),
    [materials,setMaterials]=useState<any[]>([]),
    [good, setGood] = useState(""),
    [defect, setDefect] = useState("0");
  const navigate = useNavigate();
  const load = useCallback(
    () =>
      api(`/operations/my-current?id=${id}`)
        .then((result:any)=>{setOp(result);setError(result ? "" : "Công việc này không còn được giao cho bạn hoặc đã kết thúc.");})
        .catch((e) => setError(e.message)),
    [id],
  );
  useEffect(() => {
    load();
  }, [load]);
  useEffect(()=>{
    api<any[]>("/materials").then(setMaterials).catch(()=>setMaterials([]));
  },[]);
  const action = actionGate.guard(async (path: string, payload?: any) => {
    try {
      await post(`/operations/${op.id}/${path}`, payload);
      await load();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    }
  });
  if (!op)
    return (
      <Page>
        {error ? (
          <div className="error">{error}</div>
        ) : (
          <div className="skeleton tall" />
        )}
      </Page>
    );
  if(op.order_line_id)return <Navigate to={`/production?tab=jobs&job=${op.id}`} replace/>;
  const checklist = op.checklist_json?.length
    ? op.checklist_json
    : op.workflow_node_type === "PRODUCTION"
      ? ["Nguyên liệu đầy đủ", "Máy sẵn sàng", "Đã hiểu yêu cầu sản xuất"]
      : ["Đã đọc và hiểu yêu cầu của công đoạn"];
  const operationUnit=op.output_unit || op.unit || "";
  return (
    <Page narrow>
      <button className="back" onClick={() => navigate("/worker")}>
        ← Máy của tôi
      </button>
      <section className="operation-card">
        <div className="section-head">
          <span>WORK TASK</span>
          <Status value={op.status} />
        </div>
        <h1>{op.order_code}</h1>
        <h2>{op.product_name}</h2>
        <dl>
          <dt>Công đoạn</dt>
          <dd>{op.operation_name}</dd>
          <dt>Máy</dt>
          <dd>{op.machine_name || "Không yêu cầu máy"}</dd>
          <dt>Số lượng</dt>
          <dd>
            {num(op.planned_quantity)} {operationUnit}
          </dd>
          <dt>Đã đạt lũy kế</dt>
          <dd>
            {num(op.completed_quantity)} {operationUnit}
          </dd>
          <dt>Lỗi lũy kế</dt>
          <dd>
            {num(op.defect_quantity)} {operationUnit}
          </dd>
          <dt>Còn lại dự kiến</dt>
          <dd>
            {num(
              Math.max(
                0,
                Number(op.planned_quantity) - Number(op.completed_quantity),
              ),
            )}{" "}
            {operationUnit}
          </dd>
          <dt>Deadline</dt>
          <dd>{fmt(op.planned_end_at)}</dd>
          <dt>Ghi chú</dt>
          <dd>{op.order_notes || "Không có"}</dd>
          {op.customer_name && (
            <>
              <dt>Khách hàng</dt>
              <dd>{op.customer_name}</dd>
            </>
          )}
          {op.delivery_address && (
            <>
              <dt>Giao tới</dt>
              <dd>{op.delivery_address}</dd>
            </>
          )}
        </dl>
        {op.last_shift_reported_at && (
          <div className="callout shift-handover-summary operation-handover">
            <b>Bàn giao từ ca của {op.last_shift_worker_name}</b>
            <span>
              Sản lượng ca trước: đạt {num(op.last_shift_good_quantity)}, lỗi{" "}
              {num(op.last_shift_defect_quantity)} ·{" "}
              {fmt(op.last_shift_reported_at)}
            </span>
            {op.last_shift_handover_note && (
              <span>Ghi chú: {op.last_shift_handover_note}</span>
            )}
          </div>
        )}
        {op.status === "ASSIGNED" && (
          <button className="primary wide big" onClick={() => action("accept")}>
            <CheckCircle2 />
            TIẾP NHẬN CÔNG VIỆC
          </button>
        )}
        {op.status === "ACCEPTED" && (
          <>
            <button
              className="primary wide big"
              onClick={() => setCheck(!check)}
            >
              <CheckCircle2 />
              KIỂM TRA ĐIỀU KIỆN
            </button>
            {check && (
              <div className="complete-form">
                {checklist.map((item: string) => (
                  <label key={item} className="check-row">
                    <input type="checkbox" defaultChecked /> {item}
                  </label>
                ))}
                <button
                  className="primary wide"
                  onClick={() =>
                    action("check", {
                      checks: checklist.map((label: string) => ({
                        label,
                        checked: true,
                      })),
                    })
                  }
                >
                  XÁC NHẬN ĐỦ ĐIỀU KIỆN
                </button>
              </div>
            )}
          </>
        )}
        {op.status === "READY" && (
          <div className="work-actions">
            <button className="secondary" onClick={() => setSupport(true)}>
              <Wrench />
              Yêu cầu hỗ trợ
            </button>
            <button className="primary" onClick={() => action("start")}>
              <Play />
              BẮT ĐẦU CÔNG ĐOẠN
            </button>
          </div>
        )}
        {op.status === "IN_PROGRESS" && op.workflow_node_type === "QC" && (
          <div className="qc-actions">
            <button
              className="secondary danger-text"
              onClick={async () => {
                const notes = prompt("Mô tả lỗi QC / lý do cần làm lại") || "";
                if (await action("qc-result", { result: "FAIL", notes }))
                  navigate("/worker");
              }}
            >
              QC FAIL · LÀM LẠI
            </button>
            <button
              className="primary"
              onClick={async () => {
                if (await action("qc-result", { result: "PASS" }))
                  navigate("/worker");
              }}
            >
              <CheckCircle2 /> QC PASS
            </button>
          </div>
        )}
        {op.status === "IN_PROGRESS" && op.workflow_node_type !== "QC" && (
          <div className="work-actions">
            <button className="secondary" onClick={() => action("pause")}>
              <Square />
              Tạm dừng
            </button>
            <button className="secondary" onClick={() => setSupport(true)}>
              <Wrench />
              Hỗ trợ
            </button>
            <button
              className="primary"
              onClick={() => {
                setGood(
                  String(
                    Math.max(
                      0,
                      Number(op.planned_quantity) -
                        Number(op.completed_quantity),
                    ),
                  ),
                );
                setDefect("0");
                setCheck(true);
              }}
            >
              <CheckCircle2 />
              Hoàn thành
            </button>
          </div>
        )}
        {op.status === "PAUSED" && (
          <button className="primary wide" onClick={() => action("start")}>
            <Play />
            TIẾP TỤC SẢN XUẤT
          </button>
        )}
        {["BLOCKED", "WAITING_SUPPORT"].includes(op.status) && (
          <div className="callout warning">
            Đã yêu cầu hỗ trợ. Chờ tổ trưởng hoặc quản lý xử lý và tiếp tục công
            việc.
          </div>
        )}
        {support && (
          <form
            className="complete-form"
            onSubmit={(e) => {
              e.preventDefault();
              const f = new FormData(e.currentTarget);
              action("support", {
                reason: f.get("reason"),
                note: f.get("note"),
                materialId: f.get("materialId") || null,
                requestedQuantity: f.get("requestedQuantity") ? Number(f.get("requestedQuantity")) : null,
                unit: f.get("unit") || null,
              });
              setSupport(false);
            }}
          >
            <h3>Yêu cầu hỗ trợ</h3>
            <label>
              Lý do
              <select name="reason" value={supportReason} onChange={(event)=>setSupportReason(event.target.value)}>
                <option value="MATERIAL_SHORTAGE">Thiếu nguyên liệu</option>
                <option value="MACHINE_PROBLEM">Máy có vấn đề</option>
                <option value="TOOLING_MISSING">Thiếu khuôn / dụng cụ</option>
                <option value="UNCLEAR_REQUIREMENT">Chưa rõ yêu cầu</option>
                <option value="OTHER">Khác</option>
              </select>
            </label>
            {supportReason==="MATERIAL_SHORTAGE"&&<div className="support-material-grid">
              <label>
                Loại nguyên liệu / bán thành phẩm
                <select name="materialId" value={supportMaterialId} onChange={event=>setSupportMaterialId(event.target.value)}>
                  <option value="">Chưa xác định rõ</option>
                  {materials.map(material=><option key={material.id} value={material.id}>{material.code} · {material.name}</option>)}
                </select>
              </label>
              <label>
                Số lượng cần bổ sung
                <input name="requestedQuantity" type="number" min="0.001" step="0.001" inputMode="decimal" />
              </label>
              <label>
                Đơn vị
                <input name="unit" key={supportMaterialId || operationUnit} defaultValue={materials.find(material=>material.id===supportMaterialId)?.default_unit || operationUnit}/>
              </label>
            </div>}
            <label>
              Ghi chú
              <textarea name="note" required minLength={2} />
            </label>
            <button className="primary wide">GỬI YÊU CẦU</button>
          </form>
        )}
        {check &&
          op.status === "IN_PROGRESS" &&
          op.workflow_node_type !== "QC" && (
            <form
              className="complete-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const completed = await action("complete", {
                  completedQuantity: Number(good),
                  defectQuantity: Number(defect),
                  quantityMode: 'INCREMENT',
                });
                if (completed) navigate("/worker");
              }}
            >
              <h3>Báo cáo phần còn lại & hoàn thành</h3>
              <p className="form-help">
                Lũy kế hiện tại: đạt {num(op.completed_quantity)}, lỗi{" "}
                {num(op.defect_quantity)}. Chỉ nhập phần phát sinh CHƯA báo cáo,
                không nhập lại số đã gửi trong báo cáo tiến độ giữa ca.
              </p>
              <label>
                Sản lượng đạt bổ sung
                <input
                  type="number"
                  required
                  min="0"
                  step="any"
                  inputMode="decimal"
                  value={good}
                  onChange={(e) => setGood(e.target.value)}
                />
              </label>
              <label>
                Sản lượng lỗi bổ sung
                <input
                  type="number"
                  required
                  min="0"
                  step="any"
                  inputMode="decimal"
                  value={defect}
                  onChange={(e) => setDefect(e.target.value)}
                />
              </label>
              <button className="primary wide">XÁC NHẬN HOÀN THÀNH</button>
            </form>
          )}
        {error && <div className="error space">{error}</div>}
      </section>
    </Page>
  );
}

function SmartFlowPage() {
  const [flows, setFlows] = useState<any[]>([]),
    [current, setCurrent] = useState<any>(null),
    [name, setName] = useState(""),
    [kind, setKind] = useState<"TRANSITION" | "PASS" | "FAIL" | "REWORK">(
      "TRANSITION",
    ),
    [expanded, setExpanded] = useState(false),
    [selected, setSelected] = useState<string | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);
  const labels: Record<string, string> = {
    START: "Bắt đầu",
    DISPATCH: "Điều động / phân công",
    PRODUCTION: "Sản xuất",
    QC: "QC",
    PACKING: "Đóng gói",
    WAREHOUSE: "Nhập kho",
    SHIPPING: "Xuất / giao hàng",
    END: "Hoàn tất",
    CUSTOM: "Bước tùy chỉnh",
  };
  const arrange = (rawNodes: any[], rawEdges: any[]) => {
    const ids = new Set(rawNodes.map((node) => node.id));
    const incoming = new Map<string, number>(
      rawNodes.map((node) => [node.id, 0]),
    );
    const next = new Map<string, string[]>();
    rawEdges.forEach((edge) => {
      if (ids.has(edge.source) && ids.has(edge.target)) {
        incoming.set(edge.target, (incoming.get(edge.target) || 0) + 1);
        next.set(edge.source, [...(next.get(edge.source) || []), edge.target]);
      }
    });
    const level = new Map<string, number>();
    const queue = rawNodes.filter((node) => (incoming.get(node.id) || 0) === 0);
    queue.forEach((node) => level.set(node.id, 0));
    for (let cursor = 0; cursor < queue.length; cursor++) {
      const node = queue[cursor];
      for (const target of next.get(node.id) || []) {
        const currentLevel = Math.max(
          level.get(target) || 0,
          (level.get(node.id) || 0) + 1,
        );
        level.set(target, currentLevel);
        incoming.set(target, (incoming.get(target) || 1) - 1);
        if (incoming.get(target) === 0)
          queue.push(rawNodes.find((item) => item.id === target)!);
      }
    }
    rawNodes.forEach((node) => {
      if (!level.has(node.id)) level.set(node.id, level.size);
    });
    const groups = new Map<number, any[]>();
    rawNodes.forEach((node) =>
      groups.set(level.get(node.id)!, [
        ...(groups.get(level.get(node.id)!) || []),
        node,
      ]),
    );
    return rawNodes.map((node) => {
      const group = groups.get(level.get(node.id)!) || [];
      const row = group.indexOf(node);
      return {
        ...node,
        position: {
          x: 80 + (level.get(node.id) || 0) * 280,
          y: 120 + row * 190,
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      };
    });
  };
  const edgeLabels: Record<string, string> = {
    TRANSITION: "Tiếp theo",
    PASS: "PASS",
    FAIL: "FAIL",
    REWORK: "Làm lại",
  };
  const edgeView = (edge: any) => ({
    id: edge.id,
    source: edge.source_node_id || edge.source,
    target: edge.target_node_id || edge.target,
    label:
      edge.label ||
      edgeLabels[edge.relation_type || edge.data?.relationType] ||
      "Tiếp theo",
    data: { relationType: edge.relation_type || edge.data?.relationType },
    type: "smoothstep",
    style: {
      stroke:
        edge.relation_type === "FAIL"
          ? "#d04532"
          : edge.relation_type === "PASS"
            ? "#15866f"
            : "#087db7",
      strokeWidth: 2,
    },
    markerEnd: { type: MarkerType.ArrowClosed },
  });
  const load = async () => {
    const rows = await api<any[]>("/workflows");
    setFlows(rows);
    return rows;
  };
  const choose = (flow: any) => {
    const graphEdges = (flow.edges || []).map(edgeView);
    const graphNodes = (flow.nodes || [])
      .filter((node: any) => labels[node.node_type])
      .map((node: any) => ({
        id: node.id,
        position: { x: node.position_x, y: node.position_y },
        data: { ...node, label: node.label || labels[node.node_type] },
        className: "flow-node process-node",
      }));
    setCurrent(flow);
    setName(flow.name);
    setSelected(null);
    setEdges(graphEdges);
    setNodes(arrange(graphNodes, graphEdges));
  };
  useEffect(() => {
    load().then((rows) => {
      if (rows[0]) choose(rows[0]);
    });
  }, []);
  const create = async () => {
    const value = prompt("Tên flow công việc")?.trim();
    if (!value) return;
    const made = await post<any>("/workflows", { name: value, template: true });
    const rows = await load();
    choose(rows.find((row) => row.id === made.id) || made);
  };
  const add = (type: string) => {
    const id = crypto.randomUUID();
    const added = {
      id,
      position: { x: 100, y: 100 },
      data: {
        node_type: type,
        label: labels[type],
        assignment_mode: type === "PRODUCTION" ? "DIRECT_MACHINE" : "ROLE",
        checklist_json:
          type === "PRODUCTION"
            ? ["Nguyên liệu đầy đủ", "Máy sẵn sàng", "Đã hiểu yêu cầu sản xuất"]
            : [],
      },
      className: "flow-node process-node",
    };
    const all = [...nodes, added];
    setNodes(arrange(all, edges));
    setSelected(id);
  };
  const connect = useCallback(
    (connection: Connection) => {
      if (
        !connection.source ||
        !connection.target ||
        connection.source === connection.target
      )
        return;
      setEdges((items) => {
        const added = addEdge(
          {
            id: crypto.randomUUID(),
            source: connection.source!,
            target: connection.target!,
            label: kind === "TRANSITION" ? "Tiếp theo" : kind,
            data: { relationType: kind },
            type: "smoothstep",
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          items,
        );
        setNodes((current) => arrange(current, added));
        return added;
      });
    },
    [kind, setEdges],
  );
  const node = nodes.find((item: any) => item.id === selected);
  const update = (data: any) =>
    setNodes((items) =>
      items.map((item: any) =>
        item.id === selected
          ? { ...item, data: { ...item.data, ...data } }
          : item,
      ),
    );
  const remove = () => {
    if (!selected) return;
    if (node) {
      const kept = nodes.filter((item: any) => item.id !== selected);
      const keptEdges = edges.filter(
        (edge: any) => edge.source !== selected && edge.target !== selected,
      );
      setEdges(keptEdges);
      setNodes(arrange(kept, keptEdges));
    } else
      setEdges((items) => items.filter((item: any) => item.id !== selected));
    setSelected(null);
  };
  const save = async () => {
    if (!current) return;
    try {
      await api(`/workflows/${current.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name,
          description: current.description || "",
          isActive: true,
          nodes: nodes.map((item: any) => ({
            id: item.id,
            nodeType: item.data.node_type,
            label: item.data.label,
            positionX: item.position.x,
            positionY: item.position.y,
            assignmentMode: item.data.assignment_mode || "ROLE",
            fixedMachineId: null,
            assigneeRole: null,
            checklist: item.data.checklist_json || [],
          })),
          edges: edges.map((item: any) => ({
            id: item.id,
            sourceNodeId: item.source,
            targetNodeId: item.target,
            relationType: item.data?.relationType || "TRANSITION",
            label: String(item.label || ""),
          })),
          subscriptions: current.subscriptions || [],
        }),
      });
      const rows = await load();
      choose(rows.find((row) => row.id === current.id));
      alert("Đã lưu flow quy trình.");
    } catch (error) {
      alert((error as Error).message);
    }
  };
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">QUY TRÌNH NGHIỆP VỤ</span>
          <h1>Flow công việc</h1>
        </div>
        <div className="page-actions">
          <button
            className="secondary"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Thu gọn" : "Mở rộng"}
          </button>
          <button className="primary" onClick={create}>
            <Plus />
            Tạo flow mẫu
          </button>
        </div>
      </div>
      <div className={expanded ? "flow-layout expanded-flow" : "flow-layout"}>
        <aside className="flow-panel">
          <label>
            Flow
            <select
              value={current?.id || ""}
              onChange={(event) => {
                const flow = flows.find(
                  (item) => item.id === event.target.value,
                );
                if (flow) choose(flow);
              }}
            >
              <option value="">Chọn flow</option>
              {flows.map((flow) => (
                <option key={flow.id} value={flow.id}>
                  {flow.name}
                </option>
              ))}
            </select>
          </label>
          {current && (
            <>
              <label>
                Tên flow
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <span className="panel-label">THÊM BƯỚC NGHIỆP VỤ</span>
              <select
                defaultValue=""
                onChange={(event) => {
                  if (event.target.value) add(event.target.value);
                  event.target.value = "";
                }}
              >
                <option value="">Chọn bước…</option>
                {Object.entries(labels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="panel-label">CHUYỂN BƯỚC</span>
              <div className="relation-switch">
                {(["TRANSITION", "PASS", "FAIL", "REWORK"] as const).map(
                  (value) => (
                    <button
                      type="button"
                      key={value}
                      className={kind === value ? "active" : ""}
                      onClick={() => setKind(value)}
                    >
                      {value === "TRANSITION" ? "Tiếp theo" : value}
                    </button>
                  ),
                )}
              </div>
              <button
                className="secondary wide"
                onClick={() => setNodes(arrange(nodes, edges))}
              >
                TỰ ĐỘNG SẮP XẾP
              </button>
              {node && (
                <section className="flow-selection">
                  <span className="panel-label">CẤU HÌNH BƯỚC</span>
                  <label>
                    Tên bước
                    <input
                      value={node.data.label}
                      onChange={(event) =>
                        update({ label: event.target.value })
                      }
                    />
                  </label>
                  <label>
                    Phân công
                    <select
                      value={node.data.assignment_mode || "ROLE"}
                      onChange={(event) =>
                        update({ assignment_mode: event.target.value })
                      }
                    >
                      <option value="DIRECT_MACHINE">Điều động chọn máy</option>
                      <option value="TEAM_LEADER">Tổ trưởng chọn máy</option>
                      <option value="FIXED_MACHINE">Máy cố định</option>
                      <option value="ROLE">Theo vai trò</option>
                    </select>
                  </label>
                  <label>
                    Checklist <small>Mỗi dòng một mục</small>
                    <textarea
                      rows={4}
                      value={(node.data.checklist_json || []).join("\n")}
                      onChange={(event) =>
                        update({
                          checklist_json: event.target.value
                            .split("\n")
                            .map((value) => value.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </label>
                </section>
              )}
              {selected && (
                <button className="secondary danger-text wide" onClick={remove}>
                  <Trash2 />
                  Xóa bước / liên kết
                </button>
              )}
              <p className="flow-help">
                Bố cục tự động dàn flow theo từng tầng và tách nhánh cùng tầng
                để hạn chế giao cắt.
              </p>
              <button className="primary wide" onClick={save}>
                <Save />
                LƯU FLOW
              </button>
            </>
          )}
        </aside>
        <section className="flow-canvas">
          {current ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={connect}
              onNodeClick={(_event, item) => setSelected(item.id)}
              onEdgeClick={(_event, item) => setSelected(item.id)}
              onPaneClick={() => setSelected(null)}
              fitView
            >
              <Background gap={22} />
              <MiniMap />
              <Controls />
            </ReactFlow>
          ) : (
            <div className="empty-list">Tạo flow mẫu để bắt đầu.</div>
          )}
        </section>
      </div>
    </Page>
  );
}

let elkPromise: Promise<import('elkjs/lib/elk-api.js').ELK> | undefined;
const flowLayoutEngine = () => elkPromise ??= import('elkjs/lib/elk.bundled.js')
  .then(({ default: ELK }) => new ELK())
  .catch(error => { elkPromise = undefined; throw error; });
function OrthogonalEdge(props: EdgeProps) {
  const data = props.data as any;
  const edge = props as any;
  const { screenToFlowPosition } = useReactFlow();
  const sourceHandle = edge.sourceHandle || "out-right";
  const targetHandle = edge.targetHandle || "in-left";
  const middleX = (props.sourceX + props.targetX) / 2;
  const middleY = (props.sourceY + props.targetY) / 2;
  const route = data?.manualPoint;
  const vertical =
    sourceHandle.includes("top") ||
    sourceHandle.includes("bottom") ||
    targetHandle.includes("top") ||
    targetHandle.includes("bottom");
  const fallback = vertical
    ? `M ${props.sourceX},${props.sourceY} L ${props.sourceX},${route?.y ?? middleY} L ${route?.x ?? middleX},${route?.y ?? middleY} L ${props.targetX},${route?.y ?? middleY} L ${props.targetX},${props.targetY}`
    : `M ${props.sourceX},${props.sourceY} L ${route?.x ?? middleX},${props.sourceY} L ${route?.x ?? middleX},${route?.y ?? middleY} L ${props.targetX},${route?.y ?? middleY} L ${props.targetX},${props.targetY}`;
  const path = route ? fallback : data?.elkPath || fallback;
  const labelX = route?.x ?? data?.labelX ?? middleX;
  const labelY = route?.y ?? data?.labelY ?? middleY;
  const begin = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    const move = (next: any) =>
      data?.onRouteChange?.(
        props.id,
        screenToFlowPosition({ x: next.clientX, y: next.clientY }),
      );
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
  };
  return (
    <>
      <BaseEdge id={props.id} path={path} markerEnd={props.markerEnd} />
      <EdgeLabelRenderer>
        {props.label && (
          <div
            className="elk-edge-label"
            style={{
              transform: `translate(-50%,-50%) translate(${labelX}px,${labelY - 15}px)`,
            }}
          >
            {String(props.label)}
          </div>
        )}
        <button
          type="button"
          title="Kéo để điều chỉnh đường nối"
          className="edge-route-handle"
          onPointerDown={begin}
          style={{
            transform: `translate(-50%,-50%) translate(${labelX}px,${labelY}px)`,
          }}
        />
      </EdgeLabelRenderer>
    </>
  );
}
const elkEdgeTypes = { orthogonal: OrthogonalEdge };
function ProcessNodeWithPorts({ data, selected }: any) {
  return (
    <div className={`process-node-body${selected ? " selected" : ""}`}>
      <Handle type="target" position={Position.Left} id="in-left" />
      <Handle type="source" position={Position.Right} id="out-right" />
      <Handle type="target" position={Position.Top} id="in-top" />
      <Handle type="source" position={Position.Bottom} id="out-bottom" />
      <span>{data.label}</span>
    </div>
  );
}
const elkNodeTypes = { process: ProcessNodeWithPorts };
function ElkFlowPage() {
  const actionGate = useActionGate();
  const {user} = useAuth();
  const editable = can(user, 'workflows.manage');
  const [deletingFlow, setDeletingFlow] = useState(false);
  const [flows, setFlows] = useState<any[]>([]),
    [machines, setMachines] = useState<any[]>([]),
    [current, setCurrent] = useState<any>(null),
    [name, setName] = useState(""),
    [multiRolePolicy, setMultiRolePolicy] = useState<
      "ALLOW" | "WARN" | "REQUIRE_DIFFERENT"
    >("WARN"),
    [kind, setKind] = useState<"TRANSITION" | "PASS" | "FAIL" | "REWORK">(
      "TRANSITION",
    ),
    [expanded, setExpanded] = useState(false),
    [selected, setSelected] = useState<string | null>(null),
    [layingOut, setLayingOut] = useState(false);
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);
  const moveRoute = useCallback(
    (id: string, manualPoint: { x: number; y: number }) =>
      setEdges((items) =>
        items.map((item) =>
          item.id === id
            ? {
                ...item,
                data: {
                  ...item.data,
                  routeMode: "MANUAL",
                  manualPoint,
                  elkPath: undefined,
                  labelX: manualPoint.x,
                  labelY: manualPoint.y,
                },
              }
            : item,
        ),
      ),
    [setEdges],
  );
  const onDesignerNodesChange = useCallback(
    (changes: any[]) => {
      onNodesChange(changes);
      if (
        changes.some((change) => change.type === "position" && change.position)
      )
        setEdges((items) =>
          items.map((item) =>
            item.data?.routeMode === "MANUAL"
              ? item
              : {
                  ...item,
                  data: {
                    ...item.data,
                    elkPath: undefined,
                    labelX: undefined,
                    labelY: undefined,
                  },
                },
          ),
        );
    },
    [onNodesChange, setEdges],
  );
  const labels: Record<string, string> = {
    START: "Bắt đầu",
    DISPATCH: "Điều động / phân công",
    PRODUCTION: "Sản xuất",
    QC: "QC",
    PACKING: "Đóng gói",
    WAREHOUSE: "Nhập kho",
    SHIPPING: "Xuất / giao hàng",
    END: "Hoàn tất",
    CUSTOM: "Bước tùy chỉnh",
  };
  const edgeLabels: Record<string, string> = {
    TRANSITION: "Tiếp theo",
    PASS: "PASS",
    FAIL: "FAIL",
    REWORK: "Làm lại",
  };
  const makeEdge = (edge: any) => {
    const route = edge.route_json || {};
    const manualPoint =
      route.mode === "MANUAL"
        ? route.waypoints?.[0]
        : route.x !== undefined
          ? route
          : undefined;
    return {
      id: edge.id,
      source: edge.source_node_id || edge.source,
      target: edge.target_node_id || edge.target,
      sourceHandle: route.sourceHandle || edge.sourceHandle || undefined,
      targetHandle: route.targetHandle || edge.targetHandle || undefined,
      label:
        edge.label ||
        edgeLabels[edge.relation_type || edge.data?.relationType] ||
        "Tiếp theo",
      data: {
        relationType: edge.relation_type || edge.data?.relationType,
        routeMode: manualPoint ? "MANUAL" : "AUTO",
        manualPoint,
        onRouteChange: editable ? moveRoute : undefined,
      },
      type: "orthogonal",
      markerEnd: { type: MarkerType.ArrowClosed },
    };
  };
  const layout = async (rawNodes: any[], rawEdges: any[]) => {
    if (!rawNodes.length) return;
    setLayingOut(true);
    try {
      const elk = await flowLayoutEngine();
      const result = await elk.layout({
        id: "root",
        layoutOptions: {
          "elk.algorithm": "layered",
          "elk.direction": "RIGHT",
          "elk.edgeRouting": "ORTHOGONAL",
          "elk.spacing.nodeNode": "70",
          "elk.layered.spacing.nodeNodeBetweenLayers": "120",
          "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
          "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
          "elk.layered.cycleBreaking.strategy": "DEPTH_FIRST",
        },
        children: rawNodes.map((node) => ({
          id: node.id,
          width: 200,
          height: 62,
          layoutOptions: { "elk.portConstraints": "FIXED_SIDE" },
          ports: [
            {
              id: `${node.id}:in-left`,
              width: 1,
              height: 1,
              x: 0,
              y: 31,
              layoutOptions: { "elk.port.side": "WEST" },
            },
            {
              id: `${node.id}:out-right`,
              width: 1,
              height: 1,
              x: 199,
              y: 31,
              layoutOptions: { "elk.port.side": "EAST" },
            },
            {
              id: `${node.id}:in-top`,
              width: 1,
              height: 1,
              x: 100,
              y: 0,
              layoutOptions: { "elk.port.side": "NORTH" },
            },
            {
              id: `${node.id}:out-bottom`,
              width: 1,
              height: 1,
              x: 100,
              y: 61,
              layoutOptions: { "elk.port.side": "SOUTH" },
            },
          ],
        })),
        edges: rawEdges.map((edge) => ({
          id: edge.id,
          sources: [`${edge.source}:${edge.sourceHandle || "out-right"}`],
          targets: [`${edge.target}:${edge.targetHandle || "in-left"}`],
          layoutOptions:
            edge.data?.relationType === "REWORK"
              ? {
                  "elk.layered.priority.direction": "-1",
                  "elk.priority": "100",
                }
              : edge.data?.relationType === "FAIL"
                ? { "elk.layered.priority.direction": "1" }
                : {},
        })),
      } as any);
      const placed = new Map(
        (result.children || []).map((node: any) => [node.id, node]),
      );
      const sections = new Map(
        (result.edges || []).map((edge: any) => [edge.id, edge.sections?.[0]]),
      );
      setNodes(
        rawNodes.map((node) => {
          const placedNode: any = placed.get(node.id);
          return {
            ...node,
            type: "process",
            className: "process-node",
            position: {
              x: placedNode?.x ?? node.position.x,
              y: placedNode?.y ?? node.position.y,
            },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
          };
        }),
      );
      setEdges(
        rawEdges.map((edge) => {
          const section: any = sections.get(edge.id);
          const points = [
            section?.startPoint,
            ...(section?.bendPoints || []),
            section?.endPoint,
          ].filter(Boolean);
          const path = points.length
            ? `M ${points.map((point: any) => `${point.x} ${point.y}`).join(" L ")}`
            : undefined;
          const labelPoint = points[Math.floor(points.length / 2)] || {};
          return {
            ...edge,
            data: {
              ...edge.data,
              routeMode: "AUTO",
              manualPoint: undefined,
              elkPath: path,
              labelX: labelPoint.x,
              labelY: labelPoint.y,
            },
            type: "orthogonal",
          };
        }),
      );
    } finally {
      setLayingOut(false);
    }
  };
  const load = async () => {
    const rows = await api<any[]>("/workflows");
    setFlows(rows);
    return rows;
  };
  const choose = (flow: any) => {
    const graphNodes = (flow.nodes || [])
      .filter((node: any) => labels[node.node_type])
      .map((node: any) => ({
        id: node.id,
        type: "process",
        position: { x: node.position_x, y: node.position_y },
        data: { ...node, label: node.label || labels[node.node_type] },
        className: "flow-node process-node",
      }));
    const graphEdges = (flow.edges || []).map(makeEdge);
    setCurrent(flow);
    setName(flow.name);
    setMultiRolePolicy(flow.multi_role_policy || "WARN");
    setSelected(null);
    setNodes(graphNodes);
    setEdges(graphEdges);
  };
  const deleteFlow = actionGate.guard(async () => {
    if (!editable || !current || deletingFlow || layingOut) return;
    if (!confirm(`Xóa flow “${current.name}” cùng toàn bộ bước và liên kết? Thay đổi chưa lưu cũng sẽ mất. Hành động này không thể hoàn tác.`)) return;
    setDeletingFlow(true);
    try {
      await api(`/workflows/${current.id}`, {method:'DELETE'});
      setCurrent(null); setName(''); setSelected(null); setNodes([]); setEdges([]);
      const rows = await load();
      if (rows[0]) choose(rows[0]);
    } catch (error) {
      alert((error as Error).message);
    } finally {
      setDeletingFlow(false);
    }
  });
  useEffect(() => {
    api<any[]>("/machines").then(setMachines);
    load().then((rows) => {
      if (rows[0]) choose(rows[0]);
    });
  }, []);
  const create = actionGate.guard(async () => {
    const value = prompt("Tên flow công việc")?.trim();
    if (!value) return;
    const made = await post<any>("/workflows", { name: value, template: true });
    const rows = await load();
    const created = rows.find((row) => row.id === made.id) || made;
    choose(created);
    setTimeout(
      () =>
        void layout(
          (created.nodes || []).map((node: any) => ({
            id: node.id,
            type: "process",
            position: { x: node.position_x, y: node.position_y },
            data: { ...node, label: node.label || labels[node.node_type] },
          })),
          (created.edges || []).map(makeEdge),
        ),
      0,
    );
  });
  const add = (type: string) => {
    const id = crypto.randomUUID();
    setNodes((items) => [
      ...items,
      {
        id,
        type: "process",
        position: { x: 120, y: 100 + items.length * 80 },
        data: {
          node_type: type,
          label: labels[type],
          assignment_mode: type === "PRODUCTION" ? "DIRECT_MACHINE" : "ROLE",
          assignee_role:
            type === "QC"
              ? "QC"
              : type === "PACKING"
                ? "PACKING"
                : type === "WAREHOUSE"
                  ? "WAREHOUSE"
                  : type === "SHIPPING"
                    ? "SHIPPING"
                    : null,
          checklist_json:
            type === "PRODUCTION"
              ? [
                  "Nguyên liệu đầy đủ",
                  "Máy sẵn sàng",
                  "Đã hiểu yêu cầu sản xuất",
                ]
              : [],
        },
        className: "process-node",
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      },
    ]);
    setSelected(id);
  };
  const connect = useCallback(
    (connection: Connection) => {
      if (
        !connection.source ||
        !connection.target ||
        connection.source === connection.target
      )
        return;
      setEdges((items) =>
        addEdge(
          {
            id: crypto.randomUUID(),
            source: connection.source!,
            target: connection.target!,
            sourceHandle: connection.sourceHandle || undefined,
            targetHandle: connection.targetHandle || undefined,
            label: edgeLabels[kind],
            data: {
              relationType: kind,
              routeMode: "AUTO",
              onRouteChange: moveRoute,
            },
            type: "orthogonal",
            markerEnd: { type: MarkerType.ArrowClosed },
          },
          items,
        ),
      );
    },
    [kind, moveRoute, setEdges],
  );
  const node = nodes.find((item: any) => item.id === selected);
  const update = (data: any) =>
    setNodes((items) =>
      items.map((item: any) =>
        item.id === selected
          ? { ...item, data: { ...item.data, ...data } }
          : item,
      ),
    );
  const remove = () => {
    if (!selected) return;
    if (node) {
      setNodes((items) => items.filter((item: any) => item.id !== selected));
      setEdges((items) =>
        items.filter(
          (item: any) => item.source !== selected && item.target !== selected,
        ),
      );
    } else
      setEdges((items) => items.filter((item: any) => item.id !== selected));
    setSelected(null);
  };
  const save = actionGate.guard(async () => {
    if (!current) return;
    try {
      await api(`/workflows/${current.id}`, {
        method: "PUT",
        body: JSON.stringify({
          name,
          description: current.description || "",
          isActive: true,
          multiRolePolicy,
          nodes: nodes.map((item: any) => ({
            id: item.id,
            nodeType: item.data.node_type,
            label: item.data.label,
            positionX: item.position.x,
            positionY: item.position.y,
            assignmentMode: item.data.assignment_mode || "ROLE",
            fixedMachineId: item.data.fixed_machine_id || null,
            assigneeRole: item.data.assignee_role || null,
            checklist: item.data.checklist_json || [],
          })),
          edges: edges.map((item: any) => ({
            id: item.id,
            sourceNodeId: item.source,
            targetNodeId: item.target,
            relationType: item.data?.relationType || "TRANSITION",
            label: String(item.label || ""),
            route: {
              mode: item.data?.routeMode === "MANUAL" ? "MANUAL" : "AUTO",
              waypoints: item.data?.manualPoint ? [item.data.manualPoint] : [],
              sourceHandle: item.sourceHandle || null,
              targetHandle: item.targetHandle || null,
            },
          })),
          subscriptions: current.subscriptions || [],
        }),
      });
      const rows = await load();
      const saved = rows.find((row) => row.id === current.id);
      if (saved) choose(saved);
      alert("Đã lưu flow quy trình.");
    } catch (error) {
      alert((error as Error).message);
    }
  });
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">ELK HIERARCHICAL LAYOUT</span>
          <h1>Flow công việc</h1>
        </div>
        <div className="page-actions">
          <button
            className="secondary"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? "Thu gọn" : "Mở rộng"}
          </button>
          {editable && current && <button className="secondary danger-text" onClick={() => void deleteFlow()} disabled={deletingFlow || layingOut}>
            <Trash2 /> {deletingFlow ? 'Đang xóa…' : 'Xóa flow'}
          </button>}
          <button className="primary" onClick={create} disabled={!editable || deletingFlow}>
            <Plus />
            Tạo flow mẫu
          </button>
        </div>
      </div>
      <div className={expanded ? "flow-layout expanded-flow" : "flow-layout"}>
        <aside className="flow-panel">
          <label>
            Flow
            <select
              value={current?.id || ""}
              disabled={deletingFlow}
              onChange={(event) => {
                const flow = flows.find(
                  (item) => item.id === event.target.value,
                );
                if (flow) choose(flow);
              }}
            >
              <option value="">Chọn flow</option>
              {flows.map((flow) => (
                <option key={flow.id} value={flow.id}>
                  {flow.name}
                </option>
              ))}
            </select>
          </label>
          {current && (
            <fieldset className="permission-fieldset" disabled={!editable || deletingFlow}>
              <label>
                Tên flow
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </label>
              <label>
                Chính sách kiêm nhiệm
                <select
                  value={multiRolePolicy}
                  onChange={(event) =>
                    setMultiRolePolicy(
                      event.target.value as
                        | "ALLOW"
                        | "WARN"
                        | "REQUIRE_DIFFERENT",
                    )
                  }
                >
                  <option value="ALLOW">Cho phép tự do</option>
                  <option value="WARN">Cho phép, có cảnh báo</option>
                  <option value="REQUIRE_DIFFERENT">
                    Bắt buộc đổi người
                  </option>
                </select>
                <small>
                  Kiểm soát một người làm nhiều công đoạn trên cùng lệnh.
                </small>
              </label>
              <span className="panel-label">THÊM BƯỚC NGHIỆP VỤ</span>
              <select
                defaultValue=""
                onChange={(event) => {
                  if (event.target.value) add(event.target.value);
                  event.target.value = "";
                }}
              >
                <option value="">Chọn bước…</option>
                {Object.entries(labels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="panel-label">CHUYỂN BƯỚC</span>
              <div className="relation-switch">
                {(["TRANSITION", "PASS", "FAIL", "REWORK"] as const).map(
                  (value) => (
                    <button
                      type="button"
                      key={value}
                      className={kind === value ? "active" : ""}
                      onClick={() => setKind(value)}
                    >
                      {value === "TRANSITION" ? "Tiếp theo" : value}
                    </button>
                  ),
                )}
              </div>
              <button
                className="secondary wide"
                disabled={layingOut}
                onClick={() => void layout(nodes, edges)}
              >
                {layingOut ? "ĐANG SẮP XẾP…" : "ELK TỰ ĐỘNG SẮP XẾP"}
              </button>
              {node && (
                <section className="flow-selection">
                  <span className="panel-label">CẤU HÌNH BƯỚC</span>
                  <label>
                    Tên bước
                    <input
                      value={node.data.label}
                      onChange={(event) =>
                        update({ label: event.target.value })
                      }
                    />
                  </label>
                  {node.data.assignment_mode === "ROLE" && (
                    <label>
                      Vai trò thực hiện
                      <select
                        value={node.data.assignee_role || "WORKER"}
                        onChange={(event) =>
                          update({ assignee_role: event.target.value })
                        }
                      >
                        {Object.entries(roleLabels)
                          .filter(
                            ([role]) =>
                              !["ADMIN", "SUPERADMIN"].includes(role),
                          )
                          .map(([role, label]) => (
                            <option value={role} key={role}>
                              {label}
                            </option>
                          ))}
                      </select>
                    </label>
                  )}
                  {node.data.assignment_mode === "FIXED_MACHINE" && (
                    <label>
                      Máy cố định
                      <select
                        value={node.data.fixed_machine_id || ""}
                        onChange={(event) =>
                          update({
                            fixed_machine_id: event.target.value || null,
                          })
                        }
                      >
                        <option value="">Chọn máy…</option>
                        {machines.map((machine) => (
                          <option value={machine.id} key={machine.id}>
                            {machine.code} · {machine.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label>
                    Phân công
                    <select
                      value={node.data.assignment_mode || "ROLE"}
                      onChange={(event) =>
                        update({ assignment_mode: event.target.value })
                      }
                    >
                      <option value="DIRECT_MACHINE">Điều động chọn máy</option>
                      <option value="TEAM_LEADER">Tổ trưởng chọn máy</option>
                      <option value="FIXED_MACHINE">Máy cố định</option>
                      <option value="ROLE">Theo vai trò</option>
                    </select>
                  </label>
                  <label>
                    Checklist <small>Mỗi dòng một mục</small>
                    <textarea
                      rows={4}
                      value={(node.data.checklist_json || []).join("\n")}
                      onChange={(event) =>
                        update({
                          checklist_json: event.target.value
                            .split("\n")
                            .map((value) => value.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </label>
                </section>
              )}
              {selected && (
                <button className="secondary danger-text wide" onClick={remove}>
                  <Trash2 />
                  Xóa bước / liên kết
                </button>
              )}
              <p className="flow-help">
                ELK chỉ chạy khi bấm nút. Kéo node sẽ chuyển về tuyến trực giao
                động; kéo nút vuông trên line để lưu tuyến thủ công theo đúng
                mức zoom.
              </p>
              <button className="primary wide" onClick={save}>
                <Save />
                LƯU FLOW
              </button>
            </fieldset>
          )}
        </aside>
        <section className="flow-canvas">
          {current ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              edgeTypes={elkEdgeTypes}
              nodeTypes={elkNodeTypes}
              nodesDraggable={editable}
              nodesConnectable={editable}
              edgesReconnectable={editable}
              deleteKeyCode={editable ? ['Backspace', 'Delete'] : null}
              onNodesChange={onDesignerNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={connect}
              onNodeClick={(_event, item) => setSelected(item.id)}
              onEdgeClick={(_event, item) => setSelected(item.id)}
              onPaneClick={() => setSelected(null)}
              fitView
            >
              <Background gap={22} />
              <MiniMap />
              <Controls />
            </ReactFlow>
          ) : (
            <div className="empty-list">Tạo flow mẫu để bắt đầu.</div>
          )}
        </section>
      </div>
    </Page>
  );
}

function WorkflowsPage() {
  return <ElkFlowPage />;
}

function LiveProcessNode({ data }: any) {
  const openTasks = data.tasks.filter(
    (task: any) => task.status !== "COMPLETED",
  );
  const blocked = openTasks.some((task: any) =>
    ["BLOCKED", "WAITING_SUPPORT"].includes(task.status),
  );
  const running = openTasks.some((task: any) => task.status === "IN_PROGRESS");
  return (
    <div
      className={`live-process-node ${blocked ? "blocked" : running ? "running" : openTasks.length ? "waiting" : "quiet"}`}
    >
      <Handle type="target" position={Position.Left} id="in-left" />
      <Handle type="source" position={Position.Right} id="out-right" />
      <div className="live-node-title">
        <span>{data.label}</span>
        <b>{openTasks.length}</b>
      </div>
      <small>
        {openTasks.length ? "Đang có công việc" : "Chưa có việc đang chạy"}
      </small>
      <div className="live-node-orders">
        {openTasks.slice(0, 2).map((task: any) => (
          <div key={task.id}>
            <b>{task.order_code}</b>
            <span>
              {task.machine_code || task.assignee_name || "Chưa phân công"}
            </span>
          </div>
        ))}
        {openTasks.length > 2 && (
          <em>+{openTasks.length - 2} công việc khác</em>
        )}
      </div>
    </div>
  );
}
const liveNodeTypes = { liveProcess: LiveProcessNode };

function LiveOperationsPage() {
  const actionGate = useActionGate();
  const {user} = useAuth();
  const [data, setData] = useState<any>(null);
  const [flowId, setFlowId] = useState("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);
  const load = useCallback(
    () =>
      api<any>("/live-operations").then((result) => {
        setData(result);
        setFlowId((current) =>
          current && result.flows.some((flow: any) => flow.id === current)
            ? current
            : result.flows[0]?.id || "",
        );
      }),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  useSocket(load);
  const flow = data?.flows.find((item: any) => item.id === flowId);
  const flowTasks = useMemo(
    () =>
      (data?.tasks || []).filter(
        (task: any) => !flowId || task.workflow_id === flowId,
      ),
    [data, flowId],
  );
  const graphNodes = useMemo(
    () =>
      (flow?.nodes || []).map((node: any, index: number) => ({
        id: node.id,
        type: "liveProcess",
        position: {
          x: Number.isFinite(node.position_x) ? node.position_x : index * 270,
          y: Number.isFinite(node.position_y) ? node.position_y : 180,
        },
        data: {
          label: node.label,
          tasks: flowTasks.filter(
            (task: any) => task.workflow_node_id === node.id,
          ),
        },
      })),
    [flow, flowTasks],
  );
  const graphEdges = useMemo(
    () =>
      (flow?.edges || []).map((edge: any) => ({
        id: edge.id,
        source: edge.source_node_id,
        target: edge.target_node_id,
        type: "smoothstep",
        label:
          edge.label ||
          (edge.relation_type === "TRANSITION"
            ? "Tiếp theo"
            : edge.relation_type),
        animated: flowTasks.some(
          (task: any) =>
            task.workflow_node_id === edge.source_node_id &&
            task.status === "IN_PROGRESS",
        ),
        markerEnd: { type: MarkerType.ArrowClosed },
        className: `live-edge ${edge.relation_type.toLowerCase()}`,
      })),
    [flow, flowTasks],
  );
  const visibleTasks = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return flowTasks.filter(
      (task: any) =>
        !needle ||
        [
          task.order_code,
          task.product_name,
          task.customer_name,
          task.machine_name,
          task.assignee_name,
          task.active_operator_name,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(needle),
        ),
    );
  }, [flowTasks, query]);
  const assign = actionGate.guard(async (taskId: string, userId: string) => {
    if (!userId) return;
    try {
      await post(`/operations/${taskId}/assign-user`, { userId });
      await load();
    } catch (error) {
      alert((error as Error).message);
    }
  });
  if (!data)
    return (
      <Page>
        <div className="skeleton tall" />
      </Page>
    );
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">ĐIỀU HÀNH TRỰC QUAN</span>
          <h1>Theo dõi sản xuất realtime</h1>
        </div>
        <div className="live-sync">
          <span className="live-dot" /> Đồng bộ {fmt(data.serverTime)}
        </div>
      </div>
      <div className="live-kpis">
        <article>
          <b>{data.summary.active}</b>
          <span>Công việc đang mở</span>
        </article>
        <article className="blue">
          <b>{data.summary.inProgress}</b>
          <span>Đang thực hiện</span>
        </article>
        <article className="red">
          <b>{data.summary.blocked}</b>
          <span>Đang vướng / cần hỗ trợ</span>
        </article>
        <article className="amber">
          <b>{data.summary.unassigned}</b>
          <span>Chưa phân công</span>
        </article>
        <article>
          <b>{data.summary.shipping}</b>
          <span>Đang giao hàng</span>
        </article>
      </div>
      <section className="live-toolbar">
        <label>
          Flow đang theo dõi
          <select
            value={flowId}
            onChange={(event) => setFlowId(event.target.value)}
          >
            {data.flows.map((item: any) => (
              <option value={item.id} key={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Tìm lệnh, máy, người, khách hàng
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Ví dụ: LSX-001, Máy dập, công ty A"
          />
        </label>
        <button
          className="secondary"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? "Thu gọn bản đồ" : "Mở rộng bản đồ"}
        </button>
      </section>
      <section className={expanded ? "live-map expanded-live-map" : "live-map"}>
        {expanded && (
          <button
            className="secondary live-map-close"
            onClick={() => setExpanded(false)}
          >
            <X /> Thu gọn
          </button>
        )}
        {flow ? (
          <ReactFlow
            nodes={graphNodes}
            edges={graphEdges}
            nodeTypes={liveNodeTypes}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.25}
          >
            <Background gap={22} />
            <MiniMap pannable zoomable />
            <Controls showInteractive={false} />
          </ReactFlow>
        ) : (
          <div className="empty-list">Chưa có flow hoạt động để theo dõi.</div>
        )}
      </section>
      <section className="live-detail">
        <div className="card-title">
          <h2>Chi tiết lệnh theo công đoạn</h2>
          <span>{visibleTasks.length} bản ghi</span>
        </div>
        <div className="live-task-list">
          {visibleTasks.map((task: any) => (
            <article
              key={task.id}
              className={
                ["BLOCKED", "WAITING_SUPPORT"].includes(task.status)
                  ? "blocked"
                  : ""
              }
            >
              <div className="live-task-order">
                <b>{task.order_code}</b>
                <span>
                  {task.product_name} · {num(task.planned_quantity)} {task.unit}
                </span>
                <small>
                  {task.customer_name || "Chưa nhập khách hàng"}
                  {task.delivery_address ? ` · ${task.delivery_address}` : ""}
                </small>
              </div>
              <div>
                <small>Công đoạn</small>
                <b>{task.node_label || task.operation_name}</b>
                <Status value={task.status} />
                {task.policy_warning && (
                  <span className="policy-warning">
                    Cảnh báo: nhân sự kiêm nhiều công đoạn
                  </span>
                )}
                <span>
                  Lũy kế: đạt {num(task.completed_quantity)} /{" "}
                  {num(task.planned_quantity)} · lỗi{" "}
                  {num(task.defect_quantity)}
                </span>
              </div>
              <div>
                <small>Máy / khu vực</small>
                <b>
                  {task.machine_code
                    ? `${task.machine_code} · ${task.machine_name}`
                    : "Không yêu cầu máy"}
                </b>
                <span>{task.department_name || "—"}</span>
              </div>
              <div>
                <small>Người thực hiện</small>
                <b>{task.assignee_name || "Chưa phân công"}</b>
                <span>
                  {task.active_operator_name
                    ? `Đứng máy: ${task.active_operator_name}`
                    : ""}
                </span>
                {task.last_shift_reported_at && (
                  <span>
                    Ca trước: {task.last_shift_worker_name} bàn giao đạt{" "}
                    {num(task.last_shift_good_quantity)}, lỗi{" "}
                    {num(task.last_shift_defect_quantity)}
                  </span>
                )}
              </div>
              <div className="live-assignment">
                <small>Phân công nhanh</small>
                {task.status !== "COMPLETED" ? (
                  <select
                    disabled={!can(user, 'operations.assign')}
                    value={task.assignee_id || ""}
                    onChange={(event) =>
                      void assign(task.id, event.target.value)
                    }
                  >
                    <option value="">Chọn nhân sự…</option>
                    {data.people.map((person: any) => (
                      <option value={person.id} key={person.id}>
                        {person.full_name} ·{" "}
                        {person.department_name ||
                          person.roles
                            .map((role: string) => roleLabels[role] || role)
                            .join("/")}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span>Hoàn tất {fmt(task.completed_at)}</span>
                )}
              </div>
            </article>
          ))}
          {!visibleTasks.length && (
            <div className="empty-list">Không có công việc phù hợp bộ lọc.</div>
          )}
        </div>
      </section>
    </Page>
  );
}

function AuditPage() {
  const [items, setItems] = useState<any[]>([]);
  useEffect(() => {
    api<any[]>("/audit-logs?limit=300").then(setItems);
  }, []);
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">TRUY VẾT HỆ THỐNG</span>
          <h1>Nhật ký hoạt động</h1>
        </div>
      </div>
      <div className="audit-list">
        {items.map((item) => (
          <article key={item.id}>
            <span className="audit-dot" />
            <div>
              <b>{item.actor_name || "Hệ thống"}</b>
              <p>{auditLabel(item.action)}</p>
              <small>
                {fmt(item.created_at)} · {item.entity_type}
              </small>
            </div>
          </article>
        ))}
      </div>
    </Page>
  );
}

type DeveloperResetScope =
  | "accounts"
  | "operations"
  | "workflows"
  | "materials"
  | "machines"
  | "auditLogs";

function RolesPage() {
  const actionGate = useActionGate();
  const [items, setItems] = useState<any[]>([]);
  const [catalog,setCatalog] = useState<{code:string;name:string}[]>([]);
  const [editing, setEditing] = useState<any>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const load = () => Promise.all([api<any[]>('/roles'),api<{code:string;name:string}[]>('/permission-catalog')]).then(([r,c])=>{setItems(r);setCatalog(c);});
  useEffect(() => { void load().catch(e => setError(e.message)); }, []);
  const save = actionGate.guard(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    setSaving(true); setError('');
    try {
      const input = { name: String(f.get('name')), permissions: f.getAll('permissions').map(String),dataScope:String(f.get('dataScope')) };
      if (editing) await api(`/roles/${editing.code}`, { method: 'PATCH', body: JSON.stringify(input) });
      else await post('/roles', { ...input, code: String(f.get('code')).trim().toUpperCase() });
      setEditing(null); await load();
      form.reset();
    } catch (err) { setError((err as Error).message); }
    finally { setSaving(false); }
  });
  return <Page>
    <div className="page-title"><div><span className="eyebrow">SUPERADMIN</span><h1>Vai trò và quyền</h1></div></div>
    <p className="page-subtitle">Chọn quyền theo tính năng và thao tác. Một người có nhiều role nhận tổng quyền của các role. Quyền xem dữ liệu cần thiết sẽ được bổ sung khi cấp quyền thao tác. SUPERADMIN luôn có toàn quyền.</p>
    {error && <div className="error">{error}</div>}
    <form key={editing?.code || 'new'} className="modal-card" onSubmit={save}>
      <h2>{editing ? `Sửa role ${editing.code}` : 'Tạo role mới'}</h2>
      <div className="form-grid">
        <label>Mã role<input name="code" required pattern="[A-Za-z][A-Za-z0-9_]{1,49}" maxLength={50} placeholder="QC_GIAO_HANG" defaultValue={editing?.code || ''} disabled={!!editing} /></label>
        <label>Tên role<input name="name" required minLength={2} maxLength={100} defaultValue={editing?.name || ''} /></label>
      </div>
      <label>Phạm vi dữ liệu máy / lệnh / nhân sự / sự cố<select name="dataScope" defaultValue={editing?.data_scope || 'ALL'}><option value="ALL">Toàn công ty</option><option value="DEPARTMENT">Phòng / xưởng được phân công</option></select></label>
      <p>TV, thiết kế flow, danh mục sản phẩm và phòng ban là cấu hình chung toàn công ty.</p>
      <p>Quyền thao tác tự kèm quyền đọc dữ liệu cần thiết. Phát hành / ghép TV kèm quyền xem tổng quan. Nếu một tài khoản có nhiều role, quyền được cộng dồn; role toàn công ty mở rộng phạm vi của quyền tương ứng.</p>
      {[...new Set(catalog.map(p=>p.code.split('.')[0]))].map(group=><fieldset className="role-picker" key={group}><legend>{({tv:'Kịch bản TV',machines:'Máy sản xuất',orders:'Lệnh sản xuất',users:'Nhân sự',departments:'Phòng / xưởng',workflows:'Flow',products:'Sản phẩm',sessions:'Ca làm việc',operations:'Thực hiện công đoạn',stages:'Danh mục công đoạn',materials:'Nguyên liệu / bán thành phẩm',qc:'QC',issues:'Sự cố',dashboard:'Tổng quan',live:'Realtime',audit:'Nhật ký',printing:'Thiết bị in'} as Record<string,string>)[group]}</legend><div>
        {catalog.filter(p=>p.code.startsWith(group+'.')).map(p => <label key={p.code}><input type="checkbox" name="permissions" value={p.code} defaultChecked={editing?.permissions.includes(p.code) || false} /><span>{p.name}</span></label>)}
      </div></fieldset>)}
      <button className="primary" disabled={saving}>{saving ? 'Đang lưu…' : 'Lưu role'}</button>
      {editing && <button type="button" className="secondary" onClick={() => setEditing(null)}>Hủy sửa</button>}
    </form>
    <div className="role-catalog-grid">{items.map(r => <article className="table-card" key={r.code}>
      <h3>{r.name}</h3><p>{r.code} · {r.is_system ? 'Role hệ thống' : 'Role tùy chỉnh'}</p>
      <p>{r.code==='SUPERADMIN'?'Toàn quyền chủ hệ thống':`${r.permissions.length} quyền · ${r.data_scope==='ALL'?'Toàn công ty':'Phòng / xưởng'}`}</p>
      {r.code!=='SUPERADMIN' && <button className="secondary" onClick={() => { setEditing(r); setError(''); window.scrollTo({top:0,behavior:'smooth'}); }}>Sửa quyền</button>}
    </article>)}</div>
  </Page>;
}

function DeveloperModePage() {
  const actionGate = useActionGate();
  const [preview, setPreview] = useState<any>(null);
  const [scopes, setScopes] = useState<Record<DeveloperResetScope, boolean>>({
    accounts: false,
    operations: false,
    workflows: false,
    materials: false,
    machines: false,
    auditLogs: false,
  });
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<Record<string, number> | null>(null);
  const load = useCallback(
    () => api<any>("/developer/reset-preview").then(setPreview),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const selectedCount = Object.values(scopes).filter(Boolean).length;
  const toggleScope = (scope: DeveloperResetScope) =>
    setScopes((current) => ({ ...current, [scope]: !current[scope] }));
  const reset = actionGate.guard(async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setResult(null);
    if (!selectedCount) {
      setError("Hãy chọn ít nhất một nhóm dữ liệu cần xóa.");
      return;
    }
    if (confirmation !== "XOA DU LIEU TEST") {
      setError("Cụm xác nhận chưa chính xác.");
      return;
    }
    if (
      !confirm(
        "Xác nhận xóa vĩnh viễn các nhóm dữ liệu đã chọn? Thao tác này không thể hoàn tác.",
      )
    )
      return;
    setBusy(true);
    try {
      const response = await post<{ deleted: Record<string, number> }>(
        "/developer/reset",
        { ...scopes, confirmation },
      );
      setResult(response.deleted);
      setScopes({
        accounts: false,
        operations: false,
        workflows: false,
        materials: false,
        machines: false,
        auditLogs: false,
      });
      setConfirmation("");
      await load();
    } catch (resetError) {
      setError((resetError as Error).message);
    } finally {
      setBusy(false);
    }
  });
  const cards: Array<{
    id: DeveloperResetScope;
    title: string;
    count: string;
    description: string;
  }> = [
    {
      id: "accounts",
      title: "Tài khoản test",
      count: num(preview?.accounts),
      description:
        "Xóa mọi tài khoản trừ superadmin đang đăng nhập. Lịch sử còn giữ sẽ được gỡ liên kết an toàn.",
    },
    {
      id: "operations",
      title: "Trạng thái vận hành",
      count: `${num(preview?.orders)} lệnh · ${num(preview?.operations)} công đoạn · ${num(preview?.customers)} khách hàng`,
      description:
        "Xóa lệnh, sản phẩm phát sinh, khách hàng, bộ đếm mã, công đoạn, sự cố, ca máy và thông báo; đưa máy còn lại về Sẵn sàng.",
    },
    {
      id: "workflows",
      title: "Flow thử nghiệm",
      count: num(preview?.workflows),
      description: "Xóa các flow cũ. Tuyến riêng của lệnh mới không phụ thuộc trực tiếp vào flow mẫu.",
    },
    {
      id: "materials",
      title: "Kho vật tư thử nghiệm",
      count: `${num(preview?.materials)} danh mục · ${num(preview?.material_lots)} lô`,
      description: "Xóa danh mục, lô, tồn và liên kết vật tư; không xóa máy hoặc nhân sự.",
    },
    {
      id: "machines",
      title: "Máy móc",
      count: num(preview?.machines),
      description:
        "Xóa toàn bộ máy, mã QR, phiên đứng máy và dữ liệu sự cố liên quan.",
    },
    {
      id: "auditLogs",
      title: "Nhật ký hệ thống",
      count: num(preview?.audit_logs),
      description:
        "Xóa nhật ký cũ. Hệ thống vẫn tạo một bản ghi mới cho chính lần reset này.",
    },
  ];
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">SUPERADMIN · MÔI TRƯỜNG KIỂM THỬ</span>
          <h1>Chế độ nhà phát triển</h1>
        </div>
      </div>
      <section className="developer-warning">
        <ShieldAlert />
        <div>
          <b>Khu vực thao tác dữ liệu nguy hiểm</b>
          <p>
            Chỉ dùng để làm sạch dữ liệu test. Tài khoản đang đăng nhập
            <strong>
              {preview
                ? ` ${preview.protectedAccount.fullName} (${preview.protectedAccount.username}) `
                : " superadmin "}
            </strong>
            luôn được bảo vệ để hệ thống không mất quyền quản trị.
          </p>
        </div>
      </section>
      <form className="developer-reset" onSubmit={reset}>
        <div className="developer-reset-grid">
          {cards.map((card) => (
            <label
              key={card.id}
              className={scopes[card.id] ? "selected" : ""}
            >
              <input
                type="checkbox"
                checked={scopes[card.id]}
                onChange={() => toggleScope(card.id)}
                disabled={busy}
              />
              <span className="developer-check" aria-hidden="true">
                {scopes[card.id] ? "✓" : ""}
              </span>
              <span>
                <b>{card.title}</b>
                <strong>{preview ? card.count : "Đang tải…"}</strong>
                <small>{card.description}</small>
              </span>
            </label>
          ))}
        </div>
        <section className="developer-confirmation">
          <div>
            <b>Xác nhận hai lớp</b>
            <p>
              Nhập chính xác <code>XOA DU LIEU TEST</code> để mở nút xóa.
            </p>
          </div>
          <input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            placeholder="XOA DU LIEU TEST"
            autoComplete="off"
            disabled={busy}
          />
          <button
            className="developer-reset-button"
            disabled={
              busy ||
              !selectedCount ||
              confirmation !== "XOA DU LIEU TEST"
            }
          >
            <Trash2 />
            {busy ? "ĐANG XÓA…" : `XÓA ${selectedCount} NHÓM ĐÃ CHỌN`}
          </button>
        </section>
        {error && <div className="error">{error}</div>}
        {result && (
          <div className="developer-result">
            <CheckCircle2 />
            <div>
              <b>Đã làm sạch dữ liệu test</b>
              <p>
                {Object.entries(result)
                  .map(([key, value]) => `${key}: ${num(value)}`)
                  .join(" · ")}
              </p>
            </div>
          </div>
        )}
      </form>
    </Page>
  );
}

function auditLabel(action: string) {
  const labels: Record<string, string> = {
    ORDER_CREATED: "Tạo lệnh sản xuất",
    ORDER_RELEASED: "Phát hành lệnh sản xuất",
    ORDER_CANCELLED: "Hủy lệnh sản xuất",
    WORKFLOW_CREATED: "Tạo flow công việc",
    WORKFLOW_UPDATED: "Cập nhật flow công việc",
    WORKFLOW_DELETED: "Xóa flow công việc",
    DEPARTMENT_CREATED: "Thêm phòng/xưởng",
    DEPARTMENT_UPDATED: "Đổi tên phòng/xưởng",
    DEPARTMENT_DELETED: "Xóa phòng/xưởng",
    OPERATION_COMPLETED: "Báo cáo hoàn thành công đoạn",
    ISSUE_CREATED: "Báo cáo sự cố",
    ISSUE_IN_PROGRESS: "Tiếp nhận xử lý sự cố",
    ISSUE_RESOLVED: "Hoàn tất xử lý sự cố",
    USER_CREATED: "Tạo nhân sự",
    USER_UPDATED: "Cập nhật nhân sự",
    USER_DISABLED: "Khóa tài khoản",
    USER_DELETED: "Xóa tài khoản",
    MACHINE_CREATED: "Tạo máy",
    MACHINE_RESTORED: "Khôi phục máy đã xóa",
    MACHINE_UPDATED: "Cập nhật máy",
    MACHINE_DELETED: "Xóa máy",
    MACHINE_CHECK_IN: "Bắt đầu phiên đứng máy",
    MACHINE_CHECK_OUT: "Kết thúc phiên đứng máy",
    SHIFT_OUTPUT_REPORTED: "Báo cáo sản lượng và bàn giao ca",
    TASK_SHIFT_TAKEN_OVER: "Tiếp quản công việc từ ca trước",
    DEVELOPER_DATA_RESET: "Làm sạch dữ liệu kiểm thử",
  };
  return labels[action] || action.replaceAll("_", " ");
}

function Issues() {
  const actionGate = useActionGate();
  const {user} = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [resolveTarget, setResolveTarget] = useState<any>(null);
  const [resolution, setResolution] = useState("");
  const [resolveError, setResolveError] = useState("");
  const [updatingIssueId, setUpdatingIssueId] = useState<string | null>(null);
  const load = useCallback(() => api<any[]>("/issues").then(setItems), []);
  useEffect(() => {
    void load();
  }, [load]);
  useSocket(load);
  const updateStatus = actionGate.guard(async (
    issue: any,
    status: "IN_PROGRESS" | "RESOLVED",
    result?: string,
  ) => {
    setUpdatingIssueId(issue.id);
    try {
      await api(`/issues/${issue.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status, resolution: result?.trim() }),
      });
      await load();
      return true;
    } catch (error) {
      if (status === "RESOLVED") {
        setResolveError((error as Error).message);
      } else {
        alert((error as Error).message);
      }
      return false;
    } finally {
      setUpdatingIssueId(null);
    }
  });
  const openResolve = (issue: any) => {
    setResolveTarget(issue);
    setResolution("");
    setResolveError("");
  };
  const closeResolve = () => {
    if (updatingIssueId) return;
    setResolveTarget(null);
    setResolution("");
    setResolveError("");
  };
  const submitResolve = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!resolveTarget || updatingIssueId) return;
    const result = resolution.trim();
    if (!result) {
      setResolveError("Vui lòng nhập kết quả xử lý sự cố.");
      return;
    }
    setResolveError("");
    if (await updateStatus(resolveTarget, "RESOLVED", result)) closeResolve();
  };
  return (
    <Page>
      <div className="page-title">
        <div>
          <span className="eyebrow">AN TOÀN VẬN HÀNH</span>
          <h1>Sự cố sản xuất</h1>
        </div>
      </div>
      <div className="notification-list">
        {items.map((i) => (
          <article key={i.id}>
            <span className="notice-dot urgent" />
            <div>
              <div className="section-head">
                <b>
                  {i.machine_name} · {i.issue_type}
                </b>
                <small>{fmt(i.reported_at)}</small>
              </div>
              <p>
                {i.description || "Không có mô tả"} · Báo bởi{" "}
                {i.reported_by_name}
              </p>
              <Status value={i.status} />
              {i.status !== "RESOLVED" && can(user, 'issues.resolve') && (
                <div className="issue-actions">
                  {i.status === "OPEN" && (
                    <button
                      className="small-btn"
                      disabled={updatingIssueId === i.id}
                      onClick={() => updateStatus(i, "IN_PROGRESS")}
                    >
                      {updatingIssueId === i.id ? "Đang lưu..." : "Tiếp nhận"}
                    </button>
                  )}
                  <button
                    className="small-btn"
                    disabled={updatingIssueId === i.id}
                    onClick={() => openResolve(i)}
                  >
                    Đã xử lý
                  </button>
                </div>
              )}
            </div>
          </article>
        ))}
        {!items.length && (
          <div className="empty-list">Không có sự cố đang ghi nhận.</div>
        )}
      </div>
      {resolveTarget && (
        <div className="dialog-backdrop" role="presentation">
          <form
            className="modal-card issue-resolution-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="issue-resolution-title"
            onSubmit={submitResolve}
          >
            <div className="card-title">
              <div>
                <span className="eyebrow">HOÀN TẤT SỬA CHỮA</span>
                <h2 id="issue-resolution-title">
                  {resolveTarget.machine_code || resolveTarget.machine_name} ·{" "}
                  {resolveTarget.issue_type}
                </h2>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={closeResolve}
                disabled={updatingIssueId === resolveTarget.id}
                aria-label="Đóng"
              >
                <X />
              </button>
            </div>
            <p>
              Ghi lại nội dung đã kiểm tra hoặc sửa chữa. Máy chỉ trở lại bình
              thường khi không còn sự cố đang mở.
            </p>
            <label>
              Kết quả xử lý
              <textarea
                rows={4}
                value={resolution}
                onChange={(event) => {
                  setResolution(event.target.value);
                  if (resolveError) setResolveError("");
                }}
                minLength={2}
                maxLength={1000}
                required
                autoFocus
                placeholder="Ví dụ: Đã kiểm tra nguồn điện, căn chỉnh lại máy và chạy thử ổn định."
              />
            </label>
            {resolveError && <div className="error">{resolveError}</div>}
            <div className="issue-resolution-actions">
              <button
                type="button"
                className="secondary"
                onClick={closeResolve}
                disabled={updatingIssueId === resolveTarget.id}
              >
                Quay lại
              </button>
              <button
                className="primary"
                disabled={updatingIssueId === resolveTarget.id}
              >
                {updatingIssueId === resolveTarget.id
                  ? "ĐANG LƯU..."
                  : "XÁC NHẬN ĐÃ XỬ LÝ"}
              </button>
            </div>
          </form>
        </div>
      )}
    </Page>
  );
}
function ReportIssue() {
  const actionGate = useActionGate();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const submit = actionGate.guard(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    await post("/issues", {
      machineId: params.get("machine"),
      operationId: params.get("operation") || null,
      issueType: f.get("type"),
      severity: f.get("severity"),
      description: f.get("description"),
    });
    navigate("/worker");
  });
  return (
    <Page narrow>
      <div className="page-title">
        <div>
          <span className="eyebrow">BÁO CÁO NHANH</span>
          <h1>Báo sự cố</h1>
        </div>
      </div>
      <form className="operation-card" onSubmit={submit}>
        <label>
          Loại sự cố
          <select name="type">
            <option>Máy lỗi</option>
            <option>Thiếu nguyên liệu</option>
            <option>Thiếu bán thành phẩm</option>
            <option>Sai nguyên liệu</option>
            <option>Lỗi chất lượng</option>
            <option>Chờ QC</option>
            <option>Chờ kỹ thuật</option>
            <option>Khác</option>
          </select>
        </label>
        <label>
          Mức độ
          <select name="severity">
            <option value="IMPORTANT">Quan trọng</option>
            <option value="URGENT">Khẩn</option>
            <option value="CRITICAL">Nghiêm trọng</option>
          </select>
        </label>
        <label>
          Mô tả
          <textarea name="description" rows={5} />
        </label>
        <button className="primary wide big" disabled={actionGate.busy} aria-busy={actionGate.busy}>
          <AlertTriangle />
          {actionGate.busy ? 'ĐANG GỬI BÁO CÁO…' : 'GỬI BÁO CÁO'}
        </button>
      </form>
    </Page>
  );
}

function Page({
  children,
  narrow = false,
}: {
  children: ReactNode;
  narrow?: boolean;
}) {
  return <div className={`page ${narrow ? "narrow" : ""}`}>{children}</div>;
}
function Status({ value }: { value: string }) {
  const labels: Record<string, string> = {
    ACTIVE: "Hoạt động",
    DISABLED: "Đã khóa",
    AVAILABLE: "Sẵn sàng",
    RUNNING: "Đang chạy",
    IDLE: "Sẵn sàng",
    MAINTENANCE: "Bảo trì",
    BREAKDOWN: "Máy lỗi",
    DRAFT: "Bản nháp",
    PLANNED: "Đã lên lịch",
    RELEASED: "Chờ xác nhận",
    ASSIGNED: "Đã phân công",
    ACCEPTED: "Đã tiếp nhận",
    CHECKING: "Đang kiểm tra",
    READY: "Sẵn sàng bắt đầu",
    ACKNOWLEDGED: "Đã nhận",
    IN_PROGRESS: "Đang thực hiện",
    PAUSED: "Tạm dừng",
    BLOCKED: "Bị chặn",
    WAITING_SUPPORT: "Chờ hỗ trợ",
    WAITING_ROUTING: "Chờ điều phối",
    COMPLETED: "Hoàn thành",
    CANCELLED: "Đã hủy",
    OPEN: "Đang mở",
    SUPPLYING: "Đang bổ sung",
    FULFILLED: "Đã cấp vật tư",
    RESOLVED: "Đã xử lý",
  };
  return (
    <span className={`status ${value.toLowerCase()}`}>
      {labels[value] || value}
    </span>
  );
}

export default function App() {
  return (
    <Shell>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/tv/device" element={<TvDevice />} />
        <Route path="/tv/pair" element={<TvPair />} />
        <Route
          path="/worker"
          element={
            <Guard>
              <WorkerHome />
            </Guard>
          }
        />
        <Route
          path="/scan-machine"
          element={
            <Guard>
              <ScanMachine />
            </Guard>
          }
        />
        <Route
          path="/operation/:id"
          element={
            <Guard>
              <Operation />
            </Guard>
          }
        />
        <Route
          path="/notifications"
          element={
            <Guard>
              <Notifications />
            </Guard>
          }
        />
        <Route path="/notifications/:id" element={<Guard><NotificationDetail /></Guard>} />
        <Route
          path="/report-issue"
          element={
            <Guard>
              <ReportIssue />
            </Guard>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <Guard roles={["ADMIN", "PRODUCTION_MANAGER", "TEAM_LEADER"]}>
              <Dashboard />
            </Guard>
          }
        />
        <Route
          path="/admin/live"
          element={
            <Guard roles={["ADMIN", "PRODUCTION_MANAGER", "TEAM_LEADER"]}>
              <LiveOperationsPage />
            </Guard>
          }
        />
        <Route
          path="/admin/machines"
          element={
            <Guard roles={["ADMIN", "PRODUCTION_MANAGER", "TEAM_LEADER"]}>
              <Machines />
            </Guard>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <Guard roles={["ADMIN", "PRODUCTION_MANAGER"]}>
              <><NavLink className="primary" to="/production?tab=orders">Tạo / điều phối lệnh theo mô hình mới</NavLink><Orders /></>
            </Guard>
          }
        />
        <Route path="/admin/orders/:id/dispatch" element={<Guard><OrderDispatchPage /></Guard>} />
        <Route path="/production" element={<Guard><ProductionWorkspace DeadlinePicker={DeadlinePicker}/></Guard>}/>
        <Route path="/admin/materials" element={<Guard><MaterialsPage /></Guard>} />
        <Route
          path="/admin/users"
          element={
            <Guard roles={["ADMIN", "PRODUCTION_MANAGER", "TEAM_LEADER"]}>
              <UsersPage />
            </Guard>
          }
        />
        <Route
          path="/admin/departments"
          element={
            <Guard roles={["ADMIN"]}>
              <DepartmentsPage />
            </Guard>
          }
        />
        <Route
          path="/admin/workflows"
          element={
            <Guard roles={["ADMIN"]}>
              <WorkflowsPage />
            </Guard>
          }
        />
        <Route
          path="/admin/tv"
          element={
            <Guard roles={["ADMIN"]}>
              <TvScenarioAdmin />
            </Guard>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <Guard roles={["ADMIN"]}>
              <AuditPage />
            </Guard>
          }
        />
        <Route
          path="/admin/issues"
          element={
            <Guard roles={["ADMIN", "PRODUCTION_MANAGER", "TEAM_LEADER"]}>
              <Issues />
            </Guard>
          }
        />
        <Route
          path="/admin/developer"
          element={
            <Guard roles={["SUPERADMIN"]}>
              <DeveloperModePage />
            </Guard>
          }
        />
        <Route path="/admin/roles" element={<Guard roles={["SUPERADMIN"]}><RolesPage /></Guard>} />
        <Route path="/printing" element={<Guard><PrintingPage /></Guard>} />
        <Route path="/connection-stations" element={<Guard><ConnectionStationsPage /></Guard>} />
        <Route path="/admin/esp32" element={<Guard roles={["SUPERADMIN"]}><Esp32FirmwarePage /></Guard>} />
        <Route path="/announcements" element={<Guard><Navigate to="/admin/tv#tv-audio" replace /></Guard>} />
        <Route path="*" element={<Navigate to="/worker" replace />} />
      </Routes>
    </Shell>
  );
}
